<template>
    <div class="vs-page">
        <!-- LOADING OVERLAY -->
        <div v-if="isPageLoading" class="page-loading-overlay">
            <div class="loading-spinner-container">
                <div class="vs-logo vs-logo--lg">FM</div>
                <div class="loading-text">Loading invoice</div>
                <div class="loading-progress-bar">
                    <div class="loading-progress-fill" :style="{ width: loadingProgress + '%' }"></div>
                </div>
            </div>
        </div>

        <!-- ===================================================== -->
        <!-- SCREEN HEADER (hidden when printing)                   -->
        <!-- ===================================================== -->
        <div class="screen-only">
            <div class="vs-wrap">

                <!-- Child invoices banner -->
                <div v-if="invoice.child_invoices && invoice.child_invoices.length > 0" class="vs-banner is-warn">
                    <div class="vs-banner-icon"><b-icon icon="arrow-left-right"></b-icon></div>
                    <div class="vs-banner-body">
                        <strong>An exchange invoice was created from this order</strong>
                        <span>This order was split or modified, so a new invoice now holds the exchanged items.</span>
                        <div v-if="invoice.child_invoices.length > 1" class="vs-banner-links">
                            Other child invoices:
                            <b-link v-for="childId in invoice.child_invoices.slice(1)" :key="childId" href="#"
                                    @click.prevent="viewChildInvoice(childId)">{{ childId }}</b-link>
                        </div>
                    </div>
                    <div class="vs-banner-actions">
                        <span class="vs-chip is-warn">{{ invoice.child_invoices.length }} exchange invoice{{ invoice.child_invoices.length > 1 ? 's' : '' }}</span>
                        <b-button class="vs-btn-primary" @click.stop="viewChildInvoice(invoice.child_invoices[0])">
                            Open {{ invoice.child_invoices[0] }}
                        </b-button>
                    </div>
                </div>

                <!-- Exchange invoice banner -->
                <div v-if="invoice.invoice_type === 'EX'" class="vs-banner is-info">
                    <div class="vs-banner-icon"><b-icon icon="arrow-repeat"></b-icon></div>
                    <div class="vs-banner-body">
                        <strong>Exchange invoice</strong>
                        <span>
                            Parent invoice <b>{{ invoice.parent_invoice_id }}</b>
                            <template v-if="parentInvoiceData && parentInvoiceData.customer">
                                for {{ parentInvoiceData.customer.fullname || 'Customer' }}
                            </template>
                        </span>
                        <div v-if="invoice.child_invoices && invoice.child_invoices.length > 0" class="vs-banner-links">
                            Child invoices:
                            <b-link v-for="childId in invoice.child_invoices" :key="childId" href="#"
                                    @click.prevent="viewChildInvoice(childId)">{{ childId }}</b-link>
                        </div>
                    </div>
                    <div class="vs-banner-actions">
                        <b-button class="vs-btn-teal" @click="viewParentInvoice">
                            Open parent {{ invoice.parent_invoice_id }}
                        </b-button>
                    </div>
                </div>

                <TicketListModal
                    v-if="currentTicketItem"
                    :item="currentTicketItem"
                    :tickets="currentTickets"
                    @view-ticket="handleViewTicket"
                    @create-ticket="handleCreateTicket"
                    @closed="currentTicketItem = null"
                />

                <!-- HERO -->
                <header class="vs-hero hidden-print">
                    <div class="vs-hero-grid">

                        <div class="vs-hero-invoice">
                            <div class="vs-hero-label">Invoice</div>
                            <div class="vs-hero-number">
                                {{ invoice.invoice_id }}<span v-if="editCount > 0" class="vs-edit-count-suffix">-{{ editCount }}</span>
                            </div>
                            <div class="vs-hero-tags">
                                <span v-if="invoice.is_layaway" class="vs-tag is-danger">Layaway sale</span>
                                <span v-if="invoice.invoice_type === 'EX'" class="vs-tag is-teal">Exchange invoice</span>
                                <span v-if="invoice.available_credit_value > 0" class="vs-tag is-orange">
                                    Store credit ${{ invoice.available_credit_value }}
                                </span>
                                <span v-if="editCount > 0" class="vs-tag is-orange" :title="lastEditedTooltip">
                                    Edited {{ editCount }} time{{ editCount === 1 ? '' : 's' }}
                                </span>
                            </div>
                            <dl class="vs-hero-meta">
                                <div><dt>Sale date</dt><dd>{{ invoice.sale_date }}</dd></div>
                                <div><dt>Sales agent</dt><dd>{{ invoice.username ? invoice.username.toUpperCase() : '—' }}</dd></div>
                                <div><dt>Location</dt><dd>{{ packet.locationsdict ? packet.locationsdict[invoice.location_id] : '—' }}</dd></div>
                            </dl>
                        </div>

                        <div class="vs-hero-brand">
                            <div v-if="invoice_location_details.short_name && invoice_location_details.short_name.split('SEN').length > 1">
                                <div class="vs-brand-name">INT FOURNITURE</div>
                                <div class="vs-brand-line vs-brand-line--strong">{{ invoice_location_details.address }}</div>
                                <div class="vs-brand-line">{{ invoice_location_details.email }}</div>
                                <div class="vs-brand-line">{{ invoice_location_details.phone }}</div>
                            </div>
                            <div v-else>
                                <div class="vs-brand-name">Furniture Mecca</div>
                                <div class="vs-brand-line vs-brand-line--strong">{{ invoice_location_details.address }}</div>
                                <div class="vs-brand-line">{{ invoice_location_details.email }}</div>
                                <div class="vs-brand-line">{{ invoice_location_details.phone }}</div>
                                <div class="vs-brand-line">{{ invoice_location_details.website }}</div>
                            </div>
                        </div>

                        <div class="vs-hero-qr">
                            <img src="@/assets/QRCODE.png" width="112" alt="QR code to track or reschedule the order" />
                            <div>
                                <div class="vs-qr-title">Track or reschedule</div>
                                <div class="vs-qr-sub">Customer scans to follow the delivery.</div>
                                <div class="mt-2">
                                    <span v-if="[1, 3].includes(invoice.refund_request_approved)">
                                        <b-button size="sm" class="vs-btn-ghost-dark" :disabled="true">Edit approval pending</b-button>
                                    </span>
                                    <span v-else-if="invoice.refund_request_approved == 2">
                                        <b-button size="sm" class="vs-btn-primary" @click="complete_refund(invoice.invoice_id)">Complete edit</b-button>
                                    </span>
                                    <span v-else>
                                        <b-button size="sm" class="vs-btn-ghost-dark" @click="request_refund(invoice.invoice_id)">Request edit</b-button>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="vs-hero-actions">
                        <b-button class="vs-btn-ghost-dark" @click="print_it"><b-icon icon="printer"></b-icon> Print invoice</b-button>
                        <b-button class="vs-btn-ghost-dark" :disabled="update_disabled" @click="reload_invoice()"><b-icon icon="arrow-clockwise"></b-icon> Reload</b-button>
                        <b-button class="vs-btn-ghost-dark" @click="printReceipt"><b-icon icon="receipt"></b-icon> Register receipt</b-button>

                        <b-button v-if="invoiceHasHistory" class="vs-btn-ghost-dark" @click="openInvoiceHistoryModal">
                            <b-icon icon="clock-history"></b-icon>
                            Invoice history
                            <span v-if="editCount > 0" class="vs-edit-count-pill">{{ editCount }}</span>
                        </b-button>

                        <b-button class="vs-btn-ghost-dark" :disabled="update_disabled" @click="syncWithEnvio()"><b-icon icon="arrow-clockwise"></b-icon> Sync with Envio</b-button>

                        <b-button v-if="check_if_edit_allowed()" :class="edit_invoice_action ? 'vs-btn-danger' : 'vs-btn-primary'"
                                  :disabled="update_disabled" @click="edit_invoice()">
                            <b-icon :icon="edit_invoice_action ? 'x' : 'pencil'"></b-icon>
                            <span v-if="edit_invoice_action">Cancel edit</span>
                            <span v-else>Edit invoice</span>
                        </b-button>
                    </div>
                </header>
            </div>
        </div>

        <!-- ===================================================== -->
        <!-- PRINT HEADER (repeats on every printed page)           -->
        <!-- ===================================================== -->
        <table class="print-header-container">
            <thead>
                <tr>
                    <td>
                        <div class="print-header">
                            <div class="pi-banner">
                                <!-- LEFT: invoice meta -->
                                <div class="pi-banner-invoice">
                                    <div class="pi-hero-label">Invoice</div>
                                    <div class="pi-hero-number">
                                        {{ invoice.invoice_id }}<span v-if="editCount > 0" class="pi-edit-suffix">-{{ editCount }}</span>
                                    </div>
                                    <div class="pi-hero-tags">
                                        <span v-if="invoice.is_layaway" class="pi-tag is-danger">Layaway sale</span>
                                        <span v-if="invoice.invoice_type === 'EX'" class="pi-tag is-teal">Exchange invoice</span>
                                    </div>
                                    <div class="pi-hero-meta">
                                        <div>
                                            <span>Sale date</span>
                                            <b>{{ invoice.sale_date }}</b>
                                        </div>
                                        <div>
                                            <span>Sales agent</span>
                                            <b>{{ invoice.username ? invoice.username.toUpperCase() : '—' }}</b>
                                        </div>
                                        <div>
                                            <span>Location</span>
                                            <b>{{ packet.locationsdict ? packet.locationsdict[invoice.location_id] : '—' }}</b>
                                        </div>
                                    </div>
                                </div>

                                <!-- MIDDLE: brand (centered) -->
                                <div class="pi-banner-brand">
                                    <div class="pi-brand-text">
                                        <template v-if="invoice_location_details.short_name && invoice_location_details.short_name.split('SEN').length > 1">
                                            <div class="pi-brand-name">INT FOURNITURE</div>
                                            <div class="pi-brand-line pi-brand-line--strong">{{ invoice_location_details.address }}</div>
                                            <div class="pi-brand-line">{{ invoice_location_details.email }}</div>
                                            <div class="pi-brand-line">{{ invoice_location_details.phone }}</div>
                                        </template>
                                        <template v-else>
                                            <div class="pi-brand-name">Furniture Mecca</div>
                                            <div class="pi-brand-line pi-brand-line--strong">{{ invoice_location_details.address }}</div>
                                            <div class="pi-brand-line">{{ invoice_location_details.email }}</div>
                                            <div class="pi-brand-line">{{ invoice_location_details.phone }}</div>
                                            <div class="pi-brand-line">{{ invoice_location_details.website || 'www.myfurnituremecca.com' }}</div>
                                        </template>
                                    </div>
                                </div>

                                <!-- RIGHT: QR -->
                                <div class="pi-banner-qr">
                                    <div class="pi-qr-title">Track or reschedule</div>
                                    <img src="@/assets/QRCODE.png" alt="QR code to track or reschedule the order" />
                                    <div class="pi-qr-sub">Customer scans to follow the delivery.</div>
                                </div>
                            </div>

                            <div v-if="financeLeaseInfo.lease_id || (invoice.invoice_type === 'EX' && invoice.parent_invoice_id)" class="pi-banner-extra">
                                <span v-if="invoice.invoice_type === 'EX' && invoice.parent_invoice_id">Parent invoice <b>{{ invoice.parent_invoice_id }}</b></span>
                            </div>
                        </div>
                    </td>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>
                        <div class="print-content">

                            <!-- ===================================================== -->
                            <!-- MODALS                                                 -->
                            <!-- ===================================================== -->
                            <b-modal id="modal-image-viewer" size="lg" hide-footer centered title="Product image" content-class="vs-modal" dialog-class="vs-dialog">
                                <div class="text-center">
                                    <b-img
                                        :src="currentFullSizeImage"
                                        fluid
                                        alt="Full size product image"
                                        style="max-height: 70vh; object-fit: contain;"
                                    />
                                    <div class="mt-3 vs-modal-caption">{{ currentProductName }}</div>
                                </div>
                                <template #modal-footer>
                                    <b-button size="sm" class="vs-btn-ghost" @click="$bvModal.hide('modal-image-viewer')">Close</b-button>
                                </template>
                            </b-modal>

                            <b-modal
                                id="modal-invoice-history"
                                size="xl"
                                scrollable
                                centered
                                hide-footer
                                title="Invoice history"
                                content-class="vs-modal" dialog-class="vs-dialog">
                                <div v-if="historyLoading" class="vs-history-loading">
                                    <b-spinner small variant="primary"></b-spinner>
                                    <span class="ml-2">Loading invoice history…</span>
                                </div>

                                <div v-else-if="historyError" class="vs-history-error">
                                    <b-icon icon="exclamation-triangle-fill"></b-icon>
                                    <span>{{ historyError }}</span>
                                </div>

                                <div v-else-if="!invoiceEditHistory.length" class="vs-history-empty">
                                    <b-icon icon="clock-history" font-scale="2"></b-icon>
                                    <p>No edits recorded for this invoice yet.</p>
                                </div>

                                <ol v-else class="vs-history-timeline">
                                    <li v-for="entry in invoiceEditHistory"
                                        :key="entry.id || entry.edit_number"
                                        class="vs-history-entry"
                                        :class="'is-' + (entry.edit_type || 'unknown')">
                                        <div class="vs-history-marker">
                                            <b-icon :icon="historyIconFor(entry.edit_type)"></b-icon>
                                        </div>

                                        <div class="vs-history-card">
                                            <div class="vs-history-head">
                                                <div class="vs-history-title">
                                                    <span class="vs-history-num">#{{ entry.edit_number }}</span>
                                                    <strong>{{ historyLabelFor(entry.edit_type) }}</strong>
                                                </div>
                                                <div class="vs-history-meta">
                                                    <span><b-icon icon="person"></b-icon> {{ entry.edited_by || '—' }}</span>
                                                    <span><b-icon icon="calendar-event"></b-icon> {{ formatDate(entry.edited_at) }}</span>
                                                    <span v-if="entry.amount_delta !== null && entry.amount_delta !== undefined"
                                                          class="vs-history-amount"
                                                          :class="entry.amount_delta < 0 ? 'is-down' : 'is-up'">
                                                        {{ entry.amount_delta < 0 ? '-' : '+' }}${{ Math.abs(entry.amount_delta).toFixed(2) }}
                                                    </span>
                                                </div>
                                            </div>

                                            <div v-if="entry.reason" class="vs-history-reason">
                                                {{ entry.reason }}
                                            </div>

                                            <div v-if="entry.detail && (entry.detail.changed_fields || entry.detail.refund_items || entry.detail.new_items || entry.detail.payment_methods)"
                                                 class="vs-history-detail">
                                                <div v-if="entry.detail.changed_fields && entry.detail.changed_fields.length"
                                                     class="vs-history-chips">
                                                    <span class="vs-history-chip-label">Changed:</span>
                                                    <span v-for="field in entry.detail.changed_fields"
                                                          :key="field"
                                                          class="vs-chip">{{ field }}</span>
                                                </div>

                                                <div v-if="entry.detail.refund_items && entry.detail.refund_items.length"
                                                     class="vs-history-list">
                                                    <span class="vs-history-chip-label">Refunded:</span>
                                                    <span v-for="(ri, i) in entry.detail.refund_items" :key="'ri-'+i" class="vs-chip">
                                                        {{ ri.product_sku }} × {{ ri.return_quantity }}
                                                    </span>
                                                </div>

                                                <div v-if="entry.detail.new_items && entry.detail.new_items.length"
                                                     class="vs-history-list">
                                                    <span class="vs-history-chip-label">Added:</span>
                                                    <span v-for="(ni, i) in entry.detail.new_items" :key="'ni-'+i" class="vs-chip">
                                                        {{ ni.product_sku }} × {{ ni.quantity }}
                                                    </span>
                                                </div>

                                                <div v-if="entry.detail.payment_methods && entry.detail.payment_methods.length"
                                                     class="vs-history-list">
                                                    <span class="vs-history-chip-label">Payout:</span>
                                                    <span v-for="(pm, i) in entry.detail.payment_methods" :key="'pm-'+i" class="vs-chip">
                                                        {{ (pm.value || '').toUpperCase() }} ${{ parseFloat(pm.issue_amount || pm.amount || 0).toFixed(2) }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                </ol>

                                <template #modal-footer>
                                    <div class="vs-history-foot">
                                        <span class="vs-history-foot-note">
                                            {{ invoiceEditHistory.length }} entr{{ invoiceEditHistory.length === 1 ? 'y' : 'ies' }} ·
                                            Current edit count: <b>{{ editCount }}</b>
                                        </span>
                                        <b-button size="sm" class="vs-btn-ghost" @click="$bvModal.hide('modal-invoice-history')">Close</b-button>
                                    </div>
                                </template>
                            </b-modal>

                            <b-modal
                                v-model="showFinanceDetails"
                                title="Finance agreements"
                                size="lg"
                                hide-footer
                                centered
                                content-class="vs-modal" dialog-class="vs-dialog">

                                <p v-if="allFinanceAgreements.length === 0" class="vs-empty text-center">No finance agreements on this invoice.</p>

                                <div v-for="(finance, index) in allFinanceAgreements" :key="index" class="vs-finance-detail">
                                    <div class="vs-finance-detail-head">
                                        <h6>{{ finance.company_name }}</h6>
                                        <span class="vs-chip is-teal">Agreement {{ index + 1 }}</span>
                                    </div>
                                    <dl class="vs-dl">
                                        <dt>Finance amount</dt><dd>${{ finance.amount.toFixed(2) }}</dd>
                                        <dt>Used amount</dt><dd>${{ finance.used_amount.toFixed(2) }}</dd>
                                        <dt>Approved amount</dt><dd>${{ finance.approved_amount.toFixed(2) }}</dd>
                                        <dt>Balance</dt><dd>${{ finance.balance_amount.toFixed(2) }}</dd>
                                        <template v-if="finance.lease_id">
                                            <dt>Lease / agreement ID</dt><dd><code>{{ finance.lease_id }}</code></dd>
                                        </template>
                                        <template v-if="finance.misc_info">
                                            <dt>Miscellaneous information</dt><dd>{{ finance.misc_info }}</dd>
                                        </template>
                                        <template v-if="finance.company_phone">
                                            <dt>Finance company phone</dt><dd><a :href="'tel:' + finance.company_phone">{{ finance.company_phone }}</a></dd>
                                        </template>
                                        <dt>Tax status</dt>
                                        <dd>
                                            <span v-if="finance.charged_tax_itself" class="is-teal">Tax charged by finance company</span>
                                            <span v-else class="vs-muted">No tax charged by finance company</span>
                                        </dd>
                                        <template v-if="finance.ts">
                                            <dt>Transaction date</dt><dd>{{ formatDate(finance.ts) }}</dd>
                                        </template>
                                    </dl>
                                </div>

                                <div class="vs-modal-total">
                                    <div>
                                        <span>Total financed</span>
                                        <strong>${{ getFinanceTotal.toFixed(2) }}</strong>
                                    </div>
                                    <b-button class="vs-btn-ghost" @click="showFinanceDetails = false">Close</b-button>
                                </div>
                            </b-modal>

                            <b-modal id="modal-cs-ticket" @hide="reload_invoice" scrollable title="CS ticket" size="lg" hide-footer content-class="vs-modal" dialog-class="vs-dialog">
                                <CustomerServiceTicket :viewdata="cs_item_data" />
                            </b-modal>

                            <b-modal id="modal-layaway-payments" @hide="reload_invoice" scrollable title="Layaway payments" size="xl" hide-footer content-class="vs-modal" dialog-class="vs-dialog">
                                <LayAwayPaymentsModal v-on:added-layaway-payments="close_layaway" :viewdata="invoice" :total_refunded_value="total_refunded_value" :financecompanies="finance_companies" />
                            </b-modal>

                            <!-- EDIT MODE -->
                            <div v-if="edit_invoice_action == true" class="vs-wrap vs-edit-wrap">
                                <div class="vs-edit-note hidden-print">
                                    <b-icon icon="pencil-square"></b-icon>
                                    You are editing this invoice. Use Cancel edit at the top to return to the invoice view.
                                </div>
                                <EditInvoiceWrapper :viewdata="{invoice_id: invoice.invoice_id}" />
                            </div>

                            <div v-else class="vs-wrap">

                                <!-- CUSTOMER -->
                                <section class="vs-card hidden-print">
                                    <div class="vs-card-head">
                                        <div class="vs-card-title">
                                            <span class="vs-icon"><b-icon icon="person"></b-icon></span>
                                            <div>
                                                <h3>Customer</h3>
                                                <p>Correct the contact or delivery details, then save.</p>
                                            </div>
                                        </div>
                                        <b-button class="vs-btn-primary" :disabled="update_disabled" @click="update_invoice_customer">
                                            <b-icon icon="check2"></b-icon> Save customer
                                        </b-button>
                                    </div>

                                    <div class="vs-fields">
                                        <label class="vs-field">
                                            <span>Full name</span>
                                            <b-input
                                                :disabled="packet.userDetails ? packet.userDetails.userrole != 'admin' : true"
                                                v-model="invoice.customer.fullname"
                                            ></b-input>
                                        </label>
                                        <label class="vs-field">
                                            <span>Email</span>
                                            <b-input v-model="invoice.customer.email"></b-input>
                                        </label>
                                        <label class="vs-field">
                                            <span>Phone 1</span>
                                            <b-input disabled v-model="invoice.customer.phone"></b-input>
                                        </label>
                                        <label class="vs-field">
                                            <span>Phone 2</span>
                                            <b-input v-model="invoice.customer.phone_2"></b-input>
                                        </label>
                                        <label class="vs-field vs-field--wide">
                                            <span>Address</span>
                                            <b-input v-model="invoice.customer.address_line_1"></b-input>
                                        </label>
                                        <label class="vs-field">
                                            <span>Address 2</span>
                                            <b-input v-model="invoice.customer.address_line_2"></b-input>
                                        </label>
                                        <label class="vs-field">
                                            <span>Floor</span>
                                            <b-input v-model="invoice.customer.floor"></b-input>
                                        </label>
                                        <label class="vs-field">
                                            <span>City</span>
                                            <b-input v-model="invoice.customer.city"></b-input>
                                        </label>
                                        <label class="vs-field vs-field--narrow">
                                            <span>State</span>
                                            <b-input v-model="invoice.customer.state"></b-input>
                                        </label>
                                        <label class="vs-field vs-field--narrow">
                                            <span>Zip code</span>
                                            <b-input v-model="invoice.customer.zipcode"></b-input>
                                        </label>
                                    </div>
                                </section>

                                <!-- COMMENTS -->
                                <section v-if="invoice.comment_txt" class="vs-note hidden-print">
                                    <b-icon icon="chat-left-text"></b-icon>
                                    <div>
                                        <strong>Comments</strong>
                                        <p>{{ invoice.comment_txt }}</p>
                                    </div>
                                </section>

                                <!-- PRODUCTS -->
                                <section class="vs-card vs-items hidden-print">
                                    <div class="vs-card-head">
                                        <div class="vs-card-title">
                                            <span class="vs-icon"><b-icon icon="box-seam"></b-icon></span>
                                            <div>
                                                <h3>Products</h3>
                                                <p>{{ (invoice.items || []).length }} line{{ (invoice.items || []).length === 1 ? '' : 's' }} on this invoice. Product sets list their pieces underneath.</p>
                                            </div>
                                        </div>
                                        <div class="vs-legend">
                                            <span><i class="vs-dot is-teal"></i> Spiff</span>
                                            <span><i class="vs-dot is-danger"></i> Returned</span>
                                            <span><i class="vs-dot is-orange"></i> Product set</span>
                                        </div>
                                    </div>

                                    <p v-if="!displayRows.length" class="vs-empty-line">No products on this invoice.</p>

                                    <div class="vs-plist">
                                        <template v-for="row in displayRows">

                                            <!-- PARENT / SET -->
                                            <article v-if="row.kind === 'parent'" :key="row.key"
                                                     class="vs-pcard vs-pcard--parent"
                                                     :class="{ 'is-returned': isParentReturned(row.items) }">
                                                <div class="vs-pcard-img">
                                                    <div v-if="parentImageForGroup(row.sku)" class="image-container">
                                                        <img
                                                            :src="parentImageForGroup(row.sku)"
                                                            :alt="'Product set - ' + (row.items[0].parent_product_name || row.sku)"
                                                            @error="handleImageError($event, {product_sku: row.sku})"
                                                            @load="handleImageLoad($event, {product_sku: row.sku})"
                                                            @click="showParentFullImage(row.items[0])"
                                                            class="product-image"
                                                        />
                                                        <div class="image-overlay" @click="showParentFullImage(row.items[0])"><b-icon icon="zoom-in"></b-icon></div>
                                                    </div>
                                                    <div v-else class="no-image-placeholder"><b-icon icon="image"></b-icon><span>No image</span></div>
                                                </div>

                                                <div class="vs-pcard-panel vs-pcard-info">
                                                    <div class="vs-pcard-kicker"><b-icon icon="collection"></b-icon> Product set</div>
                                                    <div class="vs-pcard-title">{{ (row.items[0].parent_product_name || 'Group product').toUpperCase() }}</div>
                                                    <div class="vs-pcard-line">SKU <b>{{ row.sku }}</b></div>
                                                    <div v-if="row.items[0].parent_vendor_sku" class="vs-pcard-line">Vendor SKU <b>{{ row.items[0].parent_vendor_sku }}</b></div>
                                                    <div class="vs-pcard-tags">
                                                        <span class="vs-chip is-dark">Set of {{ row.items.length }}</span>
                                                        <span v-if="isParentReturned(row.items)" class="vs-chip is-danger">Returned</span>
                                                    </div>
                                                </div>

                                                <div class="vs-pcard-panel vs-pcard-meta">
                                                    <div class="vs-meta-row">
                                                        <span><b-icon icon="box-seam"></b-icon> Pieces in set</span>
                                                        <b>{{ row.items.length }}</b>
                                                    </div>
                                                    <div class="vs-meta-row">
                                                        <span><b-icon icon="box"></b-icon> Total cubes</span>
                                                        <b>{{ calculateParentCubes(row.items) }}</b>
                                                    </div>
                                                    <div class="vs-meta-note">Delivery, type and status are set on each piece below.</div>
                                                </div>

                                                <div class="vs-pcard-panel vs-pcard-price">
                                                    <div class="vs-price-row"><span>Quantity</span><b>{{ vsSetQty(row.items) }}</b></div>
                                                    <div v-if="vsSetRegularPrice(row.items) > 0" class="vs-price-row is-strike">
                                                        <span>Regular price</span><b>{{ vsMoney(vsSetRegularPrice(row.items)) }}</b>
                                                    </div>
                                                    <div class="vs-price-row"><span>Selling price</span><b>{{ vsMoney(vsSetListPrice(row.items)) }}</b></div>
                                                    <div v-if="vsSetSalePrice(row.items) !== null" class="vs-price-row is-good">
                                                        <span><b-icon icon="tag-fill"></b-icon> {{ vsSetSaleTypeLabel(row.items) }}</span><b>{{ vsMoney(vsSetSalePrice(row.items)) }}</b>
                                                    </div>
                                                    <div v-if="vsSetDiscount(row.items) > 0" class="vs-price-row is-good">
                                                        <span>Discount</span><b>-{{ vsMoney(vsSetDiscount(row.items)) }}</b>
                                                    </div>
                                                    <div v-if="vsSetSavings(row.items) > 0" class="vs-price-row is-good">
                                                        <span><b-icon icon="tag-fill"></b-icon> You save</span><b>{{ vsMoney(vsSetSavings(row.items)) }}</b>
                                                    </div>
                                                    <div class="vs-price-row is-total"><span>Set total &times; {{ vsSetQty(row.items) }}</span><b>{{ vsMoney(vsSetTotal(row.items)) }}</b></div>
                                                </div>

                                                <div class="vs-pcard-actions"></div>
                                            </article>

                                            <!-- ITEM -->
                                            <article v-else :key="row.key"
                                                     class="vs-pcard"
                                                     :class="{
                                                         'is-child': row.isChild,
                                                         'is-last': row.isLast,
                                                         'is-returned': is_returned(row.item),
                                                         'has-spiff': !is_returned(row.item) && getSpiffValue(row.item) > 0
                                                     }">
                                                <span v-if="row.isChild" class="vs-pcard-dot" aria-hidden="true"></span>

                                                <div class="vs-pcard-img">
                                                    <div v-if="row.item.is_damaged && row.item.damage_history && row.item.damage_history.length > 0" class="image-container">
                                                        <img
                                                            :src="getDamageHistoryImage(row.item)"
                                                            :alt="'Damage image - ' + (row.item.product_name || row.item.product_sku)"
                                                            @error="handleDamageImageError($event, row.item)"
                                                            @load="handleDamageImageLoad($event, row.item)"
                                                            @click="showDamageFullImage(row.item)"
                                                            class="product-image"
                                                        />
                                                        <div class="image-overlay" @click="showDamageFullImage(row.item)">Damage</div>
                                                    </div>
                                                    <div v-else-if="getProductImage(row.item)" class="image-container">
                                                        <img
                                                            :src="getProductImage(row.item)"
                                                            :alt="row.item.product_name || row.item.product_sku"
                                                            @error="handleImageError($event, row.item)"
                                                            @load="handleImageLoad($event, row.item)"
                                                            @click="showFullImage(row.item)"
                                                            class="product-image"
                                                        />
                                                        <div class="image-overlay" @click="showFullImage(row.item)"><b-icon icon="zoom-in"></b-icon></div>
                                                    </div>
                                                    <div v-else class="no-image-placeholder"><b-icon icon="image"></b-icon><span>No image</span></div>
                                                    <span v-if="row.item.is_damaged" class="vs-img-badge">Damaged</span>
                                                </div>

                                                <div class="vs-pcard-panel vs-pcard-info">
                                                    <div v-if="row.isChild" class="vs-pcard-kicker is-muted"><b-icon icon="diagram-3"></b-icon> Piece of {{ row.item.parent_product_sku }}</div>
                                                    <div class="vs-pcard-title">{{ (row.item.product_name || row.item.product_sku || '').toUpperCase() }}</div>
                                                    <div class="vs-pcard-line">SKU <b>{{ (row.item.product_sku || '').toUpperCase() }}</b></div>
                                                    <div v-if="row.item.vendor_sku" class="vs-pcard-line">Vendor SKU <b>{{ row.item.vendor_sku }}</b></div>
                                                    <div v-if="row.item.source" class="vs-pcard-line">Source <b>{{ (row.item.source || '').toUpperCase() }}</b></div>
                                                    <div class="vs-pcard-line vs-qty-cubes-row">
    <span>Qty <b :class="{ 'is-danger': is_returned(row.item) }">{{ (row.item.quantity || 0) - (row.item.refunded_so_far || 0) }}</b></span>
    <span class="vs-qty-cubes-sep">•</span>
    <span>Cubes <b>{{ row.item.cubes || 0.0 }}</b></span>
      <div v-if="row.item.avail" class="vs-price-row is-warn"><span>Available</span><b>{{ row.item.avail }}</b></div>
</div>

                                                    <div class="vs-pcard-tags">
                                                        <span v-if="getSpiffValue(row.item) > 0" class="vs-chip is-teal">Spiff ${{ getSpiffValue(row.item).toFixed(2) }}</span>
                                                        <span v-if="hasBackorderLocsub(row.item)" class="vs-chip is-danger">Backorder</span>
                                                        <span v-if="(row.item.product_id < 0 || !row.item.product_id) && row.item.is_special" class="vs-chip is-orange">Special order</span>
                                                        <span v-if="row.item.as_is === true" class="vs-chip is-danger">As-is</span>
                                                        <span v-if="is_returned(row.item)" class="vs-chip is-danger">Returned</span>
                                                    </div>
                                                    <div v-if="(row.item.product_id < 0 || !row.item.product_id) && row.item.is_special" class="vs-hint">Special orders can take up to 8 weeks.</div>
                                                    <div v-if="row.item.as_is === true" class="vs-flag is-danger">Sold as-is, non-returnable</div>
                                                    <div v-if="row.item.is_damaged" class="vs-flag-box">Damaged item: it can't be returned or refunded.</div>
                                                </div>

                                                <!-- ============ META: 2 rows ============ -->
                                                <div class="vs-pcard-panel vs-pcard-meta">

                                                    <!-- Row 1: Order Type + Status -->
                                                    <div class="vs-meta-row-2col">
                                                        <div class="vs-meta-field">
                                                            <span><b-icon icon="truck"></b-icon> Order type</span>
                                                            <b-form-select
                                                                :options="order_types"
                                                                size="sm"
                                                                v-model="row.item.order_type"
                                                                @change="(val) => onItemOrderTypeChange(row, val)"
                                                                :disabled="is_returned(row.item)"
                                                                :title="getOrderTypeHistoryTooltip(row.item)"
                                                            ></b-form-select>
                                                            <small v-if="row.item.source === 'store'" class="vs-sub">
                                                                {{ getLocationDisplay(row.item.original_locsub) || row.item.original_locsub || 'N/A' }}
                                                            </small>
                                                            <small v-if="row.item.order_type_history && row.item.order_type_history.length > 0"
                                                                   class="vs-sub vs-help" :title="getOrderTypeHistoryTooltip(row.item)">
                                                                Changed {{ row.item.order_type_history.length }} time(s)
                                                            </small>
                                                        </div>

                                                        <div class="vs-meta-field">
                                                            <span><b-icon icon="info-circle"></b-icon> Status</span>
                                                            <b-form-select :options="states" v-model="row.item.state" size="sm" :disabled="true"></b-form-select>
                                                        </div>
                                                    </div>

                                                    <!-- Row 2: Delivery Date + Delivery/Pickup Location -->
                                                    <div class="vs-meta-row-2col">
                                                        <div class="vs-meta-field">
                                                            <span><b-icon icon="calendar-event"></b-icon> Delivery date</span>
                                                            <date-picker v-model="row.item.delivery_date"
                                                                format="YYYY-MM-DD"
                                                                valueType="format"
                                                                class="vs-date"
                                                                @change="update_item_level(row.item)"
                                                                disabled
                                                            ></date-picker>
                                                         
                                                            <small v-if="row.item.last_delivery_date && row.item.last_delivery_date !== row.item.delivery_date" class="vs-prev-date">
                                                                Previously {{ row.item.last_delivery_date }}
                                                            </small>
                                                        </div>

                                                        <!-- ✅ CHANGED: Dynamic label based on order_type -->
                                                        <div class="vs-meta-field">
                                                            <span>
                                                                <b-icon :icon="locationIconFor(row.item.order_type)"></b-icon>
                                                                {{ locationLabelFor(row.item.order_type) }}
                                                            </span>
                                                            <b class="vs-meta-value padding-top">
                                                                {{ getLocationDisplay(row.item.drop_locsub) || row.item.drop_locsub || 'N/A' }}
                                                            </b>
                                                        </div>
                                                    </div>

                                                </div>

                                                <div class="vs-pcard-panel vs-pcard-price">
                                                    <div v-if="row.item.refunded_so_far > 0" class="vs-price-row is-muted">
                                                        <span>Returned so far</span><b>{{ row.item.refunded_so_far }}</b>
                                                    </div>

                                                    <!-- Regular / MSRP (selling + 100 / 200 / 500 / 1000) -->
                                                    <div v-if="vsRegularPrice(row.item) > 0" class="vs-price-row is-strike">
                                                        <span>Regular price</span><b>{{ vsMoney(vsRegularPrice(row.item)) }}</b>
                                                    </div>

                                                    <!-- Selling price (product list price) -->
                                                    <div class="vs-price-row"><span>Selling price</span><b>{{ vsMoney(vsListPrice(row.item)) }}</b></div>

                                                    <!-- Sale-type price (Clearance / Promo / ...) — only when sold below selling price -->
                                                    <div v-if="vsSalePrice(row.item) !== null" class="vs-price-row is-good">
                                                        <span><b-icon icon="tag-fill"></b-icon> {{ vsSaleTypeLabel(row.item) }}</span><b>{{ vsMoney(vsSalePrice(row.item)) }}</b>
                                                    </div>

                                                    <!-- Unit discount — only when given -->
                                                    <div v-if="vsUnitDiscount(row.item) > 0" class="vs-price-row is-good">
                                                        <span>Unit discount</span><b>-{{ vsMoney(vsUnitDiscount(row.item)) }}</b>
                                                    </div>

                                                    <!-- You save -->
                                                    <div v-if="vsItemSavings(row.item) > 0" class="vs-price-row is-good">
                                                        <span><b-icon icon="tag-fill"></b-icon> You save</span><b>{{ vsMoney(vsItemSavings(row.item)) }}</b>
                                                    </div>

                                                  
                                                    <div class="vs-price-row is-total">
                                                        <span>Total &times; {{ row.item.quantity || 0 }}</span>
                                                        <b>{{ vsMoney(vsLineAmount(row.item)) }}</b>
                                                    </div>
                                                </div>

                                                <div class="vs-pcard-actions">
                                                    <template v-if="!is_returned(row.item)">
                                                        <b-button size="sm" class="vs-btn-ghost vs-action-btn" @click="view_cs_issue(row.item)" title="View or create claim" aria-label="View or create claim">
                                                            <b-icon icon="eye"></b-icon>
                                                        </b-button>
                                                        <b-button size="sm" class="vs-btn-ghost vs-action-btn" @click="load_cs_issues(row.item)" title="Issues" aria-label="Issues">
                                                            <b-icon icon="exclamation-circle"></b-icon>
                                                            <span v-if="hasTicket(row.item)" class="vs-count">{{ row.item.cs_ticket_count || 1 }}</span>
                                                        </b-button>
                                                        <b-button v-if="row.item.as_is === true && row.item.as_is_image_url" size="sm" class="vs-btn-ghost vs-action-btn"
                                                                  @click="showAsIsImage(row.item)" title="View as-is image" aria-label="View as-is image">
                                                            <b-icon icon="image"></b-icon>
                                                        </b-button>
                                                    </template>
                                                </div>
                                            </article>
                                        </template>
                                    </div>
                                </section>

                                <!-- PAYMENT SUMMARY -->
                                <section class="vs-card vs-summary hidden-print">
                                    <div class="vs-card-head">
                                        <div class="vs-card-title">
                                            <span class="vs-icon"><b-icon icon="cash"></b-icon></span>
                                            <div>
                                                <h3>Payment summary</h3>
                                                <p class="hidden-print">Charges, totals and every payment taken on this invoice.</p>
                                            </div>
                                        </div>
                                        <b-button
                                            size="sm"
                                            v-if="(invoice.is_layaway || get_total_balance() > 0) && invoice.layaway_refunded_invoice_id == null && invoice.invoice_type !== 'EX'"
                                            class="vs-btn-primary hidden-print"
                                            @click="open_layaway_payment_modal">
                                            <b-icon icon="credit-card"></b-icon> Take payment
                                        </b-button>
                                    </div>

                                    <div class="vs-summary-grid">
                                        <div class="vs-sum-col">
                                            <h4>Extra charges</h4>
                                            <dl class="vs-dl">
                                                <dt>Delivery</dt><dd>${{ parseFloat(invoice.delivery_charges || 0).toFixed(2) }}</dd>
                                                <dt>White Glove</dt><dd>${{ parseFloat(invoice.whiteglove_charges || 0).toFixed(2) }}</dd>
                                                <dt>Setup</dt><dd>${{ parseFloat(invoice.setup_charges || 0).toFixed(2) }}</dd>

                                                <!-- WARRANTY: 0 → show refused message instead of price -->
                                                <dt>Warranty</dt>
                                                <dd v-if="parseFloat(invoice.warranty_charges || 0) > 0">
                                                    ${{ parseFloat(invoice.warranty_charges || 0).toFixed(2) }}
                                                </dd>
                                                <dd v-else class="vs-warranty-refused">
                                                    Customer refused to purchase extended warranties
                                                </dd>
                                            </dl>
                                        </div>

                                        <div class="vs-sum-col is-totals">
                                            <h4>Totals</h4>
                                            <dl class="vs-dl">
                                                <dt>Total discount</dt><dd>${{ getTotalDiscount().toFixed(2) }}</dd>
                                                <dt>Order total</dt><dd>${{ getOrderTotal().toFixed(2) }}</dd>
                                                <dt>Extra charges</dt><dd>${{ getChargesTotal().toFixed(2) }}</dd>
                                                <dt>Taxable total</dt><dd>${{ getTaxableSubtotal().toFixed(2) }}</dd>
                                                <dt>Sales tax</dt><dd>${{ getSalesTaxAmount().toFixed(2) }}</dd>
                                                <dt class="vs-dl-grand">Grand total</dt><dd class="vs-dl-grand">${{ getGrandTotal().toFixed(2) }}</dd>
                                            </dl>
                                        </div>

                                        <div class="vs-sum-col">
                                            <h4>Payments</h4>
                                            <dl class="vs-dl">
                                                <template v-for="(pm, pi) in orderedPaymentMethods">
                                                    <dt :key="'pm-lbl-' + pi">
                                                        <span v-if="pm.source" class="vs-chip is-teal" style="font-size: 10px; padding: 1px 6px; margin-right: 4px;">
                                                            {{ (pm.source || '').toUpperCase() }}
                                                        </span>
                                                        {{ (pm.value || '').toUpperCase() }}<template v-if="pm.name && pm.name !== 'N/A'"> - {{ pm.name }}</template>
                                                    </dt>
                                                    <dd :key="'pm-val-' + pi">${{ (parseFloat(pm.amount) || 0).toFixed(2) }}</dd>
                                                </template>

                                                <template v-if="allFinanceAgreements.length > 0">
                                                    <dt
                                                        v-for="(fin, i) in allFinanceAgreements"
                                                        :key="'fin-amt-lbl-' + i"
                                                        class="vs-dl-sub"
                                                    >
                                                        Finance amount
                                                        <span class="vs-sub-inline">
                                                            ({{ (fin.company_name || 'FINANCE').toUpperCase() }}
                                                            <template v-if="fin.lease_id"> · {{ fin.lease_id }}</template>)
                                                        </span>
                                                    </dt>
                                                    <dd
                                                        v-for="(fin, i) in allFinanceAgreements"
                                                        :key="'fin-amt-val-' + i"
                                                        class="vs-dl-sub"
                                                    >
                                                        ${{ fin.amount.toFixed(2) }}
                                                    </dd>
                                                </template>

                                                <template v-if="getTaxExemptOrFinanceTotal > 0 && allFinanceAgreements.length === 0">
                                                    <dt>
                                                        <template v-if="hasTaxExemptPayment">
                                                            Tax exempt
                                                            <span v-if="getTaxExemptCompanyNames.length > 0" class="vs-sub-inline">({{ getTaxExemptCompanyNames.join(', ') }})</span>
                                                        </template>
                                                        <template v-else>
                                                            Finance
                                                            <span v-if="getFinanceCompanyNames.length > 0" class="vs-sub-inline">({{ getFinanceCompanyNames.join(', ') }})</span>
                                                        </template>
                                                        <span v-if="allFinanceAgreements.length > 1" class="vs-sub-inline">{{ allFinanceAgreements.length }} agreements</span>
                                                    </dt>
                                                    <dd>${{ getTaxExemptOrFinanceTotal.toFixed(2) }}</dd>
                                                </template>

                                                <dt class="vs-dl-sub">Sub total</dt><dd class="vs-dl-sub">${{ getSubTotal().toFixed(2) }}</dd>
                                                <dt class="vs-dl-grand">Total paid</dt><dd class="vs-dl-grand">${{ get_pms_total().toFixed(2) }}</dd>
                                            </dl>

                                            <template v-if="financeAgreementsWithBanners.length > 0">
                                                <div class="vs-finance-banner-wrap">
                                                    <div
                                                        v-for="(fin, fi) in financeAgreementsWithBanners"
                                                        :key="'fin-banner-' + fi"
                                                        class="vs-finance-banner"
                                                    >
                                                        <div class="vs-finance-banner-left">
                                                            <div class="vs-finance-banner-name">{{ (fin.company_name || 'FINANCE').toUpperCase() }}</div>
                                                            <div v-if="fin.banner_image" class="vs-finance-banner-img-wrap">
                                                                <img
                                                                    :src="fin.banner_image"
                                                                    alt="Banner"
                                                                    class="vs-finance-banner-img-el"
                                                                    @error="onFinanceBannerError($event, fin)"
                                                                />
                                                            </div>
                                                            <div v-else class="vs-finance-banner-img is-empty">
                                                                <b-icon icon="image"></b-icon>
                                                            </div>
                                                            <div class="vs-finance-approved">
                                                                <span class="vs-finance-approved-amt">${{ fin.approved_amount.toFixed(2) }}</span>
                                                                <span class="vs-finance-approved-label">APPROVED</span>
                                                            </div>
                                                        </div>

                                                        <div class="vs-finance-banner-right">
                                                            <div class="vs-finance-info-row">
                                                                <span>LEASE ID</span>
                                                                <b>{{ fin.lease_id || '—' }}</b>
                                                            </div>
                                                            <div class="vs-finance-info-row">
                                                                <span>USED</span>
                                                                <b>${{ fin.used_amount.toFixed(2) }}</b>
                                                            </div>
                                                            <div class="vs-finance-info-row">
                                                                <span>BALANCE</span>
                                                                <b>${{ fin.balance_amount.toFixed(2) }}</b>
                                                            </div>
                                                        </div>

                                                        <div class="vs-finance-banner-foot">
                                                            YOU MAY HAVE 90 DAYS TO USE YOUR BALANCE
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>
                                        </div>
                                    </div>

                                    <div v-if="(invoice.is_layaway || get_total_balance() > 0) && invoice.layaway_refunded_invoice_id == null && invoice.invoice_type !== 'EX'"
                                         class="vs-balance">
                                        <div class="vs-balance-row">
                                            <span>Current balance</span>
                                            <strong>${{ (get_total_balance() || 0).toFixed(2) }}</strong>
                                        </div>
                                        <template v-if="predictedSalesTax > 0">
                                            <div class="vs-balance-row is-warn">
                                                <span>Predicted sales tax ({{ (parseFloat(invoice.tax_perc) * 100).toFixed(2) }}%)</span>
                                                <strong>${{ predictedSalesTax.toFixed(2) }}</strong>
                                            </div>
                                            <div class="vs-balance-total">
                                                <span>Total to pay</span>
                                                <strong>${{ totalWithPredictedTax.totalToPay.toFixed(2) }}</strong>
                                            </div>
                                        </template>
                                        <div v-else class="vs-balance-total is-due">
                                            <span>Balance due</span>
                                            <strong>${{ (get_total_balance() || 0).toFixed(2) }}</strong>
                                        </div>
                                        <div v-if="parseFloat(invoice.tax_perc) > 0" class="vs-balance-note">
                                            Tax rate {{ (parseFloat(invoice.tax_perc) * 100).toFixed(2) }}%
                                        </div>
                                    </div>

                                    <div v-if="allFinanceAgreements.length > 0" class="vs-finance">
                                        <div class="vs-finance-head">
                                            <h4>Finance agreements <span>({{ allFinanceAgreements.length }})</span></h4>
                                            <b-button v-if="allFinanceAgreements.length > 1" size="sm" variant="link" class="vs-link hidden-print"
                                                      @click="showFinanceDetails = true">View details</b-button>
                                        </div>

                                        <div v-for="(finance, index) in allFinanceAgreements" :key="index" class="vs-finance-row">
                                            <div class="vs-finance-info">
                                                <strong>{{ finance.company_name }}</strong>
                                                <span v-if="finance.lease_id">Lease {{ finance.lease_id }}</span>
                                                <span v-if="finance.company_phone">Tel {{ finance.company_phone }}</span>
                                                <span class="vs-chip" :class="finance.charged_tax_itself ? 'is-teal' : ''">
                                                    {{ finance.charged_tax_itself ? 'Tax included' : 'No tax' }}
                                                </span>
                                            </div>
                                            <div class="vs-finance-amt">${{ finance.amount.toFixed(2) }}</div>
                                        </div>
                                        <div class="vs-finance-total">
                                            Total financed <strong>${{ getFinanceTotal.toFixed(2) }}</strong>
                                        </div>
                                    </div>

                                    <div v-if="invoice.is_layaway" class="vs-layaway">
                                        <h4>Layaway payment history</h4>
                                        <div class="vs-layaway-grid">
                                            <div v-for="(payment, index) in getLayawayPayments().slice(0,4)" :key="'lw-'+index" class="vs-layaway-cell">
                                                <span class="vs-layaway-n">Payment {{ index + 1 }}</span>
                                                <strong>${{ parseFloat(payment.amount || 0).toFixed(2) }}</strong>
                                                <span class="vs-layaway-m">{{ (payment.method || '').toUpperCase() }}</span>
                                                <span class="vs-layaway-d">{{ payment.ts ? payment_ts_format(payment.ts).split(' ')[0] : '' }}</span>
                                            </div>
                                            <div v-for="n in Math.max(0, 4 - (getLayawayPayments().length || 0))" :key="'lw-empty-'+n" class="vs-layaway-cell is-empty">
                                                <span class="vs-layaway-n">Payment {{ (getLayawayPayments().length || 0) + n }}</span>
                                                <span class="vs-layaway-m">Not yet paid</span>
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                <!-- ACTIVE REFUND -->
                                <section v-if="invoice.items && invoice.items[0] && invoice.items[0].modifiable && refunds.items_to_be_refunded.length > 0"
                                         class="vs-card vs-refund hidden-print">
                                    <div class="vs-card-head">
                                        <div class="vs-card-title">
                                            <span class="vs-icon is-danger"><b-icon icon="arrow-return-left"></b-icon></span>
                                            <div>
                                                <h3>Active refund</h3>
                                                <p>Set return quantities, reasons and refund methods, then submit.</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="vs-table-scroll">
                                        <table class="vs-mini-table">
                                            <thead>
                                                <tr>
                                                    <th>Parent product</th><th>SKU</th><th>Product</th><th>Qty</th><th>Refunded qty</th>
                                                    <th>Remaining qty</th><th>Unit price</th><th>Unit discount</th><th>Return qty</th>
                                                    <th>Return amount</th><th>Reason</th><th>Delivered</th><th>Return loc</th><th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="(rtitem, rtidx) in refunds.items_to_be_refunded" :key="rtidx">
                                                    <td>{{ rtitem.parent_product_sku }}</td>
                                                    <td>{{ rtitem.product_sku }}</td>
                                                    <td>{{ rtitem.product_name }}</td>
                                                    <td>{{ rtitem.quantity }}</td>
                                                    <td>{{ rtitem.refunded_so_far }}</td>
                                                    <td>{{ rtitem.quantity - rtitem.refunded_so_far }}</td>
                                                    <td>{{ (rtitem.selling_price || 0).toFixed(2) }}</td>
                                                    <td>{{ (rtitem.unit_discount_value || 0).toFixed(2) }}</td>
                                                    <td><b-form-select size="sm" v-model="rtitem.return_quantity" :options="get_quantity_dd(rtitem.quantity - rtitem.refunded_so_far)"></b-form-select></td>
                                                    <td class="vs-amount">{{ ( (rtitem.selling_price * rtitem.return_quantity) - (rtitem.unit_discount_value * rtitem.return_quantity) ).toFixed(2) }}</td>
                                                    <td><b-form-select size="sm" v-model="rtitem.return_reason" :options="return_reasons"></b-form-select></td>
                                                    <td><b-form-select size="sm" v-model="rtitem.delivered" :options="is_delivered"></b-form-select></td>
                                                    <td>
                                                        <b-form-select size="sm" v-model="rtitem.return_locsub"
                                                                       :options="packet.locsubs ? packet.locsubs.filter(function(d){ return (d.is_main_warehouse == true || d.location_id == ((packet.userDetails || {}).properties || {}).location_id ) }) : []"
                                                                       :disabled="rtitem.delivered == false || rtitem.delivered == null"></b-form-select>
                                                    </td>
                                                    <td><b-button size="sm" class="vs-btn-ghost" @click="remove_refund_item(rtidx)">Remove</b-button></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <div class="vs-refund-boxes">
                                        <div class="vs-refund-box">
                                            <h5>Refund on items</h5>
                                            <label>Current<input class="form-control" :value="(get_total_to_be_refunded() || 0.0).toFixed(2)" disabled="true" /></label>
                                        </div>
                                        <div class="vs-refund-box">
                                            <h5>Refund on delivery</h5>
                                            <div class="vs-refund-remain">Remaining ${{ (get_total_delivery_refund_remaining() || 0.0).toFixed(2) }}</div>
                                            <label>Current<input class="form-control" v-model="refunds.total_delivery_refund" :disabled="get_total_delivery_refund_remaining() == 0" type="number" /></label>
                                        </div>
                                        <div class="vs-refund-box">
                                            <h5>Refund on setup</h5>
                                            <div class="vs-refund-remain">Remaining ${{ (get_total_setup_refund_remaining() || 0.0).toFixed(2) }}</div>
                                            <label>Current<input class="form-control" v-model="refunds.total_setup_refund" :disabled="get_total_setup_refund_remaining() == 0" type="number" /></label>
                                        </div>
                                        <div class="vs-refund-box is-dark">
                                            <h5>Totals</h5>
                                            <div class="vs-refund-line"><span>Total refund</span><b>${{ (get_total_refund_dollar_value() || 0.0).toFixed(2) }}</b></div>
                                            <div class="vs-refund-line is-big"><span>Balance</span><b>${{ ((get_total_refund_dollar_value() || 0.0) - ( get_payment_methods_sum() || 0.0 )).toFixed(2) }}</b></div>
                                        </div>
                                    </div>

                                    <div class="vs-refund-methods">
                                        <div class="vs-refund-methods-head">
                                            <b-button size="sm" class="vs-btn-ghost" @click="add_refund_payment_method"><b-icon icon="plus"></b-icon> Add refund method</b-button>
                                            <span class="vs-hint">Add non-taxable forms of payment first.</span>
                                        </div>
                                        <div v-if="refunds.refund_methods.length > 0" class="vs-refund-method-grid">
                                            <div v-for="(rpmethod, rpidx) in refunds.refund_methods" :key="rpidx" class="vs-refund-method">
                                                <b-form-select :options="refunds.refund_method_options" size="sm" v-model="rpmethod.value"></b-form-select>
                                                <div class="vs-refund-line">
                                                    <span>Max allowed</span>
                                                    <b v-if="rpmethod.value != null">${{ Math.max((get_max_allowed_on_refund_payment_method(rpmethod) || 0.0), 0.0).toFixed(2) }}</b>
                                                </div>
                                                <label>Value<input class="form-control" type="number" v-model="rpmethod.amount" /></label>
                                                <b-button size="sm" variant="link" class="vs-link-danger" @click="remove_rpm(rpidx)">Remove</b-button>
                                            </div>
                                        </div>
                                        <b-button class="vs-btn-primary mt-3" @click="submit_refund">Submit refund</b-button>
                                    </div>
                                </section>

                                <!-- REFUNDS / CASH ISSUE -->
                                <section v-if="invoice.refund_methods && invoice.refund_methods.length > 0" class="vs-card hidden-print">
                                    <div class="vs-card-head">
                                        <div class="vs-card-title">
                                            <span class="vs-icon"><b-icon icon="journal-text"></b-icon></span>
                                            <div><h3>Refunds and cash issued</h3><p>History of refunds recorded against this invoice.</p></div>
                                        </div>
                                    </div>
                                    <RefundsSummary @refunds-history="summarize_refunds_history" :viewdata="{invoice_id: invoice.invoice_id, packet: packet}" />
                                </section>

                                <!-- TERMS (screen) -->
                                <section v-if="policy_text" class="vs-card vs-terms hidden-print">
                                    <div class="vs-card-head">
                                        <div class="vs-card-title">
                                            <span class="vs-icon"><b-icon icon="file-earmark-check"></b-icon></span>
                                            <div><h3>Terms and conditions / return policy</h3><p>What the customer agreed to at the time of sale.</p></div>
                                        </div>
                                    </div>

                                    <div class="vs-policy" v-html="policy_text"></div>

                                    <h4 class="vs-subhead">Customer agreements</h4>
                                    <ul class="vs-agreements">
                                        <li v-for="a in [
                                                { ok: termsAgreed, label: 'I have read and agree to the Terms & Conditions' },
                                                { ok: warrantyRefused, label: 'Customer refused to purchase extended warranties' },
                                                { ok: deliveryWaiverAgreed, label: 'I understand that Standard Delivery does NOT include the removal of boxes/packaging. I choose to waive the extra $200 White Glove service.' },
                                                { ok: spacePreparationAgreed, label: 'Failure to Prepare Space: If the designated room is not cleared and ready for setup upon arrival, the delivery team will perform a &quot;Drop-Off Delivery&quot; only. Customer has to pay $150 to reschedule setup the merchandise.' },
                                                { ok: addressConfirmationAgreed, label: 'I confirm that the delivery address provided is accurate and accessible for delivery.' },
                                                { ok: deliveryDamageWaiverAgreed, label: 'I understand and accept that Furniture Mecca is not responsible for damages that occur during delivery.' }
                                            ]" :key="a.label" :class="a.ok ? 'is-ok' : 'is-no'">
                                            <span class="vs-check"><b-icon :icon="a.ok ? 'check' : 'x'"></b-icon></span>
                                            <span>{{ a.label }}</span>
                                        </li>
                                    </ul>

                                    <div class="vs-signature">
                                        <p>Customer acknowledges that they have read the store policy and agree to all Terms and Conditions.</p>
                                        <div class="vs-signature-box">
                                            <span>Customer signature</span>
                                            <div v-if="invoice.signature_url">
                                                <img :src="getSignatureUrl(invoice.signature_url)" alt="Customer signature" @error="handleSignatureError" />
                                            </div>
                                            <div v-else class="vs-signature-line">Not signed</div>
                                        </div>
                                    </div>
                                </section>

                                <!-- ===================================================== -->
                                <!-- PRINTED INVOICE BODY (print only)                      -->
                                <!-- ===================================================== -->
                                <div class="pi-doc">

                                    <!-- Customer information -->
                                    <section class="pi-card">
                                        <h2 class="pi-title">Customer information</h2>
                                      <div class="pi-cust">
    <!-- Line 1: contact info (4 fields) -->
    <div class="pi-cust-line is-contact">
        <div class="pi-cust-row"><span>Name</span><b>{{ getCustomerFullName() }}</b></div>
        <div class="pi-cust-row"><span>Email</span><b>{{ (invoice.customer && invoice.customer.email) || '—' }}</b></div>
        <div class="pi-cust-row"><span>Phone 1</span><b>{{ getCustomerPhone() || '—' }}</b></div>
        <div class="pi-cust-row"><span>Phone 2</span><b>{{ getCustomerPhone2() || '—' }}</b></div>
    </div>

    <!-- Line 2: address info (6 fields) -->
    <div class="pi-cust-line is-address">
        <div class="pi-cust-row"><span>Address</span><b>{{ (invoice.customer && invoice.customer.address_line_1) || '—' }}</b></div>
        <div class="pi-cust-row"><span>Address 2</span><b>{{ (invoice.customer && invoice.customer.address_line_2) || '—' }}</b></div>
        <div class="pi-cust-row"><span>Floor</span><b>{{ (invoice.customer && invoice.customer.floor) || '—' }}</b></div>
        <div class="pi-cust-row"><span>City</span><b>{{ (invoice.customer && invoice.customer.city) || '—' }}</b></div>
        <div class="pi-cust-row"><span>State</span><b>{{ (invoice.customer && invoice.customer.state) || '—' }}</b></div>
        <div class="pi-cust-row"><span>Zip code</span><b>{{ (invoice.customer && invoice.customer.zipcode) || '—' }}</b></div>
    </div>

</div>
                                        <div v-if="invoice.comment_txt" class="pi-comment"><b>Comments:</b> {{ invoice.comment_txt }}</div>

                                        
                                    </section>
                                        <div class="pi-cust-line is-confirm">
        <div class="pi-cust-row pi-cust-row--confirm">
            <b :class="{ 'is-confirmed': addressConfirmationAgreed, 'is-warn-text': !addressConfirmationAgreed }">
                <template v-if="addressConfirmationAgreed">
                    Customer confirmed that the shipping address and phone number are correct and acknowledged the $200 redelivery fee for incorrect information.
                </template>
                <template v-else>
                    Address and phone number NOT yet confirmed by the customer.
                </template>
            </b>
        </div>
    </div>

                                    <!-- Order details: card view, sets show their pieces underneath -->
                                    <section class="pi-card">
                                        <h2 class="pi-title">Order details</h2>

                                        <div class="pi-plist-head">
                                            <span>Item</span><span>Description</span><span>Delivery</span><span>Pricing</span><span>Total</span>
                                        </div>

                                        <div class="pi-plist">
                                            <template v-for="row in displayRows">

                                                <!-- Parent / set -->
                                                <div v-if="row.kind === 'parent'" :key="'pi-' + row.key"
                                                     class="pi-pcard pi-pcard--parent" :class="{ 'is-returned': isParentReturned(row.items) }">
                                                    <div class="pi-pimg"><img v-if="parentImageForGroup(row.sku)" :src="parentImageForGroup(row.sku)" alt="" /></div>
                                                    <div class="pi-pdesc">
                                                        <div class="pi-kicker">Product set &bull; {{ row.items.length }} pieces</div>
                                                        <div class="pi-pname">{{ (row.items[0].parent_product_name || 'Group product').toUpperCase() }}</div>
                                                        <div class="pi-sub">SKU {{ row.sku }}<template v-if="row.items[0].parent_vendor_sku"> &bull; {{ row.items[0].parent_vendor_sku }}</template></div>
                                                        <div v-if="isParentReturned(row.items)" class="pi-flag">Returned</div>
                                                    </div>
                                                    <div class="pi-pdel">
                                                        <div class="pi-sub">Delivery is listed on each piece below.</div>
                                                    </div>
                                                    <div class="pi-pprice">
                                                        <div v-if="vsSetRegularPrice(row.items) > 0" class="pi-prow is-strike"><span>Regular price</span><b>{{ vsMoney(vsSetRegularPrice(row.items)) }}</b></div>
                                                        <div class="pi-prow"><span>Selling price</span><b>{{ vsMoney(vsSetListPrice(row.items)) }}</b></div>
                                                        <div v-if="vsSetSalePrice(row.items) !== null" class="pi-prow is-good"><span>{{ vsSetSaleTypeLabel(row.items) }}</span><b>{{ vsMoney(vsSetSalePrice(row.items)) }}</b></div>
                                                    </div>
                                                    <div class="pi-ptotal">
                                                        <div class="pi-prow"><span>Qty</span><b>{{ vsSetQty(row.items) }}</b></div>
                                                        <div v-if="vsSetDiscount(row.items) > 0" class="pi-prow is-good"><span>Discount</span><b>-{{ vsMoney(vsSetDiscount(row.items)) }}</b></div>
                                                        <div v-if="vsSetSavings(row.items) > 0" class="pi-prow is-good"><span>You save</span><b>{{ vsMoney(vsSetSavings(row.items)) }}</b></div>
                                                        <div class="pi-prow is-total"><span>Set total</span><b>{{ vsMoney(vsSetTotal(row.items)) }}</b></div>
                                                    </div>
                                                </div>

                                                <!-- Item / piece -->
                                                <div v-else :key="'pi-' + row.key" class="pi-pcard"
                                                     :class="{ 'is-child': row.isChild, 'is-last': row.isLast, 'is-returned': is_returned(row.item) }">
                                                    <div class="pi-pimg"><img v-if="getProductImage(row.item)" :src="getProductImage(row.item)" alt="" /></div>
                                                    <div class="pi-pdesc">
                                                        <div v-if="row.isChild" class="pi-kicker is-muted">Piece of {{ row.item.parent_product_sku }}</div>
                                                        <div class="pi-pname">{{ (row.item.product_name || row.item.product_sku || '').toUpperCase() }}</div>
                                                        <div class="pi-sub">SKU {{ (row.item.product_sku || '').toUpperCase() }}<template v-if="row.item.vendor_sku"> &bull; {{ row.item.vendor_sku }}</template></div>

                                                        <div class="pi-sub pi-status-line"><span class="pi-status-label">Status</span> {{ getStateDisplayName(row.item.state) || '—' }}</div>

                                                        <div v-if="row.item.as_is === true || row.item.is_damaged" class="pi-flag">Sold as-is &bull; non-returnable</div>
                                                        <div v-if="(row.item.product_id < 0 || !row.item.product_id) && row.item.is_special" class="pi-flag is-orange">Special order &bull; up to 8 weeks</div>
                                                        <div v-if="is_returned(row.item)" class="pi-flag">Returned</div>
                                                    </div>

                                                    <div class="pi-pdel">
                                                        <table class="pi-pdel-table">
                                                            <tbody>
                                                                <tr>
                                                                    <th>Type</th>
                                                                    <td>{{ formatOrderType(row.item.order_type) }}</td>
                                                                </tr>
                                                                <tr>
                                                                    <th>Date</th>
                                                                    <td>
                                                                        {{ row.item.delivery_date || '—' }}
                                                                        <span v-if="row.item.delivery_date" class="pi-pdel-dow">({{ getDayOfWeek(row.item.delivery_date) }})</span>
                                                                    </td>
                                                                </tr>
                                                                <!-- ✅ CHANGED: Dynamic label based on order_type -->
                                                                <tr>
                                                                    <th>{{ locationLabelFor(row.item.order_type) }}</th>
                                                                    <td>
                                                                        {{ getLocationDisplay(row.item.drop_locsub) || row.item.drop_locsub || 'N/A' }}
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                        <div v-if="row.item.last_delivery_date && row.item.last_delivery_date !== row.item.delivery_date" class="pi-prev">
                                                            Prev: {{ row.item.last_delivery_date }}
                                                        </div>
                                                    </div>

                                                    <!-- Pricing column: regular / selling / sale-type price -->
                                                    <div class="pi-pprice">
                                                        <div v-if="vsRegularPrice(row.item) > 0" class="pi-prow is-strike"><span>Regular price</span><b>{{ vsMoney(vsRegularPrice(row.item)) }}</b></div>
                                                        <div class="pi-prow"><span>Selling price</span><b>{{ vsMoney(vsListPrice(row.item)) }}</b></div>
                                                        <div v-if="vsSalePrice(row.item) !== null" class="pi-prow is-good"><span>{{ vsSaleTypeLabel(row.item) }}</span><b>{{ vsMoney(vsSalePrice(row.item)) }}</b></div>
                                                    </div>

                                                    <!-- Total column: qty / discount / you save / amount -->
                                                    <div class="pi-ptotal">
                                                        <div class="pi-prow"><span>Qty</span><b>{{ (row.item.quantity || 0) - (row.item.refunded_so_far || 0) }}</b></div>
                                                        <div v-if="vsUnitDiscount(row.item) > 0" class="pi-prow is-good"><span>Unit discount</span><b>-{{ vsMoney(vsUnitDiscount(row.item)) }}</b></div>
                                                        <div v-if="vsItemSavings(row.item) > 0" class="pi-prow is-good"><span>You save</span><b>{{ vsMoney(vsItemSavings(row.item)) }}</b></div>
                                                        <div class="pi-prow is-total"><span>Amount</span><b>{{ vsMoney(vsLineAmount(row.item)) }}</b></div>
                                                    </div>
                                                </div>
                                            </template>
                                        </div>

                                        <p class="pi-foot">Thank you for choosing Furniture Mecca! Scan the QR code above to track or reschedule your delivery.</p>
                                    </section>

                                    <!-- Charges and payments -->
                                    <section class="pi-card pi-totals">
                                        <div class="pi-col">
                                            <h3>Extra charges</h3>
                                            <div class="pi-row"><span>Delivery</span><i></i><b>${{ parseFloat(invoice.delivery_charges || 0).toFixed(2) }}</b></div>
                                            <div class="pi-row"><span>White Glove</span><i></i><b>${{ parseFloat(invoice.whiteglove_charges || 0).toFixed(2) }}</b></div>
                                            <div class="pi-row"><span>Setup</span><i></i><b>${{ parseFloat(invoice.setup_charges || 0).toFixed(2) }}</b></div>

                                            <!-- WARRANTY: 0 → show refused message instead of price -->
                                            <div v-if="parseFloat(invoice.warranty_charges || 0) > 0" class="pi-row">
                                                <span>Warranty</span><i></i><b>${{ parseFloat(invoice.warranty_charges || 0).toFixed(2) }}</b>
                                            </div>
                                            <div v-else class="pi-row pi-warranty-refused">
                                                <span>Warranty</span><i></i><b>Customer refused to purchase extended warranties</b>
                                            </div>

                                            <div class="pi-row pi-row--accent"><span>Extra charges total</span><i></i><b>${{ getChargesTotal().toFixed(2) }}</b></div>
                                        </div>
                                        <div class="pi-col">
                                            <h3>Total charges</h3>
                                            <div class="pi-row"><span>Total discount</span><i></i><b>${{ getTotalDiscount().toFixed(2) }}</b></div>
                                            <div class="pi-row"><span>Order total</span><i></i><b>${{ getOrderTotal().toFixed(2) }}</b></div>
                                            <div class="pi-row"><span>Extra charges</span><i></i><b>${{ getChargesTotal().toFixed(2) }}</b></div>
                                            <div class="pi-row"><span>Taxable total</span><i></i><b>${{ getTaxableSubtotal().toFixed(2) }}</b></div>
                                            <div class="pi-row">
                                                <span>Sales tax<template v-if="parseFloat(invoice.tax_perc) > 0"> ({{ +(parseFloat(invoice.tax_perc) * 100).toFixed(2) }}%)</template></span><i></i>
                                                <b>${{ getSalesTaxAmount().toFixed(2) }}</b>
                                            </div>
                                            <div class="pi-grand"><span>Grand total</span><b>${{ getGrandTotal().toFixed(2) }}</b></div>
                                        </div>
                                        <div class="pi-col">
                                            <h3>Payments</h3>
                                            <div v-for="(pm, pi) in orderedPaymentMethods" :key="'pi-pm-' + pi" class="pi-row">
                                                <span>
                                                    <template v-if="pm.source">{{ (pm.source || '').toUpperCase() }} &mdash; </template>
                                                    {{ (pm.value || '').toUpperCase() }}<template v-if="pm.name && pm.name !== 'N/A'"> - {{ pm.name }}</template>
                                                </span>
                                                <i></i>
                                                <b>${{ (parseFloat(pm.amount) || 0).toFixed(2) }}</b>
                                            </div>

                                            <template v-if="allFinanceAgreements.length > 0">
                                                <div
                                                    v-for="(fin, fi) in allFinanceAgreements"
                                                    :key="'pi-fin-amt-' + fi"
                                                    class="pi-row"
                                                >
                                                    <span>
                                                        Finance amount
                                                        ({{ (fin.company_name || 'FINANCE').toUpperCase() }}<template v-if="fin.lease_id"> · {{ fin.lease_id }}</template>)
                                                    </span>
                                                    <i></i>
                                                    <b>${{ fin.amount.toFixed(2) }}</b>
                                                </div>
                                            </template>

                                            <div v-if="getTaxExemptOrFinanceTotal > 0 && allFinanceAgreements.length === 0" class="pi-row">
                                                <span>
                                                    <template v-if="hasTaxExemptPayment">Tax exempt<template v-if="getTaxExemptCompanyNames.length"> ({{ getTaxExemptCompanyNames.join(', ') }})</template></template>
                                                    <template v-else>Finance<template v-if="getFinanceCompanyNames.length"> ({{ getFinanceCompanyNames.join(', ') }})</template></template>
                                                </span><i></i>
                                                <b>${{ getTaxExemptOrFinanceTotal.toFixed(2) }}</b>
                                            </div>

                                            <div v-if="financeAgreementsWithBanners.length > 0" class="pi-finance-banners">
                                                <div
                                                    v-for="(fin, fi) in financeAgreementsWithBanners"
                                                    :key="'pi-fin-banner-' + fi"
                                                    class="pi-finance-banner"
                                                >
                                                    <div class="pi-finance-banner-left">
                                                        <div class="pi-finance-banner-name">{{ (fin.company_name || 'FINANCE').toUpperCase() }}</div>
                                                        <div v-if="fin.banner_image" class="pi-finance-banner-img-wrap">
                                                            <img
                                                                :src="fin.banner_image"
                                                                alt="Banner"
                                                                class="pi-finance-banner-img-el"
                                                                @error="onFinanceBannerError($event, fin)"
                                                            />
                                                        </div>
                                                        <div v-else class="pi-finance-banner-img is-empty">
                                                            <b-icon icon="image"></b-icon>
                                                        </div>
                                                        <div class="pi-finance-approved">
                                                            <span class="pi-finance-approved-amt">${{ fin.approved_amount.toFixed(2) }}</span>
                                                            <span class="pi-finance-approved-label">APPROVED</span>
                                                        </div>
                                                    </div>
                                                    <div class="pi-finance-banner-right">
                                                        <div class="pi-finance-info-row"><span>LEASE ID</span><b>{{ fin.lease_id || '—' }}</b></div>
                                                        <div class="pi-finance-info-row"><span>USED</span><b>${{ fin.used_amount.toFixed(2) }}</b></div>
                                                        <div class="pi-finance-info-row"><span>BALANCE</span><b>${{ fin.balance_amount.toFixed(2) }}</b></div>
                                                    </div>
                                                    <div class="pi-finance-banner-foot">
                                                        YOU MAY HAVE 90 DAYS TO USE YOUR BALANCE
                                                    </div>
                                                </div>
                                            </div>

                                            <div v-if="shouldShowParentPayment" class="pi-row"><span>Parent payment ({{ invoice.parent_invoice_id }})</span><i></i><b>${{ allocatedParentPaymentTotal.toFixed(2) }}</b></div>
                                            <div class="pi-row pi-row--line"><span>Sub total</span><i></i><b>${{ getSubTotal().toFixed(2) }}</b></div>
                                            <div class="pi-row pi-row--strong"><span>Total paid</span><i></i><b>${{ get_pms_total().toFixed(2) }}</b></div>
                                            <div v-if="get_total_balance() > 0 && invoice.invoice_type !== 'EX'" class="pi-row pi-row--accent">
                                                <span>Balance due</span><i></i><b>${{ get_total_balance().toFixed(2) }}</b>
                                            </div>
                                        </div>
                                    </section>

                                    <!-- Layaway payments -->
                                    <section v-if="invoice.is_layaway && getLayawayPayments().length" class="pi-card">
                                        <h2 class="pi-title">Layaway payments</h2>
                                        <div class="pi-layaway">
                                            <div v-for="(payment, index) in getLayawayPayments()" :key="'pi-lw-' + index">
                                                <span>Payment {{ index + 1 }} &bull; {{ (payment.method || '').toUpperCase() }}</span>
                                                <b>${{ parseFloat(payment.amount || 0).toFixed(2) }}</b>
                                                <small>{{ payment.ts ? payment_ts_format(payment.ts).split(' ')[0] : '' }}</small>
                                            </div>
                                        </div>
                                    </section>

                                    <!-- Finance agreements -->
                                    <section v-if="allFinanceAgreements.length > 0" class="pi-finance">
                                        <h3>Finance agreements</h3>
                                        <div v-for="(finance, index) in allFinanceAgreements" :key="'pi-fin-' + index" class="pi-finance-line">
                                            <b>{{ (finance.company_name || 'Finance').toUpperCase() }}</b>
                                            <template v-if="finance.lease_id"> &bull; Lease ID: {{ finance.lease_id }}</template>
                                            <template v-if="finance.company_phone"> &bull; Phone: {{ finance.company_phone }}</template>
                                            &bull; Finance amount: ${{ finance.amount.toFixed(2) }}
                                            &bull; Used: ${{ finance.used_amount.toFixed(2) }}
                                            &bull; Balance: ${{ finance.balance_amount.toFixed(2) }}
                                        </div>
                                        <p>Financed payments are processed by the finance company listed above. See the lease agreement for its terms.</p>
                                    </section>
                                </div>

                                <!-- PRINT VERSION OF POLICY -->
                                <div class="print-only-policy" style="display: none;">
                                    <div style="page-break-before: always;">
                                        <div style="text-align: center">
                                            <h3 style="text-decoration: underline; font-size: 18px;"><b>TERMS AND CONDITIONS / RETURN POLICY</b></h3>
                                        </div>
                                        <div v-html="policy_text" style="margin-left: 40px; font-size: 8px; line-height: 1.1; width: 90%;"></div>

                                        <div style="margin-left: 40px; margin-top: 0px; margin-bottom: 0px; width: 90%; padding-top: 0px;">
                                            <h6 style="font-size: 14px; font-weight: bold; margin-bottom: 15px;">CUSTOMER AGREEMENTS:</h6>

                                            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
                                                <div style="display: flex; align-items: flex-start;">
                                                    <span v-if="termsAgreed" style="color: green; font-size: 16px; font-weight: bold; margin-right: 10px;">✓</span>
                                                    <span v-else style="color: red; font-size: 16px; font-weight: bold; margin-right: 10px;">✗</span>
                                                    <span style="font-size: 11px;">I have read and agree to the Terms & Conditions</span>
                                                </div>
                                                <div style="display: flex; align-items: flex-start;">
                                                    <span v-if="warrantyRefused" style="color: green; font-size: 16px; font-weight: bold; margin-right: 10px;">✓</span>
                                                    <span v-else style="color: red; font-size: 16px; font-weight: bold; margin-right: 10px;">✗</span>
                                                    <span style="font-size: 11px;">Customer refused to purchase extended warranties</span>
                                                </div>
                                                <div style="display: flex; align-items: flex-start;">
                                                    <span v-if="deliveryWaiverAgreed" style="color: green; font-size: 16px; font-weight: bold; margin-right: 10px;">✓</span>
                                                    <span v-else style="color: red; font-size: 16px; font-weight: bold; margin-right: 10px;">✗</span>
                                                    <span style="font-size: 11px;">Waived White Glove service (Standard Delivery only)</span>
                                                </div>
                                                <div style="display: flex; align-items: flex-start;">
                                                    <span v-if="spacePreparationAgreed" style="color: green; font-size: 16px; font-weight: bold; margin-right: 10px;">✓</span>
                                                    <span v-else style="color: red; font-size: 16px; font-weight: bold; margin-right: 10px;">✗</span>
                                                    <span style="font-size: 11px;">Acknowledged space preparation requirement ($150 reschedule fee)</span>
                                                </div>
                                                <div style="display: flex; align-items: flex-start;">
                                                    <span v-if="addressConfirmationAgreed" style="color: green; font-size: 16px; font-weight: bold; margin-right: 10px;">✓</span>
                                                    <span v-else style="color: red; font-size: 16px; font-weight: bold; margin-right: 10px;">✗</span>
                                                    <span style="font-size: 11px;">Confirmed delivery address accuracy and accessibility</span>
                                                </div>
                                                <div style="display: flex; align-items: flex-start;">
                                                    <span v-if="deliveryDamageWaiverAgreed" style="color: green; font-size: 16px; font-weight: bold; margin-right: 10px;">✓</span>
                                                    <span v-else style="color: red; font-size: 16px; font-weight: bold; margin-right: 10px;">✗</span>
                                                    <span style="font-size: 11px;">Accepted delivery damage waiver (FM not responsible for delivery damages)</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div style="text-align: right; width: 100%; margin-top: 20px;">
                                            <div v-if="invoice.signature_url" style="text-align: right;">
                                                <img
                                                    :src="getSignatureUrl(invoice.signature_url)"
                                                    alt="Customer Signature"
                                                    style="max-height: 80px; max-width: 300px;"
                                                    @error="handleSignatureError"
                                                />
                                            </div>
                                            <div v-else>
                                                _____________________________________
                                            </div>
                                            <div style="margin-bottom: 10px; font-size: 12px;">
                                                <strong style="color:red;font-style:italic">Customer acknowledges that they have read the store policy and agree to all terms and conditions.</strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
    import moment from 'moment'
    import mappings from '@/utilities/mappings'
    import flatPickr from 'vue-flatpickr-component';
    import 'flatpickr/dist/flatpickr.css';
    import Requestor from '@/utilities/Requestor'

    import Loading from 'vue-loading-overlay';
    import 'vue-loading-overlay/dist/vue-loading.css';

    import CustomerServiceTicket from '@/components/CustomerService/CustomerServiceTicket'
    import RefundsSummary from "@/components/importables/RefundsSummary"
    import LayAwayPaymentsModal from "@/components/Sales/LayAwayPaymentsModal"
    import EditInvoiceWrapper from '@/components/Sales/EditInvoiceWrapper'
    import TicketListModal from '@/components/CustomerService/TicketListModal';

    import DatePicker from 'vue2-datepicker';
    import 'vue2-datepicker/index.css';

    export default {
        name: "ViewSale2",
        props: ["viewdata"],
        components: {flatPickr, Loading, CustomerServiceTicket, RefundsSummary, DatePicker, EditInvoiceWrapper, LayAwayPaymentsModal,  TicketListModal},
        data: function() {
            return {
                route: null,
                now: this.get_time(),
                packet: {},
                currentSignatureUrl: null,
                invoice: {
                    customer: {
                        fullname: '',
                        address_line_1: '',
                        address_line_2: '',
                        phone: '',
                        phone_2: '',
                        email: '',
                        city: '',
                        state: '',
                        zipcode: '',
                        floor: ''
                    }
                },
                order_types_dict: {
                    "store_pickup": "STORE PICKUP",
                    "home_delivery": "HOME DELIVERY",
                    "customer_takes": "CUSTOMER TOOK"
                },
                all_loc_inventory_dict: {},
                financeLeaseId: null,
                isPageLoading: true,
                loadingProgress: 0,
                loadingInterval: null,
                financeMiscInfo: null,
                showFinanceDetails: false,
                flatpickrConfig: { dateFormat: "Y-m-d" },
                states: mappings.states,
                currentTicketItem: null,
                currentTickets: [],
                merchandise_statuses: [
                    {"value": null, "text": "NONE"},
                    {"value": "IN STOCK", "text": "IN STOCK"},
                    {"value": "SPECIAL", "text": "SPECIAL"},
                    {"value": "BACKORDER", "text": "BACKORDER"},
                    {"value": "ORDERED", "text": "ORDERED"},
                    {"value": "DISCONTINUED", "text": "DISCONTINUED"},
                    {"value": "COMPLETED", "text": "COMPLETED"},
                ],
                update_disabled: false,
                deliveryWaiverAgreed: false,
                spacePreparationAgreed: false,
                ticketsLoading: false,
                deliveryDamageWaiverAgreed: false,
                addressConfirmationAgreed: false,
                termsAgreed: false,
                warrantyRefused: false,
                invoice_location_details: {},
                processed_statuses: [
                    {"value": null, "text": "NEW ORDER"},
                    {"value": "open", "text": "OPEN ORDER"},
                    {"value": "processed", "text": "PROCESSED"},
                ],
                processed_status: null,
                refund_payment_modes_dict: {
                    1: "CC/CASH RCVD",
                    2: "STORE PURCHASE"
                },
                refunded_total: null,
                invoice_being_loaded: false,
                cs_item_data: {},
                return_reasons: [
                    {value: 0, text: "OTHER"},
                    {value: 1, text: "ITEM DAMAGED"},
                    {value: 2, text: "ITEM INCOMPLETE"},
                ],
                refunds: this.get_refunds_packet(),
                is_delivered: [
                    {value: false, text: "NO"},
                    {value: true, text: "YES"}
                ],
                refund_perc_vals: [
                    {value: 0.0, text: "0%"},
                    {value: 0.10, text: "10%"},
                    {value: 0.20, text: "20%"},
                    {value: 0.30, text: "30%"},
                    {value: 0.40, text: "40%"},
                    {value: 0.50, text: "50%"},
                    {value: 0.60, text: "60%"},
                    {value: 0.70, text: "70%"},
                    {value: 0.80, text: "80%"},
                    {value: 0.90, text: "90%"},
                    {value: 1.0, text: "100%"},
                ],
                policy_text: null,
                edit_invoice_action: false,
                total_refunded_value: 0.0,
                order_types: [
                    {"value": "customer_takes", "text": "Take Home"},
                    {"value": "store_pickup", "text": "Store Pickup"},
                    {"value": "home_delivery", "text": "Home Delivery"},
                    {"value": "delivery_from_store", "text": "Delivery from Store"},
                ],
                finance_companies: [],
                currentFullSizeImage: null,
                currentProductName: null,
                parentInvoiceData: null,
                parentInvoicePaymentMethods: [],
                showParentPaymentInfo: false,

                // edit tracking
                editCount: 0,
                lastEditedAt: null,
                lastEditedBy: null,
                invoiceEditHistory: [],
                historyLoading: false,
                historyError: null,
                historyLoadedOnce: false,
            }
        },
        computed: {
            orderedPaymentMethods() {
                if (!this.invoice.payment_methods || this.invoice.payment_methods.length === 0) {
                    return [];
                }
                const sourcePriority = {
                    'credit': 0,
                    'payment': 1,
                };
                const entries = this.invoice.payment_methods.slice();
                entries.sort((a, b) => {
                    const sa = (a && a.source) ? String(a.source).toLowerCase() : '';
                    const sb = (b && b.source) ? String(b.source).toLowerCase() : '';
                    const pa = (sa in sourcePriority) ? sourcePriority[sa] : 2;
                    const pb = (sb in sourcePriority) ? sourcePriority[sb] : 2;
                    if (pa !== pb) return pa - pb;
                    return (parseInt(a.idx) || 0) - (parseInt(b.idx) || 0);
                });
                return entries;
            },
            invoiceHasHistory() {
                return (this.editCount && this.editCount > 0) ||
                       (this.invoiceEditHistory && this.invoiceEditHistory.length > 0);
            },
            lastEditedTooltip() {
                if (!this.editCount) return '';
                const parts = [];
                if (this.lastEditedBy) parts.push('Last by ' + this.lastEditedBy);
                if (this.lastEditedAt) parts.push('on ' + this.formatDate(this.lastEditedAt));
                return parts.join(' ');
            },
            hasChildInvoices() {
                return this.invoice.child_invoices && this.invoice.child_invoices.length > 0;
            },
            childInvoiceCount() {
                return this.invoice.child_invoices ? this.invoice.child_invoices.length : 0;
            },
            totalRefundAmount() {
                return this.total_refunded_value || 0;
            },
            hasTaxExemptPayment() {
                if (!this.invoice.payment_methods) return false;
                return this.invoice.payment_methods.some(pm => pm.value === 'tax_exempt');
            },
            getTaxExemptCompanyNames() {
                if (!this.invoice.payment_methods) return [];
                return this.invoice.payment_methods
                    .filter(pm => pm.value === 'tax_exempt' && pm.name)
                    .map(pm => pm.name);
            },
            getTaxExemptTotal() {
                if (!this.invoice.payment_methods) return 0;
                return this.invoice.payment_methods
                    .filter(pm => pm.value === 'tax_exempt')
                    .reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
            },
            getTaxExemptOrFinanceTotal() {
                if (this.hasTaxExemptPayment) {
                    return this.getTaxExemptTotal;
                }
                return this.getFinanceTotal;
            },

            allFinanceAgreements() {
                if (!this.invoice.payment_methods || this.invoice.payment_methods.length === 0) {
                    return [];
                }

                return this.invoice.payment_methods
                    .filter(pm => pm.value === 'finance')
                    .map(pm => {
                        const targetName = (pm.name || '').trim().toUpperCase();
                        const financeCompany = this.finance_companies.find(fc =>
                            (fc.value || '').trim().toUpperCase() === targetName
                        );

                        const financeAmount = parseFloat(pm.amount) || 0;

                        const usedAmount = parseFloat(
                            pm.used_amount ||
                            pm.amount_used ||
                            pm.spent_amount ||
                            pm.drawn_amount ||
                            financeAmount
                        ) || 0;

                        const approvedAmount = parseFloat(
                            pm.approved_amount || pm.approved || financeAmount
                        ) || 0;

                        const balanceAmount = Math.max(approvedAmount - usedAmount, 0);

                        return {
                            company_name: pm.name,
                            company_ein: pm.ein || null,
                            amount: financeAmount,
                            used_amount: usedAmount,
                            approved_amount: approvedAmount,
                            balance_amount: balanceAmount,
                            lease_id: pm.lease_id || null,
                            misc_info: pm.misc_info || null,
                            company_phone: financeCompany ? financeCompany.phone_number : null,
                            banner_image_raw: financeCompany ? financeCompany.banner_image : null,
                            charged_tax_itself: pm.charged_tax_itself || false,
                            idx: pm.idx || 0,
                            ts: pm.ts || null,
                        };
                    });
            },
            financeAgreementsWithBanners() {
                return this.allFinanceAgreements.map(fin => {
                    let bannerUrl = fin.banner_image_raw;

                    if (bannerUrl && (
                        bannerUrl.includes('blob.core.windows.net') ||
                        bannerUrl.includes('fmapi.myfurnituremecca.com')
                    )) {
                        bannerUrl = `/api/image/image-proxy/?url=${encodeURIComponent(bannerUrl)}`;
                    }

                    return {
                        ...fin,
                        banner_image: bannerUrl,
                    };
                });
            },
            allTaxExemptAgreements() {
                if (!this.invoice.payment_methods || this.invoice.payment_methods.length === 0) {
                    return [];
                }

                return this.invoice.payment_methods
                    .filter(pm => pm.value === 'tax_exempt')
                    .map(pm => ({
                        company_ein: pm.ein || null,
                        company_name: pm.name,
                        amount: parseFloat(pm.amount) || 0,
                        idx: pm.idx || 0,
                        ts: pm.ts || null
                    }));
            },
            financeLeaseInfo() {
                return this.allFinanceAgreements[0] || {
                    lease_id: null,
                    misc_info: null,
                    company_name: null,
                    company_phone: null,
                    amount: 0
                };
            },
            hasFinanceLeaseInfo() {
                return this.allFinanceAgreements.length > 0;
            },
            getFinanceTotal() {
                if (!this.invoice.payment_methods) return 0;
                return this.invoice.payment_methods
                    .filter(pm => pm.value === 'finance')
                    .reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
            },
            getFinanceCompanyNames() {
                if (!this.invoice.payment_methods) return [];
                return this.invoice.payment_methods
                    .filter(pm => pm.value === 'finance' && pm.name)
                    .map(pm => pm.name);
            },
            predictedSalesTax() {
                const remainingBalance = this.get_total_balance();
                if (remainingBalance <= 0) return 0;

                const taxRate = parseFloat(this.invoice.tax_perc) || 0;
                if (taxRate === 0) return 0;

                const soldFromState = this.invoice.sold_from_state || 'PA';
                let taxableRemaining = remainingBalance;

                if (this.invoice.payment_methods && this.invoice.payment_methods.length > 0) {
                    const lastPayment = this.invoice.payment_methods[this.invoice.payment_methods.length - 1];
                    if (lastPayment && lastPayment.value === 'finance') {
                        const financeCompany = this.finance_companies.find(fc => fc.value === lastPayment.name);
                        if (financeCompany &&
                            financeCompany.company_charges_tax_in_states &&
                            !financeCompany.company_charges_tax_in_states.includes(soldFromState)) {
                            taxableRemaining = 0;
                        }
                    }
                }

                return this.roundTo(taxableRemaining * taxRate, 2);
            },
            totalWithPredictedTax() {
                const remainingBalance = this.get_total_balance();
                const predictedTax = this.predictedSalesTax;

                return {
                    remainingBalance: remainingBalance,
                    predictedTax: predictedTax,
                    totalToPay: remainingBalance + predictedTax
                };
            },
            groupedByParent() {
                const groups = {};
                if (!this.invoice.items) return groups;
                this.invoice.items.forEach(item => {
                    const parentSku = item.parent_product_sku || item.product_sku;
                    if (!groups[parentSku]) {
                        groups[parentSku] = [];
                    }
                    groups[parentSku].push(item);
                });
                return groups;
            },
            orderedParentSkuList() {
                const groups = this.groupedByParent;
                const individualSkus = [];
                const groupSkus = [];

                Object.keys(groups).forEach(parentSku => {
                    const group = groups[parentSku];
                    if (group[0].parent_product_sku && group.length > 1) {
                        groupSkus.push(parentSku);
                    } else {
                        individualSkus.push(parentSku);
                    }
                });

                return [...individualSkus, ...groupSkus];
            },
            displayRows() {
                const rows = [];
                this.orderedParentSkuList.forEach(sku => {
                    const group = this.groupedByParent[sku];
                    if (!group || group.length === 0) return;
                    const isGroup = !!group[0].parent_product_sku && group.length > 1;
                    if (isGroup) {
                        rows.push({ kind: 'parent', key: 'p-' + sku, sku: sku, items: group });
                        group.forEach((item, i) => {
                            rows.push({
                                kind: 'item',
                                key: 'c-' + sku + '-' + i,
                                item: item,
                                isChild: true,
                                isLast: i === group.length - 1,
                            });
                        });
                    } else {
                        group.forEach((item, i) => {
                            rows.push({ kind: 'item', key: 'r-' + sku + '-' + i, item: item, isChild: false, isLast: false });
                        });
                    }
                });
                return rows;
            },
            parentSkuOrder() {
                return Object.keys(this.groupedByParent);
            },
            isExchangeInvoice() {
                return this.invoice.invoice_type === 'EX' && this.invoice.parent_invoice_id;
            },
            shouldShowParentPayment() {
                const isExchange = this.invoice.invoice_type === 'EX' && this.invoice.parent_invoice_id;

                let paymentMethods = this.parentInvoicePaymentMethods;

                if ((!paymentMethods || paymentMethods.length === 0) && this.parentInvoiceData) {
                    paymentMethods = this.parentInvoiceData.payment_methods || [];
                    this.parentInvoicePaymentMethods = paymentMethods;
                }

                return isExchange && this.parentInvoiceData && paymentMethods && paymentMethods.length > 0;
            },
            parentInvoiceTotal() {
                if (!this.parentInvoiceData) return 0;
                return parseFloat(this.parentInvoiceData.total) || 0;
            },
            getParentPaymentMethodsForItems() {
                if (!this.shouldShowParentPayment) return [];

                const methods = this.parentInvoicePaymentMethods;
                const exchangeTotal = this.getExchangeTotal;

                if (exchangeTotal === 0 || methods.length === 0) return [];

                const parentTotal = methods.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
                if (parentTotal === 0) return methods;

                const percentage = Math.min(exchangeTotal / parentTotal, 1);

                return methods.map(pm => ({
                    ...pm,
                    allocated_amount: (parseFloat(pm.amount) || 0) * percentage,
                    display_amount: ((parseFloat(pm.amount) || 0) * percentage).toFixed(2),
                    original_amount: pm.amount,
                }));
            },
            parentPaymentTotal() {
                if (!this.parentInvoicePaymentMethods) return 0;
                return this.parentInvoicePaymentMethods.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
            },
            allocatedParentPaymentTotal() {
                const methods = this.getParentPaymentMethodsForItems;
                if (!methods || methods.length === 0) return 0;
                return methods.reduce((sum, pm) => sum + (parseFloat(pm.allocated_amount) || 0), 0);
            },
            getExchangeTotal() {
                if (!this.invoice.items) return 0;
                return this.invoice.items.reduce((sum, item) => {
                    const quantity = (item.quantity || 0) - (item.refunded_so_far || 0);
                    return sum + ((item.selling_price || 0) * quantity) - (item.discount_offered || 0);
                }, 0);
            },
        },
        methods: {
            // =====================================================
            // ITEM PRICING DISPLAY (mirrors NewSale.vue labels)
            // =====================================================
            vsMoney(v) {
                const n = parseFloat(v) || 0;
                return '$' + n.toFixed(2);
            },
            vsRound(n) {
                return Math.round((parseFloat(n) || 0) * 100) / 100;
            },
            // Product list ("selling") price. Falls back to the invoiced price
            // for special-order / non-inventory items that have no product row.
            vsListPrice(item) {
                if (!item) return 0;
                const list = parseFloat(item.p_selling_price);
                if (!isNaN(list) && list > 0) return this.vsRound(list);
                return this.vsRound(item.selling_price);
            },
            // Regular price = selling price + 100 (<1000), +200 (<2000), +500 (<5000), +1000 otherwise
            vsRegularPrice(item) {
                const selling = this.vsListPrice(item);
                if (selling <= 0) return 0;
                let bump;
                if (selling < 1000) bump = 100;
                else if (selling < 2000) bump = 200;
                else if (selling < 5000) bump = 500;
                else bump = 1000;
                return this.vsRound(selling + bump);
            },
            // Price actually invoiced, shown as "<Sale type> price" only when it is below the list price
            vsSalePrice(item) {
                if (!item) return null;
                const sold = this.vsRound(item.selling_price);
                const list = this.vsListPrice(item);
                if (sold > 0 && sold < list - 0.005) return sold;
                return null;
            },
            vsSaleTypeLabel(item) {
                const st = String((item && (item.p_sale_type || item.sale_type)) || '').trim();
                if (!st || st.toLowerCase() === 'discount') return 'Promo price';
                return st + ' price';
            },
            vsUnitDiscount(item) {
                if (!item) return 0;
                const unit = parseFloat(item.unit_discount_value) || 0;
                if (unit > 0) return this.vsRound(unit);
                const total = parseFloat(item.discount_offered) || 0;
                const qty = parseInt(item.quantity) || 0;
                return total > 0 && qty > 0 ? this.vsRound(total / qty) : 0;
            },
            vsLineAmount(item) {
                if (!item) return 0;
                const qty = parseFloat(item.quantity) || 0;
                return this.vsRound((parseFloat(item.selling_price) || 0) * qty - (parseFloat(item.discount_offered) || 0));
            },
            // ---------- Sets (parent products) ----------
            vsSetQty(items) {
                return parseFloat(items && items[0] && items[0].parent_product_quantity) || 1;
            },
            // Set list ("selling") price per set
            vsSetListPrice(items) {
                if (!items || !items.length) return 0;
                const first = items[0];
                const list = parseFloat(first.parent_p_selling_price) || parseFloat(first.parent_product_selling_price) || 0;
                if (list > 0) return this.vsRound(list);
                return this.vsSetUnitSold(items);
            },
            vsSetRegularPrice(items) {
                return this.vsRegularPrice({ p_selling_price: this.vsSetListPrice(items) });
            },
            // What one set was actually invoiced at (sum of pieces, before manual discount)
            vsSetUnitSold(items) {
                if (!items || !items.length) return 0;
                const total = items.reduce((sum, it) => sum + (parseFloat(it.selling_price) || 0) * (parseFloat(it.quantity) || 0), 0);
                return this.vsRound(total / this.vsSetQty(items));
            },
            vsSetSalePrice(items) {
                const sold = this.vsSetUnitSold(items);
                const list = this.vsSetListPrice(items);
                if (sold > 0 && sold < list - 0.005) return sold;
                return null;
            },
            vsSetSaleTypeLabel(items) {
                const first = (items && items[0]) || {};
                return this.vsSaleTypeLabel({ p_sale_type: first.parent_sale_type || first.parent_p_sale_type });
            },
            vsSetDiscount(items) {
                return this.vsRound(this.calculateParentTotalDiscount(items));
            },
            vsSetTotal(items) {
                return this.vsRound(this.calculateParentTotalAfterDiscount(items));
            },
            vsSetSavings(items) {
                const regular = this.vsSetRegularPrice(items);
                if (regular <= 0) return 0;
                const savings = regular * this.vsSetQty(items) - this.vsSetTotal(items);
                return savings > 0 ? this.vsRound(savings) : 0;
            },

            // You save = (regular price x qty) - amount paid
            vsItemSavings(item) {
                const regular = this.vsRegularPrice(item);
                if (regular <= 0) return 0;
                const qty = parseFloat(item.quantity) || 0;
                const savings = regular * qty - this.vsLineAmount(item);
                return savings > 0 ? this.vsRound(savings) : 0;
            },

            onFinanceBannerError(event, fin) {
                console.warn('Finance banner failed to load:', {
                    company: fin.company_name,
                    proxied: fin.banner_image,
                    raw: fin.banner_image_raw,
                });
                event.target.style.display = 'none';
            },

            // ============================================================
            // ✅ NEW: Location label/icon helpers based on order_type
            // ============================================================
            locationLabelFor(orderType) {
                return ({
                    store_pickup: 'Pickup location',
                    customer_takes: 'Fulfilled from',
                    home_delivery: 'Delivery location',
                    delivery_from_store: 'Delivery location',
                })[orderType] || 'Delivery location';
            },
            locationIconFor(orderType) {
                return ({
                    store_pickup: 'bag-check',
                    customer_takes: 'shop',
                    home_delivery: 'geo-alt',
                    delivery_from_store: 'geo-alt',
                })[orderType] || 'geo-alt';
            },

            fetchInvoiceEditCount() {
                if (!this.invoice || !this.invoice.invoice_id) return;
                const that = this;
                const path = "/api/sales/get_invoice_edit_count/";
                const data = { invoice_id: this.invoice.invoice_id };

                const s = function (response) {
                    if (response.data && response.data.result) {
                        const r = response.data.result;
                        that.editCount    = parseInt(r.edit_count || 0);
                        that.lastEditedAt = r.last_edited_at || null;
                        that.lastEditedBy = r.last_edited_by || null;
                    } else {
                        that.editCount    = 0;
                        that.lastEditedAt = null;
                        that.lastEditedBy = null;
                    }
                };
                const f = function (error) {
                    console.warn("Could not load invoice edit count:", error);
                };
                Requestor.postAsyncP(path, data, this, s, f);
            },

            fetchInvoiceEditHistory(force) {
                if (!this.invoice || !this.invoice.invoice_id) return Promise.resolve();
                if (this.historyLoadedOnce && !force) return Promise.resolve();

                const that = this;
                const path = "/api/sales/get_invoice_edit_history/";
                const data = { invoice_id: this.invoice.invoice_id };

                this.historyLoading = true;
                this.historyError = null;

                return new Promise(resolve => {
                    const s = function (response) {
                        if (response.data && response.data.error) {
                            that.historyError = response.data.error;
                            that.invoiceEditHistory = [];
                        } else {
                            that.invoiceEditHistory = (response.data && response.data.result) || [];
                            that.historyLoadedOnce = true;

                            if (!that.editCount && that.invoiceEditHistory.length) {
                                const maxNum = that.invoiceEditHistory.reduce((m, e) =>
                                    Math.max(m, parseInt(e.edit_number) || 0), 0);
                                that.editCount = maxNum;
                            }
                        }
                        that.historyLoading = false;
                        resolve();
                    };
                    const f = function (error) {
                        console.error("Error fetching invoice history:", error);
                        that.historyError = "Could not load invoice history. Please try again.";
                        that.historyLoading = false;
                        resolve();
                    };
                    Requestor.postAsyncP(path, data, this, s, f);
                });
            },

            openInvoiceHistoryModal() {
                this.$bvModal.show('modal-invoice-history');
                this.fetchInvoiceEditHistory(true);
            },

            historyIconFor(editType) {
                return ({
                    record_sale:   'plus-circle',
                    refund:        'arrow-return-left',
                    issue_sc_out:  'cash-stack',
                    update_sale:   'pencil-square',
                })[editType] || 'clock-history';
            },

            historyLabelFor(editType) {
                return ({
                    record_sale:   'Invoice created',
                    refund:        'Refund recorded',
                    issue_sc_out:  'Store credit paid out',
                    update_sale:   'Invoice updated',
                })[editType] || (editType || 'Edit');
            },

          syncWithEnvio() {
    let that = this;
    let path = "/api/sales/sync_invoice_to_dispatch/";
    let data = { invoice_id: this.invoice.invoice_id };

    // Guard: don't fire if we don't have an invoice id yet
    if (!data.invoice_id) {
        that.$bvToast.toast("No invoice loaded to sync.", {
            title: "Sync with Envio",
            autoHideDelay: 2000,
            variant: "warning"
        });
        return;
    }

    // Disable the button while the request is in flight
    that.update_disabled = true;

    let s = function(response) {
        that.update_disabled = false;

        // API wraps everything in { success, result, error }
        const payload = response.data || {};
        const result = payload.result || {};
        const outcome = result.outcome || "unknown";
        const reason = result.reason || null;
        const message = result.message || null;

        // If the API signaled a top-level error (e.g. non-2xx folded into 200),
        // treat it as a failure.
        if (payload.success === false || payload.error) {
            const errMsg = payload.error || message || "Sync failed.";
            that.$bvToast.toast(errMsg, {
                title: "Sync with Envio",
                autoHideDelay: 3000,
                variant: "danger"
            });
            return;
        }

        // Map outcomes to toast variants so admins can tell at a glance.
        const outcomeVariant = {
            applied: "success",
            partial: "warning",
            no_changes: "info",
            skipped: "warning",
            cancelled: "warning"
        }[outcome] || "info";

        // Build a human-readable line.
        const outcomeText = {
            applied: "Invoice synced with Dispatch.",
            partial: "Sync partially applied.",
            no_changes: "Nothing to sync — already up to date.",
            skipped: "Sync skipped.",
            cancelled: "Sync was cancelled."
        }[outcome] || `Sync finished (${outcome}).`;

        const detail = reason ? ` Reason: ${reason}.` : "";
        const runInfo = result.run_id ? ` (run #${result.run_id})` : "";

        that.$bvToast.toast(outcomeText + detail + runInfo, {
            title: "Sync with Envio",
            autoHideDelay: 3000,
            variant: outcomeVariant
        });

        // If the API returns a full invoice on success, refresh our state.
        if (result.invoice) {
            that.invoice = result.invoice;
            that.get_invoice_location_address();
            that.fetchTicketsForInvoice();
            that.$forceUpdate();
        }
    };

    let f = function(error) {
        that.update_disabled = false;

        // HTTP-level errors documented for this route.
        const status = error && error.response ? error.response.status : null;
        const body = error && error.response ? error.response.data : null;
        const reason = body && body.result ? body.result.reason : null;

        let msg;
        switch (status) {
            case 400: msg = "Invalid invoice id."; break;
            case 403: msg = "You must be an admin to sync with Envio."; break;
            case 404: msg = "Invoice not found in Inventory."; break;
            case 502: msg = reason ? `Dispatch rejected the request: ${reason}` : "Dispatch is unreachable."; break;
            default:  msg = "Error syncing with Envio. Please try again.";
        }

        that.$bvToast.toast(msg, {
            title: "Sync with Envio",
            autoHideDelay: 3000,
            variant: "danger"
        });
    };

    Requestor.postAsyncP(path, data, this, s, f);
},

            onItemOrderTypeChange(row, value) {
                if (row.isChild) {
                    this.onOrderTypeChange(row.item, value);
                } else {
                    this.update_item_level(row.item);
                }
            },
            getSpiffValue(item) {
                if (!item) return 0;
                if (item.spiff !== undefined && item.spiff !== null) {
                    return parseFloat(item.spiff) || 0;
                }
                if (item.properties && item.properties.spiff !== undefined) {
                    return parseFloat(item.properties.spiff) || 0;
                }
                return 0;
            },
            getCustomerFullName() {
                if (!this.invoice || !this.invoice.customer) return 'N/A';
                return this.invoice.customer.fullname || 'N/A';
            },
            getCustomerAddress() {
                if (!this.invoice || !this.invoice.customer) return '';
                return `${this.invoice.customer.address_line_1 || ''} ${this.invoice.customer.address_line_2 || ''}`.trim();
            },
            getCustomerCityStateZip() {
                if (!this.invoice || !this.invoice.customer) return '';
                const parts = [
                    this.invoice.customer.city || '',
                    this.invoice.customer.state || '',
                    this.invoice.customer.zipcode || ''
                ];
                return parts.filter(p => p).join(', ');
            },
            getCustomerPhone() {
                if (!this.invoice || !this.invoice.customer) return '';
                return this.invoice.customer.phone || '';
            },
            getCustomerPhone2() {
                if (!this.invoice || !this.invoice.customer) return '';
                return this.invoice.customer.phone_2 || '';
            },
            viewParentInvoice() {
                if (!this.invoice.parent_invoice_id) {
                    this.$bvToast.toast("No parent invoice ID found", {
                        title: "Error",
                        autoHideDelay: 1500,
                        variant: "danger"
                    });
                    return;
                }

                const basePath = '/sales/viewsale';
                const newPath = `${basePath}/${this.invoice.parent_invoice_id}`;

                const route = this.$router.resolve(newPath);
                window.open(route.href, '_blank');

                this.$bvToast.toast(`Opening parent invoice: ${this.invoice.parent_invoice_id} in new tab`, {
                    title: "Parent Invoice",
                    autoHideDelay: 2000,
                    variant: "info"
                });
            },
            viewChildInvoice(childInvoiceId) {
                const basePath = '/sales/viewsale';
                const newPath = `${basePath}/${childInvoiceId}`;

                const route = this.$router.resolve(newPath);
                window.open(route.href, '_blank');

                this.$bvToast.toast(`Opening child invoice: ${childInvoiceId} in new tab`, {
                    title: "Child Invoice",
                    autoHideDelay: 2000,
                    variant: "info"
                });
            },
            reloadInvoiceById(invoiceId) {
                let that = this;
                let path = "/api/sales/get_invoice/";
                let data = { invoice_id: invoiceId };

                let s = function(response) {
                    if (response.data.result) {
                        that.invoice = response.data.result;
                        that.get_invoice_location_address();
                        that.fetchTicketsForInvoice();
                        that.$forceUpdate();
                    }
                };

                let f = function(error) {
                    that.$bvToast.toast("Error loading child invoice", {
                        title: "Error",
                        autoHideDelay: 1500,
                        variant: "danger"
                    });
                };

                Requestor.postAsyncP(path, data, this, s, f);
            },
            handleDeliveryFromStore(item) {
                if (item.order_type === 'delivery_from_store') {
                    if (!item.drop_locsub || item.drop_locsub === 'null' || item.drop_locsub === null) {
                        const storeLocationId = this.invoice.location_id || 25;
                        const deliveryLocation = this.packet.locsubs ? this.packet.locsubs.find(loc =>
                            loc.location_id === storeLocationId &&
                            loc.text.toLowerCase().includes('customer deliveries')
                        ) : null;

                        if (deliveryLocation) {
                            item.drop_locsub = deliveryLocation.value;
                            console.log(`Set drop_locsub for ${item.product_sku}: ${item.drop_locsub}`);
                        } else {
                            if (this.packet.default_locsubs && this.packet.default_locsubs.length > 0) {
                                const defaultLoc = this.packet.default_locsubs.find(d =>
                                    d.order_type === 'delivery_from_store' || d.order_type === 'home_delivery'
                                );
                                if (defaultLoc) {
                                    item.drop_locsub = defaultLoc.locsub;
                                }
                            }
                        }
                    }

                    if (!item.locsub || item.locsub === 'null' || item.locsub === null) {
                        const storeLocations = this.packet.locsubs ? this.packet.locsubs.filter(loc =>
                            loc.location_id === this.invoice.location_id &&
                            loc.sublocation_type_id !== 1
                        ) : [];

                        if (storeLocations.length > 0) {
                            const stockLoc = storeLocations.find(loc =>
                                loc.text.toLowerCase().includes('stock')
                            );
                            item.locsub = stockLoc ? stockLoc.value : storeLocations[0].value;
                            console.log(`Set locsub for ${item.product_sku}: ${item.locsub}`);
                        }
                    }
                }
            },
            getSignatureUrl(url) {
                if (!url) return '';
                const imageUrl = url.trim();
                if (imageUrl && imageUrl.includes('blob.core.windows.net')) {
                    return `/api/image/image-proxy/?url=${encodeURIComponent(imageUrl)}`;
                }
                return imageUrl;
            },
            handleSignatureError(event) {
                console.warn('Signature image failed to load:', event.target.src);
                const parent = event.target.parentElement;
                if (parent) {
                    parent.innerHTML = '<span style="border-bottom: 2px solid #000; min-height: 40px; display: block; margin-top: 5px;">&nbsp;</span>';
                }
            },
            startLoadingAnimation() {
                this.isPageLoading = true;
                this.loadingProgress = 0;
                if (this.loadingInterval) clearInterval(this.loadingInterval);
                this.loadingInterval = setInterval(() => {
                    if (this.loadingProgress < 90) {
                        this.loadingProgress += Math.random() * 10;
                        if (this.loadingProgress > 90) this.loadingProgress = 90;
                    }
                }, 200);
            },
            stopLoadingAnimation() {
                if (this.loadingInterval) {
                    clearInterval(this.loadingInterval);
                    this.loadingInterval = null;
                }
                this.loadingProgress = 100;
                setTimeout(() => {
                    this.isPageLoading = false;
                }, 300);
            },
            getTotalRefundAmount() {
                const totalRefunded = this.total_refunded_value || 0;
                const orderTotal = parseFloat(this.invoice.order_total) || 0;
                const chargesTotal = this.getChargesTotal();
                const totalInvoiceAmount = orderTotal + chargesTotal;

                if (totalInvoiceAmount === 0) {
                    return 0;
                }

                return Math.min(totalRefunded, totalInvoiceAmount);
            },
            getRefundTaxAmount() {
                const totalRefunded = this.getTotalRefundAmount() || 0;
                const taxRate = parseFloat(this.invoice.tax_perc) || 0;
                return Math.round((totalRefunded * taxRate) * 100) / 100;
            },
            getOrderTotal() {
                return Math.max(0, Math.round((parseFloat(this.invoice.order_total) || 0) * 100) / 100);
            },
            getTotalDiscount() {
                return Math.max(0, Math.round((parseFloat(this.invoice.total_discount) || 0) * 100) / 100);
            },
            getChargesTotal() {
                let delivery = parseFloat(this.invoice.delivery_charges) || 0.0;
                let whiteglove = parseFloat(this.invoice.whiteglove_charges) || 0.0;
                let setup = parseFloat(this.invoice.setup_charges) || 0.0;
                let warranty = parseFloat(this.invoice.warranty_charges) || 0.0;
                return Math.round((delivery + whiteglove + setup + warranty) * 100) / 100;
            },
            getTaxableSubtotal() {
                return Math.max(0, Math.round((parseFloat(this.invoice.taxable_subtotal) || 0) * 100) / 100);
            },
            getSalesTaxAmount() {
                return Math.max(0, Math.round((parseFloat(this.invoice.sales_tax) || 0) * 100) / 100);
            },
            getGrandTotal() {
                return Math.max(0, Math.round((parseFloat(this.invoice.total) || 0) * 100) / 100);
            },
            getSubTotal() {
                return Math.max(0, Math.round((parseFloat(this.invoice.subtotal) || 0) * 100) / 100);
            },
            get_total_balance() {
                const total = parseFloat(this.invoice.total) || 0;
                const totalPaid = this.get_pms_total();

                let balance = total - totalPaid;
                return Math.max(0, Math.round(balance * 100) / 100);
            },
            get_pms_total() {
                let total = 0.0;
                if (this.invoice.payment_methods && this.invoice.payment_methods.length > 0) {
                    for (let i = 0; i < this.invoice.payment_methods.length; i++) {
                        total += parseFloat(this.invoice.payment_methods[i].amount) || 0.0;
                    }
                }
                return Math.round(total * 100) / 100;
            },
            onOrderTypeChange(item, newValue, oldValue) {
                const previousOrderType = oldValue;
                item.order_type = newValue;

                if (newValue === 'delivery_from_store') {
                    this.handleDeliveryFromStore(item);
                }

                this.update_item_level_with_history(item, previousOrderType);
            },
            update_item_level_with_history(item, previousOrderType) {
                let that = this;

                let data = {
                    invoice_id: item.invoice_id,
                    itemid: item.itemid,
                    product_sku: item.product_sku,
                    delivery_date: item.delivery_date,
                    order_type: item.order_type,
                    previous_order_type: previousOrderType,
                    pull_date: moment(item.delivery_date).add(-1, 'day').format("MM-DD-YYYY"),
                    username: this.packet.userDetails ? this.packet.userDetails.username : ''
                };

                let path = "/api/sales/update_item_level/";
                let s = function(response) {
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                    } else {
                        that.$bvToast.toast("UPDATED ITEM DATA.", {title: "Success", autoHideDelay: 1500, variant: "success"});

                        if (response.data.order_type_history) {
                            item.order_type_history = response.data.order_type_history;
                        }
                    }
                };
                let f = function(error) {
                    that.$bvToast.toast("Error updating delivery date. Please try again.", {title: "Error", autoHideDelay: 1500, variant: "danger"});
                };
                Requestor.postAsyncP(path, data, this, s, f);
            },
            getOrderTypeHistory(item) {
                if (!item.order_type_history || item.order_type_history.length === 0) {
                    return [];
                }
                return item.order_type_history;
            },
            formatOrderTypeHistory(item) {
                const history = this.getOrderTypeHistory(item);
                if (history.length === 0) return '';

                return history.map(change => {
                    const oldType = change.previous_order_type || 'Not set';
                    const newType = change.order_type;
                    const changedBy = change.changed_by;
                    const changedAt = moment(change.changed_at).format('MM/DD/YYYY hh:mm A');

                    return `${oldType} → ${newType} (by ${changedBy} on ${changedAt})`;
                }).join('; ');
            },
            getOrderTypeHistoryTooltip(item) {
                const history = this.getOrderTypeHistory(item);
                if (history.length === 0) return 'No order type changes';

                return history.map(change => {
                    const oldType = change.previous_order_type || 'Not set';
                    const newType = change.order_type;
                    const changedBy = change.changed_by;
                    const changedAt = moment(change.changed_at).format('MM/DD/YYYY hh:mm A');

                    return `Changed from "${oldType}" to "${newType}" by ${changedBy} on ${changedAt}`;
                }).join('\n');
            },
            formatDate(timestamp) {
                if (!timestamp) return '';
                try {
                    return moment(timestamp).format('MM/DD/YYYY hh:mm A');
                } catch(e) {
                    return timestamp;
                }
            },
            getDayOfWeek(dateString) {
                if (!dateString) return '';
                const [year, month, day] = dateString.split('-').map(Number);
                const date = new Date(year, month - 1, day);
                return date.toLocaleDateString('en-US', { weekday: 'long' });
            },
            handleViewTicket(ticket) {
                this.cs_item_data = {
                    id: ticket.id,
                    invoice_id: ticket.invoice_id,
                    product_sku: ticket.product_sku,
                    itemid: ticket.itemid
                };
                this.$bvModal.show("modal-cs-ticket");
            },
            handleCreateTicket(item) {
                this.cs_item_data = {
                    id: null,
                    invoice_id: item.invoice_id,
                    product_sku: item.product_sku,
                    itemid: item.itemid,
                    is_new: true
                };
                this.$bvModal.show("modal-cs-ticket");
            },
            hasTicket(item) {
                if (item.cs_ticket_id) {
                    return true;
                }
                if (item.cs_ticket_count && item.cs_ticket_count > 0) {
                    return true;
                }
                if (item.tickets && item.tickets.length > 0) {
                    return true;
                }
                if (item.ticket_id) {
                    return true;
                }
                return false;
            },
            fetchTicketsForInvoice() {
                let that = this;
                let path = "/api/customer_service/get_tickets_by_invoice/";
                let data = { invoice_id: this.invoice.invoice_id };

                let s = function(response) {
                    if (response.data && response.data.result) {
                        const ticketsByKey = response.data.result;

                        if (!that.invoice.items || that.invoice.items.length === 0) {
                            return;
                        }

                        that.invoice.items.forEach((item, index) => {
                            const exactKey = `${item.product_sku}_${item.itemid}`;
                            let tickets = ticketsByKey[exactKey] || [];

                            if (tickets.length === 0) {
                                const fallbackKey = `${item.product_sku}_-1`;
                                tickets = ticketsByKey[fallbackKey] || [];
                            }

                            if (tickets.length > 0) {
                                item.tickets = tickets;
                                item.cs_ticket_count = tickets.length;

                                const sortedTickets = [...tickets].sort((a, b) => {
                                    return new Date(b.created_on) - new Date(a.created_on);
                                });

                                item.cs_ticket_id = sortedTickets[0].id;
                                item.cs_ticket_status = sortedTickets[0].status;
                                item.cs_ticket_action = sortedTickets[0].action;
                            } else {
                                item.tickets = [];
                                item.cs_ticket_count = 0;
                                item.cs_ticket_id = null;
                            }
                        });

                        that.$forceUpdate();
                    }
                };

                let f = function(error) {
                    console.error('Error fetching tickets:', error);
                };

                Requestor.getAsyncP(path, data, this, s, f);
            },
            view_cs_issue(item) {
                if (item.tickets && item.tickets.length > 0) {
                    if (item.tickets.length > 1) {
                        this.currentTicketItem = item;
                        this.currentTickets = item.tickets;
                        this.$bvModal.show("modal-ticket-list");
                        return;
                    }

                    const ticket = item.tickets[0];
                    this.cs_item_data = {
                        id: ticket.id,
                        invoice_id: item.invoice_id,
                        product_sku: item.product_sku,
                        itemid: item.itemid
                    };
                    this.$bvModal.show("modal-cs-ticket");
                    return;
                }

                if (item.cs_ticket_id) {
                    this.cs_item_data = {
                        id: item.cs_ticket_id,
                        invoice_id: item.invoice_id,
                        product_sku: item.product_sku,
                        itemid: item.itemid
                    };
                    this.$bvModal.show("modal-cs-ticket");
                    return;
                }

                this.$bvModal.msgBoxConfirm(
                    "No claim exists for this item. Would you like to create one?",
                    {
                        title: "Create Claim",
                        okVariant: "primary",
                        okTitle: "Yes, Create Claim",
                        cancelTitle: "Cancel",
                        centered: true
                    }
                ).then(value => {
                    if (value) {
                        this.createNewTicketForItem(item);
                    }
                });
            },
            createNewTicketForItem(item) {
                this.cs_item_data = {
                    id: null,
                    invoice_id: item.invoice_id,
                    product_sku: item.product_sku,
                    itemid: item.itemid,
                    is_new: true
                };
                this.$bvModal.show("modal-cs-ticket");
            },
            roundTo(n, digits) {
                var negative = false;
                if (digits === undefined) {
                    digits = 0;
                }
                if (n < 0) {
                    negative = true;
                    n = n * -1;
                }
                var multiplicator = Math.pow(10, digits);
                n = parseFloat((n * multiplicator).toFixed(11));
                n = (Math.round(n) / multiplicator).toFixed(digits);
                if (negative) {
                    n = (n * -1).toFixed(digits);
                }
                return parseFloat(n);
            },
            parentImageForGroup(parentSku) {
                const group = this.groupedByParent[parentSku];
                if (group && group.length > 0 && group[0].parent_image_url) {
                    const imageUrl = group[0].parent_image_url.trim();
                    if (imageUrl && imageUrl.includes('fmapi.myfurnituremecca.com')) {
                        return `/api/image/image-proxy/?url=${encodeURIComponent(imageUrl)}`;
                    }
                    return imageUrl;
                }
                return null;
            },
            calculateParentUnitPrice(items) {
                if (!items || items.length === 0) return '0.00';
                const totalUnitPrice = items.reduce((sum, item) => {
                    const unitPrice = parseFloat(item.selling_price) || 0;
                    const qty = parseFloat(item.quantity) || 0;
                    return sum + (unitPrice * qty);
                }, 0);
                return totalUnitPrice.toFixed(2);
            },
            calculateParentTotalDiscount(items) {
                if (!items || items.length === 0) return '0.00';
                const totalDiscount = items.reduce((sum, item) => {
                    const unitDiscount = parseFloat(item.unit_discount_value) || 0;
                    const quantity = parseFloat(item.quantity) || 0;
                    const itemDiscount = parseFloat(item.discount_offered) || 0;

                    if (itemDiscount > 0) {
                        return sum + itemDiscount;
                    } else {
                        return sum + (unitDiscount * quantity);
                    }
                }, 0);
                return totalDiscount.toFixed(2);
            },
            calculateParentTotalAfterDiscount(items) {
                if (!items || items.length === 0) return '0.00';
                const total = items.reduce((sum, item) => {
                    const price = parseFloat(item.selling_price) || 0;
                    const qty = parseFloat(item.quantity) || 0;
                    const discount = parseFloat(item.discount_offered) || 0;
                    return sum + (price * qty) - discount;
                }, 0);
                return total.toFixed(2);
            },
            calculateParentCubes(items) {
                if (!items || items.length === 0) return '0.00';
                const total = items.reduce((sum, item) => {
                    return sum + ((item.cubes || 0) * (item.quantity || 0));
                }, 0);
                return parseFloat(total).toFixed(2);
            },
            calculateParentTotal(items) {
                if (!items || items.length === 0) return '0.00';
                const total = items.reduce((sum, item) => {
                    return sum + ((item.selling_price || 0) * (item.quantity || 0) - (item.discount_offered || 0));
                }, 0);
                return parseFloat(total).toFixed(2);
            },
            formatOrderType(type) {
                const types = {
                    'customer_takes': 'Take Home',
                    'store_pickup': 'Store Pickup',
                    'home_delivery': 'Home Delivery',
                    'delivery_from_store': 'Delivery from Store'
                };
                return types[type] || type || '';
            },
            getStateDisplayName(stateCode) {
                if (!stateCode && stateCode !== 0) return '';
                const code = String(stateCode);
                const state = this.states.find(s => String(s.value) === code);
                return state ? state.text : code;
            },
            getLocationDisplay(locsubKey) {
                if (!locsubKey) return null;
                const cleanKey = locsubKey.toString().trim();

                const displayName = this.packet.locsubsdict ? this.packet.locsubsdict[cleanKey] : null;
                if (displayName) {
                    let nameOnly = displayName
                        .replace(/\s*[\(\|\-–,]\s*[^\)\|\-–,]*\)?\s*$/, '')
                        .trim();
                    if (nameOnly) return nameOnly;
                    return displayName;
                }

                if (cleanKey.includes('|')) {
                    const [locationId] = cleanKey.split('|');
                    const location = this.packet.locations
                        ? this.packet.locations.find(loc => String(loc.value) === String(locationId))
                        : null;
                    if (location) {
                        return location.name || location.short_name || location.description || `Location ${locationId}`;
                    }
                    return `Location ${locationId}`;
                }

                return cleanKey;
            },
            printReceipt() {
                const printFrame = document.createElement('iframe');
                printFrame.style.position = 'absolute';
                printFrame.style.width = '0';
                printFrame.style.height = '0';
                printFrame.style.border = 'none';
                document.body.appendChild(printFrame);

                const receiptContent = this.generateReceiptHTML();
                const frameDoc = printFrame.contentWindow.document;
                frameDoc.open();
                frameDoc.write(receiptContent);
                frameDoc.close();

                printFrame.onload = () => {
                    setTimeout(() => {
                        printFrame.contentWindow.focus();
                        printFrame.contentWindow.print();
                        setTimeout(() => {
                            document.body.removeChild(printFrame);
                        }, 1000);
                    }, 500);
                };
            },
            generateReceiptHTML() {
                let locationName = this.invoice_location_details.name ||
                                  this.invoice_location_details.short_name ||
                                  'FURNITURE MECCA';
                if (this.invoice_location_details.address) {
                    locationName = this.invoice_location_details.address;
                }

                const currentDate = new Date().toLocaleString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                });

                const rawInvoiceNumber = this.invoice.invoice_id || 'N/A';
                const invoiceNumber = this.editCount > 0
                    ? `${rawInvoiceNumber}-${this.editCount}`
                    : rawInvoiceNumber;
                const salesperson = (this.invoice.username || '').toUpperCase();
                const customerName = this.getCustomerFullName();
                const items = this.invoice.items || [];
                const orderTotal = this.getOrderTotal() || 0;
                const delivery = parseFloat(this.invoice.delivery_charges) || 0;
                const whiteglove = parseFloat(this.invoice.whiteglove_charges) || 0;
                const setup = parseFloat(this.invoice.setup_charges) || 0;
                const warranty = parseFloat(this.invoice.warranty_charges) || 0;
                const subtotal = this.getSubTotal ? this.getSubTotal() : 0;
                const salesTax = parseFloat(this.invoice.sales_tax) || 0;
                const grandTotal = parseFloat(this.invoice.total) || 0;
                const totalPayments = this.get_pms_total ? this.get_pms_total() : 0;
                const balance = grandTotal - totalPayments;
                const predictedTax = this.predictedSalesTax || 0;
                const totalToPay = balance + predictedTax;

                const warrantyBlock = warranty > 0
                    ? `<div class="total-row"><span>WARRANTY:</span><span>$${warranty.toFixed(2)}</span></div>`
                    : `<div class="total-row"><span>WARRANTY:</span><span class="warranty-refused-inline">Customer refused to purchase extended warranties</span></div>`;

                let parentPaymentHTML = '';
                if (this.shouldShowParentPayment) {
                    parentPaymentHTML = `
                        <div class="divider"></div>
                        <div style="font-weight: bold; color: #0056b3;">PARENT INVOICE PAYMENT (${this.invoice.parent_invoice_id})</div>
                        ${this.getParentPaymentMethodsForItems.map(pm => `
                            <div class="total-row" style="color: #0056b3;">
                                <span>${(pm.value || '').toUpperCase()} - ${pm.name}:</span>
                                <span>$${pm.display_amount}</span>
                            </div>
                        `).join('')}
                        <div class="total-row" style="font-weight: bold; color: #0056b3; border-top: 1px solid #0056b3;">
                            <span>Total Parent Payment Allocated:</span>
                            <span>$${this.allocatedParentPaymentTotal.toFixed(2)}</span>
                        </div>
                    `;
                }

                let financeAmountHTML = '';
                if (this.allFinanceAgreements.length > 0) {
                    financeAmountHTML = this.allFinanceAgreements.map(fin => `
                        <div class="total-row">
                            <span>FINANCE AMOUNT (${(fin.company_name || 'FINANCE').toUpperCase()}${fin.lease_id ? ' · ' + fin.lease_id : ''}):</span>
                            <span>$${fin.amount.toFixed(2)}</span>
                        </div>
                        <div class="total-row" style="font-size: 10px; color: #555;">
                            <span>USED:</span>
                            <span>$${fin.used_amount.toFixed(2)}</span>
                        </div>
                        <div class="total-row" style="font-size: 10px; color: #555;">
                            <span>BALANCE:</span>
                            <span>$${fin.balance_amount.toFixed(2)}</span>
                        </div>
                    `).join('');
                }

                return `
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <meta charset="UTF-8">
                        <title>Receipt - ${invoiceNumber}</title>
                        <style>
                            @page { size: 80mm auto; margin: 0; }
                            body {
                                font-family: 'Courier New', monospace;
                                font-size: 12px;
                                width: 72mm;
                                margin: 0 auto;
                                padding: 2mm;
                                line-height: 1.3;
                            }
                            .header { text-align: center; font-weight: bold; margin-bottom: 3mm; }
                            .store-name { font-size: 16px; font-weight: bold; letter-spacing: 1px; }
                            .store-location { font-size: 12px; margin-top: 1mm; }
                            .divider { border-top: 1px dashed #000; margin: 2mm 0; }
                            .info-row { display: flex; justify-content: space-between; margin: 1mm 0; }
                            .info-label { font-weight: bold; }
                            .items-header { font-weight: bold; border-bottom: 1px solid #000; margin-top: 3mm; padding-bottom: 1mm; }
                            .item-row { display: flex; justify-content: space-between; margin: 1mm 0; }
                            .totals { margin-top: 3mm; }
                            .total-row { display: flex; justify-content: space-between; margin: 1mm 0; }
                            .grand-total { font-weight: bold; font-size: 14px; border-top: 1px solid #000; border-bottom: 1px solid #000; padding: 3mm 0; margin: 3mm 0; }
                            .balance { font-weight: bold; font-size: 14px; background: #f0f0f0; padding: 1mm; }
                            .predicted-tax { background: #fff5e6; padding: 1mm; margin: 2mm 0; border-left: 3px solid #ed8936; }
                            .footer { text-align: center; margin-top: 5mm; font-size: 10px; }
                            .thank-you { font-size: 14px; font-weight: bold; margin: 4mm 0; }
                            .cut-line { text-align: center; margin-top: 5mm; font-size: 10px; }
                            .warranty-refused-inline { font-style: italic; color: #b71c1c; font-size: 9px; text-align: right; max-width: 60%; }
                        </style>
                    </head>
                    <body>
                        <div class="header">
                            <div class="store-name">FURNITURE MECCA</div>
                            <div class="store-location">${locationName}</div>
                            <div class="store-location">${this.invoice_location_details.phone || ''}</div>
                            <div class="store-location">${this.invoice_location_details.email || ''}</div>
                            <div class="store-location">${currentDate}</div>
                        </div>
                        <div class="divider"></div>
                        <div class="info-row"><span class="info-label">INVOICE #:</span><span>${invoiceNumber}</span></div>
                        <div class="info-row"><span class="info-label">SALESPERSON:</span><span>${salesperson}</span></div>
                        <div class="info-row"><span class="info-label">LOCATION:</span><span>${this.packet.locationsdict ? this.packet.locationsdict[this.invoice.location_id] : ''}</span></div>
                        <div class="info-row"><span class="info-label">CUSTOMER:</span><span>${customerName}</span></div>
                        ${this.getCustomerPhone() ? `<div class="info-row"><span class="info-label">PHONE:</span><span>${this.getCustomerPhone()}</span></div>` : ''}
                        ${this.isExchangeInvoice ? `<div class="info-row" style="color: #0056b3; font-weight: bold;"><span>EXCHANGE INVOICE</span><span>Parent: ${this.invoice.parent_invoice_id}</span></div>` : ''}
                        <div class="items-header">ITEM DETAILS</div>
                        ${items.map(item => {
                            const price = parseFloat(item.selling_price) || 0;
                            const qty = (item.quantity - (item.refunded_so_far || 0)) || 1;
                            const discount = parseFloat(item.discount_offered) || 0;
                            const itemTotal = (price * qty) - discount;
                            const desc = item.product_name || item.product_sku || 'Item';
                            return `
                                <div class="item-row"><span>${desc.substring(0, 30)}${desc.length > 30 ? '...' : ''}</span><span>$${itemTotal.toFixed(2)}</span></div>
                                <div style="font-size: 10px; margin-left: 2mm; margin-bottom: 1mm;">${item.product_sku} | $${price.toFixed(2)} x ${qty}${discount > 0 ? ` (-$${discount.toFixed(2)})` : ''}</div>
                            `;
                        }).join('')}
                        <div class="divider"></div>
                        <div class="totals">
                            <div class="total-row"><span>ORDER TOTAL:</span><span>$${orderTotal.toFixed(2)}</span></div>
                            <div class="total-row"><span>DELIVERY:</span><span>$${delivery.toFixed(2)}</span></div>
                            <div class="total-row"><span>WHITE GLOVE:</span><span>$${whiteglove.toFixed(2)}</span></div>
                            <div class="total-row"><span>SETUP:</span><span>$${setup.toFixed(2)}</span></div>
                            ${warrantyBlock}
                            <div class="total-row"><span>SUBTOTAL:</span><span>$${subtotal.toFixed(2)}</span></div>
                            <div class="total-row"><span>SALES TAX:</span><span>$${salesTax.toFixed(2)}</span></div>
                            <div class="grand-total total-row"><span>GRAND TOTAL:</span><span>$${grandTotal.toFixed(2)}</span></div>
                        </div>
                        ${this.orderedPaymentMethods.length > 0 ? `
                            <div class="divider"></div>
                            <div style="font-weight: bold; margin-bottom: 1mm;">PAYMENTS</div>
                            ${this.orderedPaymentMethods.map(pm => `
                                <div class="total-row">
                                    <span>
                                        ${pm.source ? '[' + (pm.source || '').toUpperCase() + '] ' : ''}${(pm.value || '').toUpperCase()}${pm.name && pm.name !== 'N/A' ? ' - ' + pm.name : ''}:
                                    </span>
                                    <span>$${(parseFloat(pm.amount) || 0).toFixed(2)}</span>
                                </div>
                            `).join('')}
                            <div class="total-row" style="font-weight: bold;"><span>TOTAL PAID:</span><span>$${totalPayments.toFixed(2)}</span></div>
                        ` : ''}
                        ${financeAmountHTML}
                        ${parentPaymentHTML}
                        <div class="divider"></div>
                        <div class="balance total-row"><span>CURRENT BALANCE:</span><span>$${balance.toFixed(2)}</span></div>
                        ${predictedTax > 0 ? `
                            <div class="predicted-tax total-row">
                                <span>PREDICTED SALES TAX (${(parseFloat(this.invoice.tax_perc) * 100).toFixed(2)}%):</span>
                                <span>$${predictedTax.toFixed(2)}</span>
                            </div>
                            <div class="total-row" style="font-weight: bold; font-size: 14px; margin-top: 2mm;">
                                <span>TOTAL TO PAY:</span>
                                <span>$${totalToPay.toFixed(2)}</span>
                            </div>
                        ` : ''}
                        ${this.invoice.is_layaway ? `<div style="text-align: center; font-weight: bold; color: red; margin: 3mm 0;">LAYAWAY SALE</div>` : ''}
                        <div class="footer"><div class="thank-you">THANK YOU!</div><div>${this.invoice_location_details.website || 'www.myfurnituremecca.com'}</div></div>
                        <div class="cut-line">- - - - - - - - - - - - - - - - - - - -</div>
                    </body>
                    </html>
                `;
            },
            showParentFullImage(parentItem) {
                if (!parentItem) return;
                const imageUrl = this.parentImageForGroup(parentItem.parent_product_sku);
                if (imageUrl) {
                    this.$bvModal.show('modal-image-viewer');
                    this.$nextTick(() => {
                        this.currentFullSizeImage = imageUrl;
                        this.currentProductName = parentItem.parent_product_name || parentItem.parent_product_sku || 'Parent Product Image';
                    });
                } else {
                    this.$bvToast.toast("No parent product image available", {title: "Info", autoHideDelay: 2000, variant:"info"});
                }
            },
            getPaymentByMethod(methodValue) {
                if (!this.invoice.payment_methods || this.invoice.payment_methods.length === 0) {
                    return '0.00';
                }
                const total = this.invoice.payment_methods
                    .filter(pm => pm.value === methodValue)
                    .reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
                return total.toFixed(2);
            },
            getLayawayPayments() {
                if (!this.invoice.payment_methods || this.invoice.payment_methods.length === 0) {
                    return [];
                }
                if (this.invoice.is_layaway) {
                    return this.invoice.payment_methods.map(pm => ({
                        method: pm.value,
                        amount: pm.amount,
                        ts: pm.ts
                    }));
                }
                return [];
            },
            showDamageFullImage(item) {
                if (!item.is_damaged) return;
                const imageUrl = this.getDamageHistoryImage(item);
                if (imageUrl) {
                    this.$bvModal.show('modal-image-viewer');
                    this.$nextTick(() => {
                        this.currentFullSizeImage = imageUrl;
                        this.currentProductName = `${item.product_name || item.product_sku} - Damage Condition`;
                    });
                } else {
                    this.showFullImage(item);
                }
            },
            getDamageHistoryImage(item) {
                if (!item.is_damaged || !item.damage_history || item.damage_history.length === 0) {
                    return null;
                }
                const damageRecord = item.damage_history.find(record => record.images && record.images.length > 0);
                if (!damageRecord || !damageRecord.images || damageRecord.images.length === 0) {
                    return null;
                }
                const damageImage = damageRecord.images[0];
                if (!damageImage || !damageImage.image_url) {
                    return null;
                }
                const imageUrl = damageImage.image_url.trim();
                if (imageUrl && imageUrl.includes('blob.core.windows.net')) {
                    const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(imageUrl)}`;
                    return proxyUrl;
                }
                return imageUrl;
            },
            handleDamageImageError(event, item) {
                console.warn(`Damage image failed to load for ${item.product_sku}:`, event.target.src);
                const normalImage = this.getProductImage(item);
                if (normalImage) {
                    event.target.src = normalImage;
                    event.target.alt = item.product_name || item.product_sku;
                } else {
                    const parent = event.target.parentElement;
                    if (parent) {
                        const existingFallback = parent.querySelector('.no-image-placeholder');
                        if (!existingFallback) {
                            parent.innerHTML = `
                                <div class="no-image-placeholder" style="width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center;">
                                    <div style="font-size: 20px; margin-bottom: 5px;">📷</div>
                                    <div style="color: #999; font-size: 10px;">No Damage Image</div>
                                </div>
                            `;
                        }
                    }
                    event.target.style.display = 'none';
                }
            },
            handleDamageImageLoad(event, item) {
                console.log(`Damage image loaded successfully for ${item.product_sku}`);
            },
            showAsIsImage(item) {
                if (!item || !item.as_is_image_url) {
                    this.$bvToast.toast("No As-Is image available for this product", {title: "Info", autoHideDelay: 2000, variant:"info"});
                    return;
                }
                const imageUrl = item.as_is_image_url.trim();
                let finalImageUrl = imageUrl;
                if (imageUrl && imageUrl.includes('fmapi.myfurnituremecca.com')) {
                    finalImageUrl = `/api/image/image-proxy/?url=${encodeURIComponent(imageUrl)}`;
                }
                this.$bvModal.show('modal-image-viewer');
                this.$nextTick(() => {
                    this.currentFullSizeImage = finalImageUrl;
                    this.currentProductName = `${item.product_name || item.product_sku} - As-Is Condition`;
                });
            },
            getNormalProductImage(item) {
                if (!item || !item.image_url) {
                    return null;
                }
                const imageUrl = item.image_url.trim();
                if (imageUrl && imageUrl.includes('fmapi.myfurnituremecca.com')) {
                    const proxyUrl = `/api/image/image-proxy/?url=${encodeURIComponent(imageUrl)}`;
                    return proxyUrl;
                }
                return imageUrl;
            },
            getProductImage(item) {
                if (item.is_damaged) {
                    const damageImage = this.getDamageHistoryImage(item);
                    if (damageImage) {
                        return damageImage;
                    }
                }
                return this.getNormalProductImage(item);
            },
            handleImageError(event, item) {
                console.warn(`Image failed to load for ${item.product_sku}:`, event.target.src);
                const parent = event.target.parentElement;
                if (parent) {
                    const existingFallback = parent.querySelector('.no-image-placeholder');
                    if (!existingFallback) {
                        parent.innerHTML = `
                            <div class="no-image-placeholder" style="width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center;">
                                <div style="font-size: 20px; margin-bottom: 5px;">📷</div>
                                <div style="color: #999; font-size: 10px;">No Image</div>
                                <div style="color: #ccc; font-size: 8px; margin-top: 2px;">${item.product_sku}</div>
                            </div>
                        `;
                    }
                }
                event.target.style.display = 'none';
            },
            handleImageLoad(event, item) {
                const sku = (item && item.product_sku) ? item.product_sku : 'unknown';
                console.log(`Image loaded successfully for ${sku}`);
            },
            showFullImage(item) {
                if (!item) return;
                const imageUrl = this.getProductImage(item);
                if (imageUrl) {
                    this.$bvModal.show('modal-image-viewer');
                    this.$nextTick(() => {
                        this.currentFullSizeImage = imageUrl;
                        this.currentProductName = item.product_name || item.product_sku || 'Product Image';
                    });
                } else {
                    this.$bvToast.toast("No image available for this product", {title: "Info", autoHideDelay: 2000, variant:"info"});
                }
            },
            hasBackorderLocsub(item) {
                if (!item || item.locsub === undefined || item.locsub === null) {
                    return false;
                }
                const itemLocsub = String(item.locsub).trim().replace(/\s+/g, '');
                return itemLocsub === '46|122';
            },
            isParentReturned(parentGroup) {
                if (!parentGroup || parentGroup.length === 0) return false;
                return parentGroup.every(item => this.is_returned(item));
            },
            is_returned(item) {
                return ((item.quantity - item.refunded_so_far) == 0);
            },
            check_if_edit_allowed() {
                if (!this.packet.userDetails) return false;
                let current_username = this.packet.userDetails.username;
                let current_userrole = this.packet.userDetails.userrole;
                let current_location_id = parseInt((this.packet.userDetails.properties || {}).location_id);
                let invoice_location_id = parseInt(this.invoice.location_id);
                let status = false;
                if (current_userrole === 'admin') {
                    status = true;
                } else {
                    if (current_userrole === 'locationadmin' && current_location_id === invoice_location_id) {
                        status = true;
                    }
                }
                return status;
            },
            close_layaway() {
                this.$bvModal.hide("modal-layaway-payments");
                this.reload_invoice();
            },
            open_layaway_payment_modal() {
                this.$bvModal.show("modal-layaway-payments");
            },
            payment_ts_format(ts) {
                let p = ts.split("T");
                let p1 = p[0];
                let p2 = p[1].split(".")[0];
                return p1 + " " + p2;
            },
            remove_refund_item(rtitdx) {
                this.refunds.items_to_be_refunded.splice(rtitdx, 1);
            },
            remove_rpm(rpidx) {
                this.refunds.refund_methods.splice(rpidx, 1);
            },
            get_time() {
                return new moment().format("MM-DD-YYYY (HH:MM:SS)");
            },
            get_payment_methods_sum() {
                let tot = 0.0;
                for (var i = 0; i < this.refunds.refund_methods.length; i++) {
                    tot += (parseFloat(this.refunds.refund_methods[i].amount) || 0.0);
                }
                return tot;
            },
            get_refunds_packet() {
                return {
                    total_delivery_refund: 0.0,
                    total_setup_refund: 0.0,
                    total_warranty_refund: 0.0,
                    total_sales_tax_refund: 0.0,
                    refund_method_options: [],
                    refund_methods: [],
                    items_to_be_refunded: [],
                };
            },
            submit_refund() {
                let that = this;
                let total_being_refunded = this.get_total_to_be_refunded();
                let total_paid_on_taxable_rpms = this.sum(this.refunds.refund_methods.filter(function(rpm) {
                    return that.check_rpm_charges_tax(rpm) == false;
                }).map(function(rpm) {
                    return (parseFloat(rpm.amount) || 0.00);
                }));
                let total_paid_on_non_taxable_rpms = this.sum(this.refunds.refund_methods.filter(function(rpm) {
                    return that.check_rpm_charges_tax(rpm) == true;
                }).map(function(rpm) {
                    return (parseFloat(rpm.amount) || 0.00);
                }));
                let total_sales_tax_being_refunded = total_paid_on_taxable_rpms * parseFloat(this.invoice.tax_perc);
                this.distribute_sales_tax(total_sales_tax_being_refunded);

                let data = {
                    invoice_id: this.invoice.invoice_id,
                    created_by: this.packet.userDetails ? this.packet.userDetails.username : '',
                    approved: true,
                    reviewed_by: this.packet.userDetails ? this.packet.userDetails.username : '',
                    total_refunded_item_amount: total_being_refunded,
                    total_refunded_sales_tax: total_sales_tax_being_refunded,
                    total_refunded_delivery: (parseFloat(this.refunds.total_delivery_refund) || 0.00),
                    total_refunded_setup: (parseFloat(this.refunds.total_setup_refund) || 0.00),
                    total_refunded_warranty: (parseFloat(this.refunds.total_warranty_refund) || 0.00),
                    refund_methods: this.refunds.refund_methods,
                    refund_items: this.refunds.items_to_be_refunded.map(function(d) {
                        return {
                            refund_id: null,
                            invoice_id: null,
                            product_id: d.product_id,
                            is_special: d.is_special,
                            product_sku: d.product_sku,
                            itemid: d.itemid,
                            return_quantity: parseInt(d.return_quantity),
                            delivered: d.delivered,
                            return_reason: d.return_reason,
                            return_locsub: d.return_locsub,
                            selling_price: d.selling_price,
                            refunded_at_state: d.state,
                        }
                    })
                };
                let path = "/api/sales/record_refund/";
                let s = function(response) {
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                    } else {
                        that.$bvToast.toast("Recorded refund.", {title: "Success", autoHideDelay: 1500, variant: "success"});
                        that.reload_invoice();
                    }
                    that.items_being_loaded = false;
                };
                let f = function(error) {
                    that.$bvToast.toast("Error registering refund. Please try again.", {title: "Error", autoHideDelay: 1500, variant: "danger"});
                    that.items_being_loaded = false;
                };
                this.items_being_loaded = true;
                Requestor.postAsyncP(path, data, this, s, f);
            },
            check_rpm_charges_tax(rpmethod) {
                let status = false;
                if (!rpmethod.value) {
                    return status;
                }
                let value = rpmethod.value.split("|")[0];
                let name = rpmethod.value.split("|")[1];
                if (value == "store_credit") {
                    status = true;
                } else {
                    let pmet = this.invoice.payment_methods.filter(function(d) {
                        return d.value == value && d.name == name;
                    });
                    if (pmet.length > 0) {
                        pmet = pmet[0];
                        if (pmet.charged_tax_itself == true) {
                            status = true;
                        }
                    }
                }
                return status;
            },
            get_max_allowed_on_refund_payment_method(rpmethod) {
                let v = 0.0;
                let b = 0.0;
                if ((rpmethod.value || "").split("|").length == 2) {
                    let pm = rpmethod.value.split("|")[0];
                    let mn = rpmethod.value.split("|")[1];
                    let ipm = this.invoice.payment_methods.filter(function(d) {
                        return d.value == pm && d.name == mn;
                    });
                    if (ipm.length > 0) {
                        let sipm = ipm[0];
                        if (sipm) {
                            v = sipm.amount;
                        }
                    }
                    if (this.invoice.refund_methods && this.invoice.refund_methods.length > 0) {
                        let refund_methods = this.invoice.refund_methods;
                        for (var i = 0; i < refund_methods.length; i++) {
                            var ri = refund_methods[i].filter(function(d) {
                                return d.value.split("|")[0] == pm && d.value.split("|")[1] == mn
                            });
                            if (ri.length > 0) {
                                for (var j = 0; j < ri.length; j++) {
                                    var jri = ri[j];
                                    let amount = (parseFloat(jri.amount) || 0.0);
                                    b += amount;
                                }
                            }
                        }
                    }
                    return (v - b) * 1.0;
                }
            },
            add_refund_payment_method() {
                if (this.refunds.refund_method_options.length == 0) {
                    let pms = this.invoice.payment_methods || [];
                    for (var i = 0; i < pms.length; i++) {
                        let pm = pms[i];
                        this.refunds.refund_method_options.push({
                            value: pm.value + "|" + pm.name,
                            text: pm.value + " - " + pm.name
                        });
                    }
                    this.refunds.refund_method_options.push({
                        value: "store_credit" + "|" + this.invoice.invoice_id,
                        text: "store_credit" + " - " + this.invoice.invoice_id + " (CURRENT INVOICE)"
                    });
                }
                this.refunds.refund_methods.push({
                    value: null,
                    amount: 0.0,
                    sales_tax: 0.0,
                });
            },
            get_total_refund_dollar_value() {
                let x = (this.get_total_to_be_refunded() || 0.0) + parseFloat(this.refunds.total_delivery_refund || 0.0) + parseFloat(this.refunds.total_setup_refund || 0.0) + parseFloat(this.refunds.total_warranty_refund || 0.0);
                return x;
            },
            get_total_to_be_refunded() {
                let k = 0.0;
                if (this.refunds.items_to_be_refunded.length > 0) {
                    k += this.sum(this.refunds.items_to_be_refunded.map(function(d) {
                        return ((d.selling_price * d.return_quantity) - (d.unit_discount_value * d.return_quantity));
                    }));
                }
                return k;
            },
            get_total_delivery_refund_remaining() {
                let original_delivery_charges = (this.invoice.delivery_charges || 0.0);
                let so_far_refunded = (this.invoice.total_refunded_delivery || 0.0);
                return (original_delivery_charges - so_far_refunded);
            },
            get_total_setup_refund_remaining() {
                let original_setup_charges = (this.invoice.setup_charges || 0.0);
                let so_far_refunded = (this.invoice.total_refunded_setup || 0.0);
                return (original_setup_charges - so_far_refunded);
            },
            load_cs_issues(item) {
                let id = item.cs_ticket_id;
                this.cs_item_data = {
                    id: id,
                    invoice_id: item.invoice_id,
                    product_sku: item.product_sku,
                    itemid: item.itemid
                };
                this.$bvModal.show("modal-cs-ticket");
            },
            complete_refund(invoice_id) {
                let data = { invoice_id: invoice_id };
                let path = "/api/sales/complete_refund/";
                let that = this;
                let s = function(response) {
                    if (response.data.errror) {
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                    } else {
                        that.reload_invoice();
                    }
                    that.invoice_being_loaded = false;
                };
                let f = function(error) {
                    that.$bvToast.toast("Error completing refund. Please try later.", {title: "Error", autoHideDelay: 1500, variant: "danger"});
                    that.invoice_being_loaded = false;
                };
                this.invoice_being_loaded = true;
                Requestor.postSyncP(path, data, this, s, f);
            },
            update_invoice_customer(e) {
                if (e) {
                    e.preventDefault();
                }
                let path = "/api/sales/update_invoice_customer/";
                let data = { invoice_id: this.invoice.invoice_id, customer: this.invoice.customer };
                let that = this;
                let s = function(response) {
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                    } else {
                        that.$bvToast.toast("Updated.", {title: "Success", autoHideDelay: 1500, variant: "success"});
                    }
                };
                let f = function(error) {
                    that.$bvToast.toast("Error updating customer info. Please try again.", {title: "Error", autoHideDelay: 1500, variant: "danger"});
                };
                Requestor.postAsyncP(path, data, this, s, f);
            },
            update_item_level(item) {
                let that = this;
                if (item.order_type === 'delivery_from_store') {
                    this.handleDeliveryFromStore(item);
                }
                let data = {
                    invoice_id: item.invoice_id,
                    itemid: item.itemid,
                    product_sku: item.product_sku,
                    delivery_date: item.delivery_date,
                    order_type: item.order_type,
                    pull_date: moment(item.delivery_date).add(-1, 'day').format("MM-DD-YYYY"),
                    username: this.packet.userDetails ? this.packet.userDetails.username : ''
                };
                let path = "/api/sales/update_item_level/";
                let s = function(response) {
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                    } else {
                        that.$bvToast.toast("UPDATED ITEM DATA.", {title: "Success", autoHideDelay: 1500, variant: "success"});
                    }
                };
                let f = function(error) {
                    that.$bvToast.toast("Error updating delivery date. Please try again.", {title: "Error", autoHideDelay: 1500, variant: "danger"});
                };
                Requestor.postAsyncP(path, data, this, s, f);
            },
            get_invoice_location_address() {
                let location_id = this.invoice.location_id;
                let location = this.packet.locations ? this.packet.locations.filter(function(d) { return d.value == location_id }) : [];
                if (location.length > 0) {
                    location = location[0];
                    this.invoice_location_details.address = location.address;
                    this.invoice_location_details.name = location.name;
                    this.invoice_location_details.email = location.email;
                    this.invoice_location_details.phone = location.phone;
                    this.invoice_location_details.location_address = location.location_address;
                    this.invoice_location_details.short_name = location.short_name;
                    this.invoice_location_details.website = "www.myfurnituremecca.com";
                    this.invoice_location_details.city = location.city || '';
                    this.invoice_location_details.state = location.state || '';
                    this.invoice_location_details.zipcode = location.zipcode || '';
                }
            },
            fetch_prereqs() {
                let that = this;
                let prereqs = [
                    {
                        path: "/api/inventory/get_all_loc_inventory_dict/",
                        data: {},
                        type: "",
                        executable: Requestor.getAsync,
                        s: function(response) {
                            if (response.data.result) {
                                that.all_loc_inventory_dict = response.data.result;
                            } else {
                                that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                            }
                        },
                        f: function(error) {
                            that.$bvToast.toast("Error loading inventory.", {title: "Error", autoHideDelay: 1500, variant: "warning"});
                        }
                    },
                    {
                        path: "/api/policy/get_policy/",
                        data: {},
                        type: "secure",
                        executable: Requestor.getAsyncP,
                        s: function(response) {
                            if (response.data.result) {
                                that.policy_text = response.data.result[0].text;
                            } else {
                                that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                            }
                        },
                        f: function(error) {
                            that.$bvToast.toast("Error loading terms and conditions.", {title: "Error", autoHideDelay: 1500, variant: "warning"});
                        }
                    },
                    {
                        path: "/api/finance_company/get_all_finance_companies/",
                        data: {},
                        type: "secure",
                        executable: Requestor.getAsyncP,
                        s: function(response) {
                            if (response.data.result) {
                                that.finance_companies = response.data.result.map(function(d) {
                                    return {
                                        "value": d.finance_company_name,
                                        "text": d.finance_company_name,
                                        "phone_number": d.finance_company_phone_number,
                                        "company_charges_tax_in_states": d.finance_company_charges_tax_in_states,
                                        "banner_image": d.banner_image || null
                                    }
                                });
                            } else {
                                that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                            }
                        },
                        f: function(error) {
                            console.log(error, 'C');
                            that.$bvToast.toast("Error loading finance companies.", {title: "Error", autoHideDelay: 1500, variant: "warning"});
                        }
                    },
                ];
                for (var i = 0; i < prereqs.length; i++) {
                    let path = prereqs[i]["path"];
                    let data = prereqs[i]["data"];
                    let s = prereqs[i]["s"];
                    let f = prereqs[i]["f"];
                    let type = prereqs[i]["type"];
                    if (type == "secure") {
                        prereqs[i]["executable"](path, data, this, s, f);
                    } else {
                        prereqs[i]["executable"](path, data, s, f);
                    }
                }
            },
            print_it() {
                window.print();
            },
            reload_invoice() {
                let path = "/api/sales/get_invoice/";
                let data = { invoice_id: this.invoice.invoice_id };
                let that = this;

                this.startLoadingAnimation();

                let s = function(response) {
                    if (response.data.result) {
                        that.invoice = response.data.result;

                        if (!that.invoice.customer) {
                            that.invoice.customer = {
                                fullname: '',
                                address_line_1: '',
                                address_line_2: '',
                                phone: '',
                                phone_2: '',
                                email: '',
                                city: '',
                                state: '',
                                zipcode: '',
                                floor: ''
                            };
                        }

                        that.lease_id = that.invoice.lease_id || null;
                        that.misc_info = that.invoice.misc_info || null;

                        that.termsAgreed = that.invoice.terms_agreed === true || that.invoice.terms_agreed === 'true' || that.invoice.terms_agreed === 1 || that.invoice.terms_agreed === '1';
                        that.warrantyRefused = that.invoice.warranty_refused === true || that.invoice.warranty_refused === 'true' || that.invoice.warranty_refused === 1 || that.invoice.warranty_refused === '1';
                        that.deliveryWaiverAgreed = that.invoice.delivery_waiver_agreed === true || that.invoice.delivery_waiver_agreed === 'true' || that.invoice.delivery_waiver_agreed === 1 || that.invoice.delivery_waiver_agreed === '1';
                        that.spacePreparationAgreed = that.invoice.space_preparation_agreed === true || that.invoice.space_preparation_agreed === 'true' || that.invoice.space_preparation_agreed === 1 || that.invoice.space_preparation_agreed === '1';
                        that.addressConfirmationAgreed = that.invoice.address_confirmation_agreed === true || that.invoice.address_confirmation_agreed === 'true' || that.invoice.address_confirmation_agreed === 1 || that.invoice.address_confirmation_agreed === '1';
                        that.deliveryDamageWaiverAgreed = that.invoice.delivery_damage_waiver_agreed === true || that.invoice.delivery_damage_waiver_agreed === 'true' || that.invoice.delivery_damage_waiver_agreed === 1 || that.invoice.delivery_damage_waiver_agreed === '1';

                        that.fetchTicketsForInvoice();

                        that.fetchInvoiceEditCount();
                        that.historyLoadedOnce = false;
                        that.invoiceEditHistory = [];

                        let parentData = that.invoice.parent_invoice_data;

                        if (typeof parentData === 'string') {
                            try {
                                parentData = JSON.parse(parentData);
                                that.invoice.parent_invoice_data = parentData;
                            } catch(e) {
                                console.error('Failed to parse parent_invoice_data:', e);
                                parentData = null;
                            }
                        }

                        if (parentData && parentData !== 'null' && parentData !== null) {
                            that.parentInvoiceData = parentData;
                            that.parentInvoicePaymentMethods = parentData.payment_methods || [];
                            that.showParentPaymentInfo = that.parentInvoicePaymentMethods.length > 0;
                        } else {
                            if (that.invoice.invoice_type === 'EX' && that.invoice.parent_invoice_id) {
                                that.fetchParentInvoice();
                            } else {
                                that.parentInvoiceData = null;
                                that.parentInvoicePaymentMethods = [];
                                that.showParentPaymentInfo = false;
                            }
                        }

                        if (!that.invoice.invoice_id) {
                            that.message = "No invoice found.";
                        }

                        that.stopLoadingAnimation();
                    } else {
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                        that.stopLoadingAnimation();
                    }
                    that.invoice_being_loaded = false;
                };

                let f = function(error) {
                    console.log(error);
                    that.$bvToast.toast("Error loading invoice. Please try again later.", {title: "Error", autoHideDelay: 1500, variant: "danger"});
                    that.invoice_being_loaded = false;
                    that.stopLoadingAnimation();
                };

                this.refunds = this.get_refunds_packet();
                this.invoice_being_loaded = true;
                Requestor.postAsyncP(path, data, this, s, f);
            },
            fetchParentInvoice() {
                if (!this.isExchangeInvoice) return;

                const parentInvoiceId = this.invoice.parent_invoice_id;
                if (!parentInvoiceId) return;

                const that = this;
                const path = "/api/sales/get_invoice/";
                const data = { invoice_id: parentInvoiceId };

                const s = function(response) {
                    if (response.data.result) {
                        that.parentInvoiceData = response.data.result;
                        that.parentInvoicePaymentMethods = response.data.result.payment_methods || [];
                        that.showParentPaymentInfo = that.parentInvoicePaymentMethods.length > 0;
                        that.$forceUpdate();
                    }
                };

                const f = function(error) {
                    console.error("Error fetching parent invoice:", error);
                };

                Requestor.postAsyncP(path, data, this, s, f);
            },
            request_refund(invoice_id) {
                let that = this;
                let data = { invoice_id: invoice_id, requesting_username: this.packet.userDetails ? this.packet.userDetails.username : '' };
                let path = "/api/sales/request_refund/";
                let s = function(response) {
                    if (response.data.errror) {
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                    } else {
                        that.invoice.refund_requested = true;
                        that.invoice.refund_request_approved = response.data.result[0].approved;
                    }
                    that.invoice_being_loaded = false;
                };
                let f = function(error) {
                    console.log(error);
                    that.$bvToast.toast("Error requesting a refund. Please try later.", {title: "Error", autoHideDelay: 1500, variant: "danger"});
                    that.invoice_being_loaded = false;
                };
                this.invoice_being_loaded = true;
                Requestor.getAsyncP(path, data, this, s, f);
            },
            get_quantity_dd(quantity) {
                return [...Array(quantity).keys()].map(function(d) { return { value: d + 1, text: d + 1 } });
            },
            sum(items) {
                return items.reduce((pv, cv) => parseFloat(pv) + parseFloat(cv), 0.0);
            },
            edit_invoice() {
                this.edit_invoice_action = !this.edit_invoice_action;
            },
            summarize_refunds_history(refunds) {
                let total_refunded_value = 0.0;
                for (var i = 0; i < refunds.length; i++) {
                    let refund = refunds[i];
                    if (refund.detail.type == 'return') {
                        let value = refund.detail.total_refunded_item_amount + refund.detail.total_refunded_sales_tax + refund.detail.total_refunded_delivery + refund.detail.total_refunded_setup + refund.detail.total_refunded_warranty;
                        total_refunded_value += (value || 0.0);
                    }
                }
                this.total_refunded_value = (total_refunded_value || 0.0);
            },
            distribute_sales_tax(total_sales_tax_being_refunded) {
                // This method can be implemented as needed
            }
        },
        created: function() {
            this.packet = JSON.parse(localStorage.packet || '{}');
            this.packet.userDetails = JSON.parse(localStorage.userDetails || '{}');
            this.edit_invoice_action = false;

            this.startLoadingAnimation();

            if (this.viewdata) {
                this.invoice = this.viewdata;

                if (!this.invoice.customer) {
                    this.invoice.customer = {
                        fullname: '',
                        address_line_1: '',
                        address_line_2: '',
                        phone: '',
                        phone_2: '',
                        email: '',
                        city: '',
                        state: '',
                        zipcode: '',
                        floor: ''
                    };
                }

                this.termsAgreed = this.invoice.terms_agreed === true || this.invoice.terms_agreed === 'true' || this.invoice.terms_agreed === 1 || this.invoice.terms_agreed === '1';
                this.warrantyRefused = this.invoice.warranty_refused === true || this.invoice.warranty_refused === 'true' || this.invoice.warranty_refused === 1 || this.invoice.warranty_refused === '1';
                this.deliveryWaiverAgreed = this.invoice.delivery_waiver_agreed === true || this.invoice.delivery_waiver_agreed === 'true' || this.invoice.delivery_waiver_agreed === 1 || this.invoice.delivery_waiver_agreed === '1';
                this.spacePreparationAgreed = this.invoice.space_preparation_agreed === true || this.invoice.space_preparation_agreed === 'true' || this.invoice.space_preparation_agreed === 1 || this.invoice.space_preparation_agreed === '1';
                this.addressConfirmationAgreed = this.invoice.address_confirmation_agreed === true || this.invoice.address_confirmation_agreed === 'true' || this.invoice.address_confirmation_agreed === 1 || this.invoice.address_confirmation_agreed === '1';
                this.deliveryDamageWaiverAgreed = this.invoice.delivery_damage_waiver_agreed === true || this.invoice.delivery_damage_waiver_agreed === 'true' || this.invoice.delivery_damage_waiver_agreed === 1 || this.invoice.delivery_damage_waiver_agreed === '1';
                this.get_invoice_location_address();
                this.fetchTicketsForInvoice();

                this.fetchInvoiceEditCount();

                let parentData = this.invoice.parent_invoice_data;

                if (typeof parentData === 'string') {
                    try {
                        parentData = JSON.parse(parentData);
                        this.invoice.parent_invoice_data = parentData;
                    } catch(e) {
                        console.error('Failed to parse parent_invoice_data in created:', e);
                        parentData = null;
                    }
                }

                if (parentData && parentData !== 'null' && parentData !== null) {
                    this.parentInvoiceData = parentData;
                    this.parentInvoicePaymentMethods = parentData.payment_methods || [];
                    this.showParentPaymentInfo = this.parentInvoicePaymentMethods.length > 0;
                }

                setTimeout(() => {
                    this.stopLoadingAnimation();
                }, 300);
            } else {
                this.reload_invoice();
            }
            this.fetch_prereqs();
        }
    }
</script>

<style>

@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700;800&family=Manrope:wght@400;500;600;700;800&family=Sora:wght@500;600;700;800&display=swap');

@page { size: letter; margin: 0.35in; }

.vs-page,
.vs-modal {
    --vs-ebony: #221F1C;
    --vs-ebony-2: #34302B;
    --vs-orange: #EE7A22;
    --vs-orange-dark: #D4620F;
    --vs-orange-soft: #FFF1E4;
    --vs-teal: #0F6E6B;
    --vs-teal-soft: #E2F2F0;
    --vs-paper: #FFFFFF;
    --vs-canvas: #F6F4F0;
    --vs-mist: #F1EEE9;
    --vs-line: #E5E1DA;
    --vs-muted: #736C63;
    --vs-danger: #C93C30;
    --vs-danger-soft: #FBE9E7;
    --vs-warn: #C98A12;
    --vs-warn-soft: #FDF3DE;
    --vs-head: 'Sora', 'Manrope', system-ui, sans-serif;
    --vs-body: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    font-family: var(--vs-body);
    color: var(--vs-ebony);
}

.vs-modal.modal-content { border: 0; border-radius: 16px; overflow: hidden; }
.vs-modal .modal-header { background: var(--vs-ebony); color: #fff; border: 0; border-bottom: 3px solid var(--vs-orange); padding: 16px 22px; }
.vs-modal .modal-title { font-family: var(--vs-head); font-weight: 700; font-size: 19px; }
.vs-modal .modal-header .close { color: #fff; opacity: 0.8; text-shadow: none; }
.vs-modal .modal-body { background: var(--vs-canvas); padding: 20px 22px; font-size: 15px; }
.vs-modal .modal-footer { background: #fff; border-top: 1px solid var(--vs-line); }
.vs-modal .vs-modal-caption { font-family: var(--vs-head); font-weight: 600; font-size: 16px; }
.vs-modal .vs-empty { color: var(--vs-muted); }
.vs-modal .vs-finance-detail { background: #fff; border: 1px solid var(--vs-line); border-radius: 12px; padding: 16px 18px; margin-bottom: 14px; }
.vs-modal .vs-finance-detail-head { display: flex; justify-content: space-between; align-items: center; gap: 10px; padding-bottom: 10px; margin-bottom: 12px; border-bottom: 2px solid var(--vs-orange); }
.vs-modal .vs-finance-detail-head h6 { font-family: var(--vs-head); font-weight: 700; font-size: 16px; margin: 0; }
.vs-modal .vs-dl { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 8px 18px; margin: 0; }
.vs-modal .vs-dl dt { color: var(--vs-muted); font-weight: 500; }
.vs-modal .vs-dl dd { margin: 0; font-weight: 700; text-align: right; font-variant-numeric: tabular-nums; }
.vs-modal .vs-chip { display: inline-block; font-size: 12px; font-weight: 700; padding: 3px 10px; border-radius: 999px; background: var(--vs-mist); }
.vs-modal .vs-chip.is-teal { background: var(--vs-teal-soft); color: var(--vs-teal); }
.vs-modal .is-teal { color: var(--vs-teal); }
.vs-modal .vs-muted { color: var(--vs-muted); }
.vs-modal .vs-modal-total { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding-top: 14px; margin-top: 6px; border-top: 1px solid var(--vs-line); }
.vs-modal .vs-modal-total span { color: var(--vs-muted); margin-right: 10px; }
.vs-modal .vs-modal-total strong { font-family: var(--vs-head); font-size: 22px; color: var(--vs-teal); }
.vs-modal .vs-btn-ghost,
.vs-modal .vs-btn-ghost.btn { background: #fff !important; color: var(--vs-ebony) !important; border: 1.5px solid var(--vs-line) !important; border-radius: 10px !important; font-weight: 700; }
.vs-modal .vs-btn-ghost.btn:hover { border-color: var(--vs-ebony) !important; }

.vs-page .vs-banner-icon,
.vs-page .vs-icon,
.vs-page .vs-check,
.vs-page .vs-logo,
.vs-modal .vs-history-marker {
  position: relative !important;
  display: inline-block !important;
  flex: 0 0 auto !important;
  padding: 0 !important;
  text-align: center !important;
  vertical-align: middle !important;
  float: none !important;
  box-sizing: border-box !important;
}
.vs-page .vs-banner-icon     { width: 46px !important; height: 46px !important; line-height: 46px !important; font-size: 22px !important; }
.vs-page .vs-icon            { width: 46px !important; height: 46px !important; line-height: 46px !important; font-size: 20px !important; }
.vs-page .vs-check           { width: 28px !important; height: 28px !important; line-height: 28px !important; font-size: 15px !important; }
.vs-modal .vs-history-marker { position: absolute !important; width: 36px !important; height: 36px !important; line-height: 36px !important; font-size: 16px !important; }
.vs-page .vs-logo            { width: 54px !important; height: 54px !important; line-height: 54px !important; font-size: 20px !important; }
.vs-page .vs-logo.vs-logo--lg { width: 64px !important; height: 64px !important; line-height: 64px !important; font-size: 24px !important; display: block !important; }

.vs-page .vs-banner-icon > svg,
.vs-page .vs-icon > svg,
.vs-page .vs-check > svg,
.vs-modal .vs-history-marker > svg,
.vs-page .vs-action-btn > svg {
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  width: 1em !important;
  height: 1em !important;
  margin: 0 !important;
  transform: translate(-50%, -50%) !important;
  display: block !important;
}

.vs-page .vs-action-btn.btn {
  position: relative !important;
  display: inline-block !important;
  width: 34px !important;
  height: 34px !important;
  min-width: 34px !important;
  padding: 0 !important;
  line-height: 34px !important;
}
.vs-page .vs-action-btn .vs-count {
  position: absolute !important;
  top: -6px !important;
  right: -6px !important;
  display: inline-block !important;
  min-width: 17px;
  height: 17px;
  line-height: 17px !important;
  text-align: center;
}

.vs-page .vs-edit-count-pill {
  display: inline-block !important;
  min-width: 20px;
  height: 20px;
  line-height: 20px !important;
  text-align: center;
}

.vs-page svg.b-icon,
.vs-modal svg.b-icon {
  width: 1em !important;
  height: 1em !important;
  vertical-align: -0.125em !important;
  float: none !important;
  overflow: visible;
}
.vs-page .no-image-placeholder svg.b-icon { width: 26px !important; height: 26px !important; }

.vs-page .vs-meta-field > span,
.vs-page .vs-meta-row > span,
.vs-page .vs-pcard-kicker,
.vs-page .vs-edit-note,
.vs-page .vs-price-row span,
.vs-modal .vs-history-meta > span {
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px;
}
.vs-page .vs-meta-field > span,
.vs-page .vs-pcard-kicker,
.vs-page .vs-edit-note { display: flex !important; }

@media screen {
  .vs-page { overflow-x: hidden; -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }
  .vs-page img { max-width: 100%; }
  .vs-page .vs-table-scroll { -webkit-overflow-scrolling: touch; }
  .vs-page .vs-refund-method-grid { grid-template-columns: repeat(auto-fill, minmax(min(240px, 100%), 1fr)); }
  .loading-spinner-container { max-width: calc(100vw - 32px); }
  .loading-progress-bar { width: min(260px, 70vw); }
}

@media screen and (pointer: coarse) {
  .vs-page .btn:not(.vs-action-btn) { min-height: 44px; }
  .vs-page .btn-sm:not(.vs-action-btn) { min-height: 40px; }
  .vs-page .vs-action-btn.btn { width: 42px !important; height: 42px !important; min-width: 42px !important; line-height: 42px !important; font-size: 17px; }
  .vs-page .vs-field .form-control { height: 44px; }
  .vs-page .vs-pcard .custom-select { height: 40px; }
  .vs-page .image-overlay { display: none; }
  .vs-modal .modal-header .close { min-width: 44px; min-height: 44px; }
}

@media screen and (min-width: 576px) and (max-width: 1199.98px) {
  .vs-dialog.modal-lg,
  .vs-dialog.modal-xl { max-width: 94vw; }
}

@media screen and (max-width: 1199.98px) {
  .vs-page .vs-wrap { width: 96%; }

  .vs-page .vs-hero-grid { gap: 22px; padding: 22px 24px; }
  .vs-page .vs-hero-brand { border-right: 0; padding: 0 0 0 22px; }
  .vs-page .vs-brand-name { font-size: 34px; }
  .vs-page .vs-hero-actions { padding: 12px 24px; }

  .vs-page .vs-card { padding: 20px; }
}

@media screen and (max-width: 991.98px) {
  .vs-page .vs-pcard,
  .vs-page .vs-pcard.is-child { grid-template-columns: 120px minmax(0, 1fr); }
  .vs-page .vs-pcard-meta,
  .vs-page .vs-pcard-price,
  .vs-page .vs-pcard-actions { grid-column: 1 / -1; }
  .vs-page .vs-pcard-actions { flex-direction: row; justify-content: flex-end; gap: 8px; padding: 2px 4px 4px; }
  .vs-page .vs-pcard-actions:empty { display: none; }
  .vs-page .vs-pcard-img { min-height: 120px; }

  .vs-page .vs-pcard.is-child { margin-left: 28px; }
  .vs-page .vs-pcard.is-child::before,
  .vs-page .vs-pcard.is-child::after { left: -17px; }
  .vs-page .vs-pcard.is-child::after { width: 16px; }
  .vs-page .vs-pcard-dot { left: -22px; }

  .vs-page .vs-finance-row { align-items: flex-start; }
  .vs-page .vs-signature { align-items: flex-start; }
  .vs-page .vs-signature-box { align-items: flex-start; }
}

@media screen and (max-width: 767.98px) {
  .vs-page { padding-bottom: 32px; }
  .vs-page .vs-wrap { width: 100%; padding: 0 12px; }

  .vs-page .vs-hero { border-radius: 18px; margin-top: 12px; }
  .vs-page .vs-hero-grid { grid-template-columns: 1fr; gap: 18px; padding: 18px; }
  .vs-page .vs-hero-invoice { padding: 0; border-top: 0; }
  .vs-page .vs-hero-number { font-size: 26px; }
  .vs-page .vs-hero-brand {
    border-left: 0; border-right: 0; padding: 16px 0 0;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
  }
  .vs-page .vs-brand-name { font-size: 28px; }
  .vs-page .vs-brand-line { font-size: 13px; }
  .vs-page .vs-hero-qr { padding-top: 16px; }
  .vs-page .vs-hero-qr img { width: 92px; }
  .vs-page .vs-hero-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    padding: 12px 18px 14px;
  }
  .vs-page .vs-hero-actions .btn { width: 100%; justify-content: center; white-space: normal; }

  .vs-page .vs-banner { padding: 14px; gap: 12px; }
  .vs-page .vs-banner-actions { width: 100%; }
  .vs-page .vs-banner-actions .btn { flex: 1 1 auto; justify-content: center; }
  .vs-page .vs-strip > div { flex: 1 1 45%; border-bottom: 1px solid var(--vs-line); }
  .vs-page .vs-strip strong { font-size: 18px; }

  .vs-page .vs-card { padding: 16px; border-radius: 16px; margin-top: 12px; }
  .vs-page .vs-card-head { margin-bottom: 14px; }
  .vs-page .vs-card-head > .btn { width: 100%; justify-content: center; }
  .vs-page .vs-card-title h3 { font-size: 18px; }
  .vs-page .vs-legend { gap: 10px; }

  .vs-page .vs-field .form-control,
  .vs-page .custom-select,
  .vs-page input,
  .vs-page select,
  .vs-modal input,
  .vs-modal select,
  .vs-modal textarea { font-size: 16px !important; }

  .vs-page .vs-pcard,
  .vs-page .vs-pcard.is-child { grid-template-columns: 1fr; }
  .vs-page .vs-pcard-img,
  .vs-page .vs-pcard.is-child .vs-pcard-img { min-height: 170px; }
  .vs-page .vs-pcard.is-child { margin-left: 18px; }
  .vs-page .vs-pcard.is-child::before,
  .vs-page .vs-pcard.is-child::after { left: -12px; }
  .vs-page .vs-pcard.is-child::after { width: 11px; }
  .vs-page .vs-pcard-dot { left: -17px; }
  .vs-page .vs-pcard-actions { justify-content: stretch; }
  .vs-page .vs-pcard-actions .vs-action-btn.btn { flex: 1 1 0; width: auto !important; }

  .vs-page .vs-sum-col { padding: 16px; }
  .vs-page .vs-sum-col.is-totals .vs-dl .vs-dl-grand { font-size: 21px; }
  .vs-page .vs-warranty-refused { max-width: 160px; }
  .vs-page .vs-finance-info { gap: 6px 12px; }

  .vs-page .vs-finance-banner { grid-template-columns: 1fr; }
  .vs-page .vs-finance-banner-left { border-right: 0; border-bottom: 1px solid var(--vs-line); }

  .vs-page .vs-refund-methods-head .btn { width: 100%; justify-content: center; }
  .vs-page .vs-refund-methods > .btn { width: 100%; justify-content: center; }

  .vs-page .vs-policy { max-height: 260px; padding: 14px; }
  .vs-page .vs-signature-box img { max-width: 100%; }
  .vs-page .vs-signature-line { min-width: 0; width: 100%; text-align: left; }

  .vs-modal .modal-header { padding: 14px 16px; }
  .vs-modal .modal-title { font-size: 17px; }
  .vs-modal .modal-body { padding: 14px 12px; }
  .vs-modal .vs-history-entry { padding-left: 42px; }
  .vs-modal .vs-history-head { flex-direction: column; align-items: flex-start; gap: 6px; }
  .vs-modal .vs-modal-total { flex-direction: column; align-items: stretch; }
  .vs-modal .vs-modal-total .btn { width: 100%; }
  .vs-modal .vs-history-foot { flex-direction: column; align-items: stretch; }
  .vs-modal .vs-history-foot .btn { width: 100%; }
}

@media screen and (max-width: 575.98px) {
  .vs-dialog.modal-dialog {
    margin: 0;
    max-width: 100%;
    width: 100%;
    min-height: 100%;
    height: 100%;
    align-items: stretch;
  }
  .vs-dialog .modal-content.vs-modal { border-radius: 0; min-height: 100%; max-height: 100%; }
  .vs-dialog.modal-dialog-scrollable { max-height: 100%; height: 100%; }
  .vs-dialog .modal-footer { padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px)); }

  .vs-page .vs-fields { grid-template-columns: 1fr; }
  .vs-page .vs-field--wide { grid-column: auto; }

  .vs-page .vs-meta-row-2col { grid-template-columns: 1fr; }
  .vs-page .vs-strip > div { flex: 1 1 100%; border-right: 0; }

  .vs-page .vs-refund-boxes { grid-template-columns: 1fr; }
  .vs-page .vs-balance-total strong { font-size: 21px; }
  .vs-page .vs-finance-row { flex-direction: column; }
  .vs-page .vs-finance-total { text-align: left; }
}

@media screen and (max-width: 379.98px) {
  .vs-page .vs-wrap { padding: 0 8px; }
  .vs-page .vs-hero-grid { padding: 14px; }
  .vs-page .vs-hero-meta { grid-template-columns: 1fr; }
  .vs-page .vs-hero-actions { grid-template-columns: 1fr; }
  .vs-page .vs-hero-qr { flex-direction: column; align-items: flex-start; }
  .vs-page .vs-brand-name { font-size: 24px; }
  .vs-page .vs-layaway-grid { grid-template-columns: 1fr; }
  .vs-page .vs-legend { font-size: 12px; }
}

@media screen and (max-height: 500px) and (orientation: landscape) {
  .vs-dialog.modal-dialog { margin: 0 auto; max-width: 100%; }
  .vs-dialog .modal-content.vs-modal { border-radius: 0; }
  .vs-page .vs-policy { max-height: 180px; }
}

</style>

<style scoped>
.vs-qty-cubes-row {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}
.vs-qty-cubes-row > span {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
}
.vs-qty-cubes-row b {
    color: var(--vs-ebony-2);
    font-weight: 700;
}
.vs-qty-cubes-row b.is-danger {
    color: var(--vs-danger);
}
.vs-qty-cubes-sep {
    color: var(--vs-muted);
    font-weight: 700;
    opacity: 0.6;
}

.vs-warranty-refused {
    color: #C93C30;
    font-style: italic;
    font-size: 12.5px;
    font-weight: 600;
    text-align: right;
    max-width: 220px;
    line-height: 1.35;
    justify-self: end;
}

.vs-page { background: var(--vs-canvas); min-height: 100vh; padding-bottom: 60px; font-size: 15px; }
.vs-wrap { max-width: 1480px; width: 94%; margin: 0 auto; }
.vs-page h3, .vs-page h4, .vs-page h5 { font-family: var(--vs-head); }

.vs-btn-primary.btn,
.vs-btn-teal.btn,
.vs-btn-danger.btn,
.vs-btn-ghost.btn,
.vs-btn-ghost-dark.btn {
    border-radius: 10px !important;
    font-weight: 700;
    font-size: 14px;
    padding: 8px 16px;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    box-shadow: none !important;
}
.vs-btn-primary.btn { background: var(--vs-orange) !important; border: 1.5px solid var(--vs-orange) !important; color: #fff !important; }
.vs-btn-primary.btn:hover:not(:disabled) { background: var(--vs-orange-dark) !important; border-color: var(--vs-orange-dark) !important; }
.vs-btn-teal.btn { background: var(--vs-teal) !important; border: 1.5px solid var(--vs-teal) !important; color: #fff !important; }
.vs-btn-danger.btn { background: var(--vs-danger) !important; border: 1.5px solid var(--vs-danger) !important; color: #fff !important; }
.vs-btn-ghost.btn { background: #fff !important; color: var(--vs-ebony) !important; border: 1.5px solid var(--vs-line) !important; }
.vs-btn-ghost.btn:hover:not(:disabled) { border-color: var(--vs-ebony) !important; }
.vs-btn-ghost-dark.btn { background: rgba(255, 255, 255, 0.06) !important; color: #fff !important; border: 1.5px solid rgba(255, 255, 255, 0.22) !important; }
.vs-btn-ghost-dark.btn:hover:not(:disabled) { border-color: var(--vs-orange) !important; color: #fff !important; }
.vs-page .btn:disabled { opacity: 0.5; }
.vs-page .btn:focus-visible { outline: 3px solid var(--vs-orange); outline-offset: 2px; }
.vs-btn-ghost.btn.btn-sm, .vs-btn-primary.btn.btn-sm, .vs-btn-ghost-dark.btn.btn-sm { padding: 5px 12px; font-size: 13px; }
.vs-link.btn { color: var(--vs-orange-dark) !important; font-weight: 700; text-decoration: underline; text-underline-offset: 3px; }
.vs-link-danger.btn { color: var(--vs-danger) !important; font-weight: 700; padding: 0; }

.vs-chip {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 12px; font-weight: 700; padding: 3px 10px; border-radius: 999px;
    background: var(--vs-mist); color: var(--vs-ebony); white-space: nowrap;
}
.vs-chip.is-warn { background: var(--vs-warn-soft); color: #8A5E09; }
.vs-chip.is-danger { background: var(--vs-danger-soft); color: var(--vs-danger); }
.vs-chip.is-teal { background: var(--vs-teal-soft); color: var(--vs-teal); }
.vs-chip.is-orange { background: var(--vs-orange-soft); color: var(--vs-orange-dark); }
.vs-chip.is-dark { background: var(--vs-ebony); color: #fff; }
.is-teal { color: var(--vs-teal); }
.is-warn { color: #8A5E09; }
.is-danger { color: var(--vs-danger); }

.vs-banner {
    display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
    background: #fff; border: 1px solid var(--vs-line); border-left: 6px solid var(--vs-warn);
    border-radius: 14px; padding: 16px 20px; margin-top: 16px;
}
.vs-banner.is-info { border-left-color: var(--vs-teal); }
.vs-banner-icon {
    width: 46px; height: 46px; border-radius: 12px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center; font-size: 22px;
    background: var(--vs-warn-soft); color: #8A5E09;
}
.vs-banner.is-info .vs-banner-icon { background: var(--vs-teal-soft); color: var(--vs-teal); }
.vs-banner-body { flex: 1 1 300px; display: flex; flex-direction: column; gap: 2px; }
.vs-banner-body strong { font-family: var(--vs-head); font-size: 17px; }
.vs-banner-body span { color: var(--vs-muted); font-size: 14px; }
.vs-banner-links { font-size: 13px; color: var(--vs-muted); display: flex; gap: 10px; flex-wrap: wrap; }
.vs-banner-links a { font-weight: 700; color: var(--vs-orange-dark); }
.vs-banner-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

.vs-strip {
    display: flex; flex-wrap: wrap; margin-top: 12px;
    background: #fff; border: 1px solid var(--vs-line); border-radius: 14px; overflow: hidden;
}
.vs-strip > div { flex: 1 1 180px; padding: 14px 18px; border-right: 1px solid var(--vs-line); }
.vs-strip > div:last-child { border-right: 0; }
.vs-strip span { display: block; font-size: 13px; color: var(--vs-muted); font-weight: 600; }
.vs-strip strong { font-family: var(--vs-head); font-size: 21px; font-variant-numeric: tabular-nums; }
.vs-strip .vs-strip-detail { flex-basis: 100%; border-top: 1px solid var(--vs-line); border-right: 0; background: var(--vs-mist); }
.vs-strip-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px; }
.vs-strip-chips .vs-chip { background: #fff; font-weight: 500; }

.vs-hero {
    background: var(--vs-ebony); color: #fff;
    border-radius: 20px; margin-top: 18px; overflow: hidden;
    border-bottom: 5px solid var(--vs-orange);
}
.vs-hero-grid {
    display: grid; grid-template-columns: minmax(0, 1.1fr) minmax(0, 1.3fr) minmax(0, 1fr);
    gap: 28px; padding: 26px 30px;
}
.vs-logo {
    width: 54px; height: 54px; border-radius: 14px; flex-shrink: 0;
    background: var(--vs-orange); color: #fff;
    display: flex; align-items: center; justify-content: center;
    font-family: var(--vs-head); font-weight: 800; font-size: 20px; letter-spacing: -0.02em;
}
.vs-logo--lg { width: 64px; height: 64px; font-size: 24px; margin: 0 auto 16px; }

.vs-hero-brand {
    display: flex; gap: 16px;
    justify-content: center; align-items: center;
    border-left: 1px solid rgba(255, 255, 255, 0.12);
    border-right: 1px solid rgba(255, 255, 255, 0.12);
    padding: 0 28px;
    text-align: center;
}
.vs-brand-name { font-family: var(--vs-head); font-size: 44px; font-weight: 800; line-height: 1.1; margin-bottom: 6px; }
.vs-brand-line { font-size: 14px; display: flex; justify-content: center; color: rgba(255, 255, 255, 0.68); line-height: 1.5; word-break: break-word; }
.vs-brand-line--strong { color: #fff; font-weight: 600; }

.vs-hero-invoice { padding: 0 10px; }
.vs-hero-label { font-size: 14px; color: rgba(255, 255, 255, 0.6); font-weight: 600; }
.vs-hero-number { font-family: var(--vs-head); font-size: 20px; font-weight: 800; letter-spacing: -0.02em; line-height: 1.1; color: var(--vs-orange); word-break: break-all; }
.vs-edit-count-suffix {
    color: #EE7A22;
    font-size: 20px;
    font-weight: 800;
    letter-spacing: 0;
    margin-left: 2px;
    opacity: 0.95;
}
.vs-hero-tags { display: flex; flex-wrap: wrap; gap: 6px; margin: 10px 0 14px; }
.vs-tag { font-size: 13px; font-weight: 700; padding: 4px 12px; border-radius: 999px; }
.vs-tag.is-danger { background: var(--vs-danger); color: #fff; }
.vs-tag.is-teal { background: var(--vs-teal); color: #fff; }
.vs-tag.is-orange { background: var(--vs-orange-soft); color: var(--vs-orange-dark); }
.vs-hero-meta { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 2px; margin: 0; }
.vs-hero-meta dt { font-size: 10px; color: rgba(255, 255, 255, 0.55); font-weight: 600; }
.vs-hero-meta dd { margin: 2px 0 0; font-size: 14px; font-weight: 400; word-break: break-word; }

.vs-hero-qr { display: flex; gap: 16px; align-items: center; }
.vs-hero-qr img { background: #fff; border-radius: 12px; padding: 8px; flex-shrink: 0; }
.vs-qr-title { font-family: var(--vs-head); font-weight: 700; font-size: 16px; }
.vs-qr-sub { font-size: 13px; color: rgba(255, 255, 255, 0.6); }

.vs-hero-actions {
    display: flex; gap: 10px; flex-wrap: wrap;
    padding: 14px 30px; background: var(--vs-ebony-2);
}

.vs-edit-count-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    margin-left: 4px;
    background: var(--vs-orange);
    color: #fff;
    font-size: 11px;
    font-weight: 800;
    border-radius: 999px;
    line-height: 1;
}

@media (max-width: 1199px) {
    .vs-hero-grid { grid-template-columns: 1fr 1fr; }
    .vs-hero-invoice { border-right: 0; }
    .vs-hero-qr { grid-column: 1 / -1; border-top: 1px solid rgba(255, 255, 255, 0.12); padding-top: 18px; }
}
@media (max-width: 767px) {
    .vs-hero-grid { grid-template-columns: 1fr; padding: 20px; gap: 20px; }
    .vs-hero-invoice { border-left: 0; padding: 18px 0 0; border-top: 1px solid rgba(255, 255, 255, 0.12); }
    .vs-hero-meta { grid-template-columns: 1fr 1fr; }
    .vs-hero-number { font-size: 28px; }
    .vs-edit-count-suffix { font-size: 14px; vertical-align: 4px; }
    .vs-hero-actions { padding: 14px 20px; }
}

.vs-card { background: #fff; border: 1px solid var(--vs-line); border-radius: 18px; padding: 22px 24px; margin-top: 18px; }
.vs-card-head { display: flex; justify-content: space-between; align-items: center; gap: 14px; flex-wrap: wrap; margin-bottom: 18px; }
.vs-card-title { display: flex; gap: 14px; align-items: center; }
.vs-card-title h3 { font-size: 20px; font-weight: 700; margin: 0; }
.vs-card-title p { margin: 2px 0 0; color: var(--vs-muted); font-size: 14px; }
.vs-icon {
    width: 46px; height: 46px; border-radius: 12px; flex-shrink: 0;
    background: var(--vs-orange); color: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px; line-height: 1;
}
.vs-icon.is-danger { background: var(--vs-danger); }
.vs-subhead { font-size: 16px; font-weight: 700; margin: 20px 0 10px; }
.vs-hint { font-size: 12px; color: var(--vs-muted); margin-top: 3px; }

.vs-edit-wrap { margin-top: 18px; }
.vs-edit-note {
    display: flex; gap: 10px; align-items: center;
    background: var(--vs-orange-soft); color: var(--vs-orange-dark);
    border-radius: 12px; padding: 12px 16px; font-weight: 600; margin-bottom: 14px;
}

.vs-fields { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 14px 16px; }
.vs-field { display: flex; flex-direction: column; gap: 5px; margin: 0; grid-column: span 1; min-width: 0; }
.vs-field--wide { grid-column: span 2; }
.vs-field > span { font-size: 13px; font-weight: 700; color: var(--vs-muted); }
.vs-field >>> .form-control {
    height: 38px; border: 1.5px solid var(--vs-line); border-radius: 10px;
    font-size: 15px; font-weight: 600; color: var(--vs-ebony); background: var(--vs-canvas);
}
.vs-field >>> .form-control:focus { border-color: var(--vs-orange); background: #fff; box-shadow: 0 0 0 3px rgba(238, 122, 34, 0.18); }
.vs-field >>> .form-control:disabled { background: var(--vs-mist); color: var(--vs-muted); }
@media (max-width: 1199px) { .vs-fields { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
@media (max-width: 767px) {
    .vs-fields { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .vs-field--wide { grid-column: span 2; }
}

.vs-note {
    display: flex; gap: 14px; align-items: flex-start; margin-top: 18px;
    background: var(--vs-warn-soft); border: 1px solid #F1DDAE; border-radius: 14px; padding: 16px 20px;
}
.vs-note > svg { font-size: 22px; color: #8A5E09; flex-shrink: 0; margin-top: 2px; }
.vs-note strong { font-family: var(--vs-head); font-size: 15px; }
.vs-note p { margin: 2px 0 0; white-space: pre-wrap; line-height: 1.5; max-width: 90ch; }

.vs-legend { display: flex; gap: 16px; flex-wrap: wrap; font-size: 13px; color: var(--vs-muted); font-weight: 600; }
.vs-dot { display: inline-block; width: 10px; height: 10px; border-radius: 3px; margin-right: 5px; vertical-align: -1px; }
.vs-dot.is-teal { background: var(--vs-teal); }
.vs-dot.is-danger { background: var(--vs-danger); }
.vs-dot.is-orange { background: var(--vs-orange); }
.vs-empty-line { color: var(--vs-muted); margin: 0; }

.vs-plist { display: flex; flex-direction: column; gap: 5px; }

.vs-pcard {
    position: relative;
    display: grid;
    grid-template-columns: 250px minmax(0, 1.35fr) minmax(0, 1.25fr) minmax(0, 1fr) 46px;
    gap: 6px;
    align-items: stretch;
    background: #fff;
    border: 1px solid var(--vs-line);
    border-radius: 16px;
    padding: 6px;
}
.vs-pcard-panel {
    background: var(--vs-canvas);
    border-radius: 12px;
    padding: 14px 16px;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 6px;
}

.vs-pcard-img {
    position: relative;
    min-height: 150px;
    border-radius: 12px;
    background: linear-gradient(160deg, #F7F3EE, #EDE8E1);
    display: flex; align-items: center; justify-content: center;
    overflow: hidden;
}
.vs-pcard-img img { max-width: 100%; max-height: 170px; object-fit: contain; cursor: zoom-in; display: block; }
.vs-img-badge {
    position: absolute; left: 8px; top: 8px;
    background: var(--vs-danger); color: #fff;
    font-size: 11px; font-weight: 800; padding: 2px 8px; border-radius: 999px;
}

.vs-pcard-kicker { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 800; color: var(--vs-orange-dark); }
.vs-pcard-kicker.is-muted { color: var(--vs-muted); font-weight: 700; }
.vs-pcard-title { font-family: var(--vs-head); font-size: 17px; font-weight: 700; line-height: 1.3; word-break: break-word; }
.vs-pcard-line { font-size: 13px; color: var(--vs-muted); font-weight: 600; word-break: break-word; }
.vs-pcard-line b { color: var(--vs-ebony-2); font-weight: 700; }
.vs-pcard-tags { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 2px; }
.vs-pcard-tags:empty { display: none; }

.vs-pcard-status-line {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 4px;
}
.vs-pcard-status-line > span {
    font-size: 12px;
    font-weight: 700;
    color: var(--vs-muted);
}
.vs-pcard-status-line >>> .custom-select {
    border: 1.5px solid var(--vs-line); border-radius: 8px;
    font-size: 13px; font-weight: 600; height: 34px; width: 100%; min-width: 0;
    padding: 0 26px 0 10px; background-color: #fff; text-overflow: ellipsis;
}
.vs-pcard-status-line >>> .custom-select:disabled { background-color: var(--vs-mist); color: var(--vs-ebony); }

.vs-meta-field { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.vs-meta-field > span,
.vs-meta-row > span { font-size: 12px; font-weight: 700; color: var(--vs-muted); display: flex; align-items: center; gap: 6px; }
.vs-meta-field > span .b-icon,
.vs-meta-row > span .b-icon { color: var(--vs-orange); }
.vs-meta-row { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; }
.vs-meta-row.is-stack { flex-direction: column; align-items: flex-start; gap: 3px; }
.vs-meta-row b { font-size: 13px; font-weight: 700; word-break: break-word; }
.vs-meta-value { font-size: 13px; font-weight: 700; color: var(--vs-ebony); word-break: break-word; }
.padding-top { padding-top: 6px; }
.vs-meta-note { font-size: 12px; color: var(--vs-muted); font-style: italic; margin-top: 2px; }
.vs-sub { display: block; font-size: 11.5px; color: var(--vs-muted); word-break: break-word; }
.vs-sub-inline { font-size: 12px; color: var(--vs-muted); font-weight: 500; }
.vs-help { cursor: help; }
.vs-prev-date { display: block; font-size: 11.5px; color: var(--vs-orange-dark); font-weight: 700; }

.vs-meta-row-2col {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 10px 14px;
    padding-bottom: 10px;
    border-bottom: 1px dashed var(--vs-line);
}
.vs-meta-row-2col:last-child {
    padding-bottom: 0;
    border-bottom: 0;
}
.vs-meta-row-2col .vs-meta-field {
    min-width: 0;
}
.vs-pcard.is-child .vs-meta-row-2col {
    gap: 8px 10px;
    padding-bottom: 8px;
}

.vs-pcard >>> .custom-select {
    border: 1.5px solid var(--vs-line); border-radius: 8px;
    font-size: 13px; font-weight: 600; height: 34px; width: 100%; min-width: 0;
    padding: 0 26px 0 10px; background-color: #fff; text-overflow: ellipsis;
}
.vs-pcard >>> .custom-select:focus { border-color: var(--vs-orange); box-shadow: 0 0 0 3px rgba(238, 122, 34, 0.18); }
.vs-pcard >>> .custom-select:disabled { background-color: var(--vs-mist); color: var(--vs-ebony); }
.vs-date { width: 100% !important; display: block; }
.vs-date >>> .mx-input {
    border: 1.5px solid var(--vs-line); border-radius: 8px;
    height: 34px; font-size: 13px; font-weight: 600; box-shadow: none; padding: 0 10px; width: 100%;
}

.vs-price-row { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; font-size: 13.5px; }
.vs-price-row span { color: var(--vs-muted); font-weight: 600; display: inline-flex; align-items: center; gap: 4px; }
.vs-price-row b { font-weight: 700; font-variant-numeric: tabular-nums; white-space: nowrap; }
.vs-price-row.is-good span, .vs-price-row.is-good b { color: var(--vs-teal); }
.vs-price-row.is-strike b { text-decoration: line-through; opacity: .65; font-weight: 600; }
.vs-price-row.is-warn b { color: #8A5E09; }
.vs-price-row.is-muted span, .vs-price-row.is-muted b { color: var(--vs-muted); font-weight: 600; }
.vs-price-row b.is-danger { color: var(--vs-danger); }
.vs-price-row.is-total { border-top: 1.5px solid #E2DCD3; padding-top: 9px; margin-top: 3px; }
.vs-price-row.is-total span { color: var(--vs-ebony); font-weight: 800; }
.vs-price-row.is-total b { font-family: var(--vs-head); font-size: 20px; font-weight: 800; color: var(--vs-orange-dark); }

.vs-pcard-actions { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; }
.vs-action-btn.btn {
    width: 34px; height: 34px; min-width: 34px;
    padding: 0 !important; border-radius: 10px !important;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 15px; position: relative;
}
.vs-action-btn.btn .vs-count { position: absolute; top: -6px; right: -6px; }
.vs-count {
    display: inline-flex; align-items: center; justify-content: center;
    min-width: 17px; height: 17px; border-radius: 999px; padding: 0 4px;
    background: var(--vs-orange); color: #fff; font-size: 10px; font-weight: 700;
}

.vs-flag { font-size: 11.5px; font-weight: 800; }
.vs-flag.is-danger { color: var(--vs-danger); }
.vs-flag-box {
    font-size: 11.5px; font-weight: 700; color: var(--vs-danger);
    background: var(--vs-danger-soft); border-left: 3px solid var(--vs-danger);
    border-radius: 6px; padding: 5px 8px;
}

.vs-pcard.has-spiff { border-color: #BFE1DD; box-shadow: inset 5px 0 0 var(--vs-teal); }
.vs-pcard.has-spiff .vs-pcard-panel { background: #EEF7F5; }
.vs-pcard.is-returned { border-color: #F3C9C3; box-shadow: inset 5px 0 0 var(--vs-danger); }
.vs-pcard.is-returned .vs-pcard-panel { background: var(--vs-danger-soft); }

.vs-pcard--parent { border-color: #F5CFAE; box-shadow: inset 0 4px 0 var(--vs-orange); padding-top: 9px; }
.vs-pcard--parent .vs-pcard-panel { background: #FFF7EF; }
.vs-pcard--parent .vs-pcard-img { min-height: 160px; }

.vs-pcard.is-child {
    margin-left: 46px;
    grid-template-columns: 230px minmax(0, 1.35fr) minmax(0, 1.5fr) minmax(0, 1fr) 46px;
}
.vs-pcard.is-child::before {
    content: ''; position: absolute; left: -28px; top: -13px; bottom: -13px;
    border-left: 2px dashed #CFC7BC;
}
.vs-pcard.is-child.is-last::before { bottom: 50%; }
.vs-pcard.is-child::after {
    content: ''; position: absolute; left: -28px; top: 50%; width: 27px;
    border-top: 2px dashed #CFC7BC;
}
.vs-pcard-dot {
    position: absolute; left: -33px; top: calc(50% - 5px);
    width: 10px; height: 10px; border-radius: 50%;
    background: var(--vs-orange); z-index: 1;
}
.vs-pcard.is-child .vs-pcard-img { min-height: 90px; }
.vs-pcard.is-child .vs-pcard-img img { max-height: 130px; }
.vs-pcard.is-child .vs-pcard-panel { padding: 8px 13px; }
.vs-pcard.is-child .vs-pcard-title { font-size: 15px; }
.vs-pcard.is-child .vs-price-row { font-size: 12.5px; }
.vs-pcard.is-child .vs-price-row.is-total b { font-size: 17px; color: var(--vs-teal); }

.image-container { position: relative; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }
.image-overlay {
    position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
    background: rgba(34, 31, 28, 0.45); color: #fff; font-size: 13px; font-weight: 700;
    opacity: 0; transition: opacity 0.2s ease; cursor: pointer;
}
.image-container:hover .image-overlay { opacity: 1; }
.no-image-placeholder {
    width: 100%; height: 100%; display: flex; flex-direction: column; gap: 4px;
    align-items: center; justify-content: center;
    color: #B9B1A6; font-size: 11px; font-weight: 600; text-align: center; padding: 4px;
}
.no-image-placeholder .b-icon { font-size: 26px; }

@media (max-width: 1199px) {
    .vs-pcard, .vs-pcard.is-child { grid-template-columns: 130px minmax(0, 1fr) minmax(0, 1fr); }
    .vs-pcard-price { grid-column: 1 / 3; }
    .vs-pcard-actions { flex-direction: row; justify-content: flex-end; }
}
@media (max-width: 767px) {
    .vs-pcard, .vs-pcard.is-child { grid-template-columns: 1fr; }
    .vs-pcard-price { grid-column: auto; }
    .vs-pcard-img, .vs-pcard.is-child .vs-pcard-img { min-height: 180px; }
    .vs-pcard.is-child { margin-left: 24px; }
    .vs-pcard.is-child::before, .vs-pcard.is-child::after { left: -15px; }
    .vs-pcard.is-child::after { width: 14px; }
    .vs-pcard-dot { left: -20px; }
}

.vs-summary-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
.vs-sum-col { background: var(--vs-canvas); border: 1px solid var(--vs-line); border-radius: 14px; padding: 18px 20px; }
.vs-sum-col h4 { font-size: 15px; font-weight: 700; margin: 0 0 12px; color: var(--vs-muted); }
.vs-sum-col.is-totals { background: var(--vs-ebony); border-color: var(--vs-ebony); color: #fff; }
.vs-sum-col.is-totals h4 { color: rgba(255, 255, 255, 0.6); }
.vs-dl { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 9px 18px; margin: 0; font-size: 15px; }
.vs-dl dt { font-weight: 500; color: var(--vs-muted); }
.vs-dl dd { margin: 0; font-weight: 700; text-align: right; font-variant-numeric: tabular-nums; }
.vs-sum-col.is-totals .vs-dl dt { color: rgba(255, 255, 255, 0.7); }
.vs-dl .vs-dl-sub { padding-top: 9px; border-top: 1px dashed var(--vs-line); }
.vs-dl .vs-dl-grand {
    font-family: var(--vs-head); font-size: 18px; font-weight: 800; color: var(--vs-ebony);
    padding-top: 10px; border-top: 1px solid var(--vs-line);
}
.vs-sum-col.is-totals .vs-dl .vs-dl-grand { color: var(--vs-orange); border-top-color: rgba(255, 255, 255, 0.18); font-size: 24px; }
.vs-sum-col.is-totals .vs-dl dt.vs-dl-grand { color: #fff; font-size: 16px; }

.vs-parent-alloc { margin-top: 14px; background: #fff; border: 1px solid var(--vs-line); border-radius: 10px; padding: 12px 14px; font-size: 13px; }
.vs-parent-alloc-head { font-weight: 700; margin-bottom: 6px; }
.vs-parent-alloc-head span { display: block; font-weight: 500; color: var(--vs-muted); font-size: 12px; }
.vs-parent-alloc-row { display: flex; justify-content: space-between; gap: 10px; padding: 4px 0; border-bottom: 1px dashed var(--vs-line); }
.vs-parent-alloc-row.is-total { color: var(--vs-teal); border-bottom: 0; border-top: 2px solid var(--vs-line); margin-top: 4px; padding-top: 6px; }
.vs-parent-alloc-row.is-warn { color: #8A5E09; border-bottom: 0; font-size: 12px; }

.vs-balance {
    margin: 18px 0 0 auto; max-width: 400px;
    border: 1px solid var(--vs-line); border-radius: 14px; overflow: hidden;
}
.vs-balance-row, .vs-balance-total { display: flex; justify-content: space-between; gap: 12px; padding: 11px 18px; font-variant-numeric: tabular-nums; }
.vs-balance-row { border-bottom: 1px solid var(--vs-line); }
.vs-balance-row span { color: var(--vs-muted); font-weight: 600; }
.vs-balance-row.is-warn span, .vs-balance-row.is-warn strong { color: var(--vs-orange-dark); }
.vs-balance-total { background: var(--vs-ebony); color: #fff; align-items: baseline; }
.vs-balance-total span { font-weight: 600; }
.vs-balance-total strong { font-family: var(--vs-head); font-size: 24px; color: var(--vs-orange); }
.vs-balance-total.is-due strong { color: #FF9D8F; }
.vs-balance-note { padding: 7px 18px; font-size: 12px; color: var(--vs-muted); background: var(--vs-mist); text-align: right; }

.vs-finance { margin-top: 20px; border-top: 1px solid var(--vs-line); padding-top: 18px; }
.vs-finance-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.vs-finance-head h4 { font-size: 16px; font-weight: 700; margin: 0; }
.vs-finance-head h4 span { color: var(--vs-muted); font-weight: 500; }
.vs-finance-row {
    display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap;
    background: var(--vs-canvas); border-radius: 10px; padding: 12px 16px; margin-bottom: 8px;
    border-left: 4px solid var(--vs-teal);
}
.vs-finance-info { display: flex; gap: 14px; align-items: center; flex-wrap: wrap; font-size: 13px; color: var(--vs-muted); }
.vs-finance-info strong { font-size: 15px; color: var(--vs-ebony); }
.vs-finance-amt { font-family: var(--vs-head); font-size: 18px; font-weight: 700; color: var(--vs-teal); }
.vs-finance-total { text-align: right; font-size: 14px; color: var(--vs-muted); padding-top: 6px; }
.vs-finance-total strong { font-family: var(--vs-head); font-size: 18px; color: var(--vs-teal); margin-left: 8px; }

.vs-layaway { margin-top: 22px; border-top: 1px solid var(--vs-line); padding-top: 18px; }
.vs-layaway h4 { font-size: 16px; font-weight: 700; margin: 0 0 12px; }
.vs-layaway-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
.vs-layaway-cell { background: var(--vs-canvas); border: 1px solid var(--vs-line); border-top: 4px solid var(--vs-teal); border-radius: 12px; padding: 14px; display: flex; flex-direction: column; gap: 2px; }
.vs-layaway-cell.is-empty { border-top-color: var(--vs-line); border-style: dashed; background: #fff; }
.vs-layaway-n { font-size: 12px; font-weight: 700; color: var(--vs-muted); }
.vs-layaway-cell strong { font-family: var(--vs-head); font-size: 20px; }
.vs-layaway-m { font-size: 13px; font-weight: 700; }
.vs-layaway-cell.is-empty .vs-layaway-m { color: var(--vs-muted); font-weight: 500; }
.vs-layaway-d { font-size: 12px; color: var(--vs-muted); }

@media (max-width: 991px) {
    .vs-summary-grid { grid-template-columns: 1fr; }
    .vs-layaway-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .vs-balance { max-width: none; }
}

.vs-table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.vs-amount { font-family: var(--vs-head); font-weight: 700; }
.vs-mini-table { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 1100px; }
.vs-mini-table th { font-size: 12px; font-weight: 700; color: var(--vs-muted); padding: 10px 8px; border-bottom: 2px solid var(--vs-line); text-align: center; white-space: nowrap; }
.vs-mini-table td { padding: 10px 8px; border-bottom: 1px solid var(--vs-line); text-align: center; vertical-align: middle; }
.vs-refund-boxes { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-top: 18px; }
.vs-refund-box { background: var(--vs-canvas); border: 1px solid var(--vs-line); border-radius: 12px; padding: 14px; display: flex; flex-direction: column; gap: 6px; }
.vs-refund-box h5 { font-size: 14px; font-weight: 700; margin: 0; }
.vs-refund-box label, .vs-refund-method label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; font-weight: 700; color: var(--vs-muted); margin: 0; }
.vs-refund-box .form-control, .vs-refund-method .form-control { height: 40px; border: 1.5px solid var(--vs-line); border-radius: 8px; text-align: center; font-weight: 700; }
.vs-refund-remain { font-size: 13px; color: var(--vs-muted); }
.vs-refund-box.is-dark { background: var(--vs-ebony); border-color: var(--vs-ebony); color: #fff; }
.vs-refund-line { display: flex; justify-content: space-between; gap: 10px; font-size: 14px; }
.vs-refund-line.is-big b { font-family: var(--vs-head); font-size: 20px; color: var(--vs-orange); }
.vs-refund-methods { margin-top: 18px; }
.vs-refund-methods-head { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
.vs-refund-method-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 12px; margin-top: 12px; }
.vs-refund-method { border: 1.5px dashed var(--vs-line); border-radius: 12px; padding: 14px; display: flex; flex-direction: column; gap: 10px; }
@media (max-width: 991px) { .vs-refund-boxes { grid-template-columns: repeat(2, minmax(0, 1fr)); } }

.vs-policy {
    background: var(--vs-canvas); border: 1px solid var(--vs-line); border-radius: 12px;
    padding: 18px 20px; max-height: 340px; overflow-y: auto; font-size: 14px; line-height: 1.6;
}
.vs-agreements { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
.vs-agreements li {
    display: flex; gap: 12px; align-items: flex-start;
    background: #fff; border: 1px solid var(--vs-line); border-radius: 12px; padding: 12px 14px;
    font-size: 14px; line-height: 1.45;
}
.vs-check {
    width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center; font-size: 15px;
}
.vs-agreements li.is-ok .vs-check { background: var(--vs-teal); color: #fff; }
.vs-agreements li.is-no { background: var(--vs-danger-soft); border-color: #F3C9C3; }
.vs-agreements li.is-no .vs-check { background: var(--vs-danger); color: #fff; }
.vs-signature {
    display: flex; justify-content: space-between; align-items: center; gap: 20px; flex-wrap: wrap;
    margin-top: 20px; padding-top: 18px; border-top: 1px dashed var(--vs-line);
}
.vs-signature p { margin: 0; color: var(--vs-danger); font-weight: 700; font-style: italic; max-width: 60ch; }
.vs-signature-box { display: flex; flex-direction: column; gap: 6px; align-items: flex-end; }
.vs-signature-box > span { font-size: 13px; font-weight: 700; color: var(--vs-muted); }
.vs-signature-box img { max-height: 70px; max-width: 260px; background: #fff; border: 1px solid var(--vs-line); border-radius: 8px; padding: 6px; }
.vs-signature-line { min-width: 240px; border-bottom: 2px solid var(--vs-ebony); padding-bottom: 4px; color: var(--vs-muted); text-align: right; font-size: 13px; }
@media (max-width: 767px) { .vs-agreements { grid-template-columns: 1fr; } }

.page-loading-overlay {
    position: fixed; inset: 0; z-index: 9999;
    background: rgba(34, 31, 28, 0.82); backdrop-filter: blur(4px);
    display: flex; justify-content: center; align-items: center;
}
.loading-spinner-container { background: #fff; padding: 32px 44px; border-radius: 20px; text-align: center; border-bottom: 5px solid var(--vs-orange); }
.loading-spinner-container .vs-logo { animation: vsPulse 1.2s ease-in-out infinite; }
.loading-text { font-family: var(--vs-head); font-size: 18px; font-weight: 700; color: var(--vs-ebony); margin-bottom: 16px; }
.loading-progress-bar { width: 260px; height: 6px; background: var(--vs-mist); border-radius: 3px; overflow: hidden; }
.loading-progress-fill { height: 100%; background: var(--vs-orange); border-radius: 3px; transition: width 0.2s ease; }
@keyframes vsPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(0.92); } }
@media (prefers-reduced-motion: reduce) { .loading-spinner-container .vs-logo { animation: none; } }

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
input[type=number] { -moz-appearance: textfield; }

.print-header-container { width: 100%; border-collapse: collapse; }
.print-header-container td { padding: 0; }

@media screen {
    .print-header-container { display: block; }
    .print-header-container > thead,
    .print-header-container > tbody,
    .print-header-container > tbody > tr,
    .print-header-container > tbody > tr > td { display: block; }
    .print-header { display: none; }
    .screen-only { display: block; }
    .print-only { display: none !important; }
    .print-only-policy { display: none; }
}

@media print {
    .vs-page { background: #fff !important; padding: 0 !important; font-size: 12px; }
    .screen-only,
    .hidden-print,
    .btn,
    button,
    .bv-example-row,
    [class*="modal"] {
        display: none !important;
    }
    .print-only { display: inline-block !important; }

    .print-header-container { display: table !important; width: 100%; }
    .print-header-container thead { display: table-header-group !important; }
    .print-header-container tbody { display: table-row-group !important; }
    .print-header { display: block !important; width: 100%; margin: 0; padding: 0; }

    body { margin: 0.25in; padding: 0 !important; }
    .print-content { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 0 !important; }
    .vs-wrap { width: 100% !important; max-width: 100% !important; }

    .print-only-policy { display: block !important; page-break-before: always !important; }
}

.pi-doc { display: none; }

@media print {
    .vs-page, .vs-page * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    .vs-page { font-family: 'Manrope', Arial, sans-serif; color: #1E1B18; }
    .pi-doc { display: block !important; }

    .pi-banner {
        position: relative;
        display: grid;
        grid-template-columns: minmax(0, 1.05fr) minmax(0, 1.35fr) minmax(0, 0.9fr);
        align-items: center;
        gap: 18px;
        padding: 14px 18px;
        border-radius: 14px;
        overflow: hidden;
        color: #1E1B18;
        background: #FFFFFF;
        border: 1.5px solid #E2DED8;
        border-bottom: 4px solid #EE7A22;
        margin-bottom: 10px;
    }
    .pi-banner::after { display: none; }

    .pi-banner-invoice {
        position: relative;
        z-index: 1;
        padding: 0 10px 0 0;
        min-width: 0;
        border-right: 1px solid #E2DED8;
    }
    .pi-hero-label {
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: #736C63;
    }
    .pi-hero-number {
        font-family: 'Barlow Condensed', 'Arial Narrow', Arial, sans-serif;
        font-size: 25px;
        font-weight: 600;
        line-height: 1.05;
        color: #D4620F;
        letter-spacing: 0.04em;
        word-break: break-all;
    }
    .pi-edit-suffix {
        color: #EE7A22;
        font-size: 20px;
        font-weight: 800;
        margin-left: 2px;
    }
    .pi-hero-edited {
        display: inline-block;
        margin-top: 3px;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: #D4620F;
    }
    .pi-hero-tags { display: flex; flex-wrap: wrap; gap: 4px; margin: 6px 0 8px; }
    .pi-tag {
        display: inline-block;
        font-size: 9px;
        font-weight: 800;
        padding: 2px 8px;
        border-radius: 999px;
        letter-spacing: 0.03em;
        text-transform: uppercase;
    }
    .pi-tag.is-danger { background: #FBE9E7; color: #C93C30; border: 1px solid #F3C9C3; }
    .pi-tag.is-teal { background: #E2F2F0; color: #0F6E6B; border: 1px solid #BFE1DD; }

    .pi-hero-meta { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 2px; margin-top: 8px; }
    .pi-hero-meta > div { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
    .pi-hero-meta span {
        font-size: 8.5px;
        font-weight: 300;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: #736C63;
    }
    .pi-hero-meta b {
        font-family: 'Barlow Condensed', 'Arial Narrow', Arial, sans-serif;
        font-size: 13px;
        font-weight: 400;
        color: #1E1B18;
        line-height: 1.15;
        word-break: break-word;
    }

    .pi-banner-brand {
        display: flex;
        gap: 0px;
        align-items: center;
        justify-content: center;
        min-width: 0;
        position: relative;
        z-index: 1;
        padding: 0 4px;
        text-align: center;
        border-right: 1px solid #E2DED8;
    }
    .pi-mark { flex-shrink: 0; }
    .pi-mark svg { width: 48px; height: 38px; display: block; }
    .pi-brand-text { min-width: 0; text-align: center; }
    .pi-brand-name {
        font-family: 'Barlow Condensed', 'Arial Narrow', Arial, sans-serif;
        font-size: 40px;
        font-weight: 800;
        line-height: 1;
        letter-spacing: 0.02em;
        color: #1E1B18;
        margin-bottom: 4px;
        white-space: nowrap;
    }
    .pi-brand-line {
        font-size: 15px;
        line-height: 1.4;
        color: #736C63;
        word-break: break-word;
    }
    .pi-brand-line--strong { color: #1E1B18; font-weight: 700; }

    .pi-banner-qr {
        position: relative;
        z-index: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
        text-align: center;
        background: #F6F4F0;
        border: 1px solid #E2DED8;
        border-radius: 12px;
        padding: 8px 10px;
    }
    .pi-qr-title {
        font-family: 'Barlow Condensed', 'Arial Narrow', Arial, sans-serif;
        font-size: 13px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #D4620F;
    }
    .pi-banner-qr img {
        background: #fff;
        border-radius: 8px;
        padding: 4px;
        width: 78px;
        height: 78px;
    }
    .pi-qr-sub {
        font-size: 8.5px;
        font-weight: 500;
        line-height: 1.2;
        color: #4A4540;
        max-width: 120px;
    }

    .pi-banner-extra {
        position: relative; z-index: 1;
        display: flex; gap: 18px; padding: 5px 22px;
        font-size: 10px;
        background: #F6F4F0;
        color: #4A4540;
        border: 1px solid #E2DED8;
        border-top: 0;
        border-radius: 0 0 14px 14px;
    }
    .pi-banner-extra b { color: #1E1B18; }

    .pi-card { background: #F4F2EF; border: 1px solid #E2DED8; border-radius: 12px; padding: 10px 14px 12px; margin-bottom: 10px; }
    .pi-title {
        font-family: 'Barlow Condensed', 'Arial Narrow', Arial, sans-serif;
        font-size: 13px; font-weight: 300; text-transform: uppercase; letter-spacing: 0.01em;
        margin: 0 0 8px; padding-left: 10px; border-left: 4px solid #EE7A22; line-height: 1;
    }

    .pi-cust {
    display: flex;
    flex-direction: column;
    gap: 6px;
    }
.pi-cust-line {
    display: grid;
    gap: 4px 4px;
}
.pi-cust-line.is-contact {
    grid-template-columns: repeat(4, minmax(0, 1fr));
}
.pi-cust-line.is-address {
    grid-template-columns: 25% 25% 14% 14% 11% 11%;
}
.pi-cust-line.is-address .pi-cust-row span {
    min-width: 0;
    font-size: 9px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
    .pi-cust-row { display: flex; gap: 14px; align-items: baseline; font-size: 13px; }
    .pi-cust-row span { color: #D4620F; font-size: 10.5px; font-weight: 700; text-transform: uppercase; min-width: 58px; }
    .pi-cust-row b { font-weight: 700; }
    .pi-comment { margin-top: 8px; padding: 6px 10px; background: #fff; border-left: 3px solid #EE7A22; border-radius: 4px; font-size: 11px; }

    .pi-plist-head,
    .pi-pcard {
        display: grid;
        grid-template-columns: 62px minmax(0, 1.45fr) minmax(0, 1fr) minmax(0, 0.95fr) minmax(0, 0.85fr);
        gap: 8px;
        align-items: start;
    }
    .pi-plist-head {
        background: #1E1B18; color: #fff; border-radius: 8px;
        padding: 6px 10px; margin-bottom: 6px;
        font-size: 9px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase;
    }
    .pi-plist { display: flex; flex-direction: column; gap: 6px; }
    .pi-pcard {
        position: relative;
        background: #fff; border: 1px solid #E2DED8; border-radius: 10px;
        padding: 6px 10px;
        page-break-inside: avoid; break-inside: avoid;
    }
    .pi-pimg {
        width: 58px; height: 50px; border-radius: 6px; background: #F1EEE9;
        display: flex; align-items: center; justify-content: center; overflow: hidden;
    }
    .pi-pimg img { max-width: 100%; max-height: 100%; object-fit: contain; }
    .pi-pdesc, .pi-pdel, .pi-pprice, .pi-ptotal { min-width: 0; }
    .pi-ptotal { border-left: 1px dashed #E2DED8; padding-left: 8px; }
    .pi-kicker { font-size: 8px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: #D4620F; }
    .pi-kicker.is-muted { color: #8C857D; }
    .pi-pname { font-size: 11px; font-weight: 800; line-height: 1.25; word-break: break-word; }
    .pi-sub { font-size: 9px; color: #736C63; font-weight: 500; margin-top: 1px; word-break: break-word; }
    .pi-flag { font-size: 8.5px; font-weight: 800; color: #C93C30; text-transform: uppercase; margin-top: 2px; }
    .pi-flag.is-orange { color: #D4620F; }
    .pi-prev { font-size: 8.5px; color: #D4620F; font-weight: 700; margin-top: 1px; }

    .pi-status-line {
        display: flex !important;
        align-items: baseline;
        gap: 6px;
        font-size: 9.5px !important;
        font-weight: 700;
        color: #1E1B18 !important;
        margin-top: 3px;
    }
    .pi-status-label {
        font-size: 8px;
        font-weight: 800;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #D4620F;
    }

    .pi-pdel-table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
        font-size: 9.5px;
        line-height: 1.35;
    }
    /* ✅ widened from 48px to 62px for longer dynamic labels */
    .pi-pdel-table th {
        width: 62px;
        padding: 1px 6px 1px 0;
        text-align: left;
        vertical-align: top;
        font-size: 8px;
        font-weight: 800;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #D4620F;
        white-space: normal;
        line-height: 1.2;
        overflow-wrap: break-word;
    }
    .pi-pdel-table td {
        padding: 1px 0;
        vertical-align: top;
        font-weight: 700;
        color: #1E1B18;
        word-break: break-word;
    }
    .pi-pdel-table tr + tr th,
    .pi-pdel-table tr + tr td {
        padding-top: 3px;
    }
    .pi-pdel-dow {
        font-weight: 500;
        color: #736C63;
        margin-left: 3px;
    }

    .pi-prow { display: flex; justify-content: space-between; gap: 8px; font-size: 9.5px; line-height: 1.45; }
    .pi-prow span { color: #736C63; font-weight: 600; }
    .pi-prow b { font-weight: 700; font-variant-numeric: tabular-nums; white-space: nowrap; }
    .pi-prow.is-strike b { text-decoration: line-through; opacity: .65; font-weight: 600; }
    .pi-prow.is-good span, .pi-prow.is-good b { color: #0F6E6B; }
    .pi-prow.is-total { border-top: 1px solid #E2DED8; margin-top: 2px; padding-top: 2px; }
    .pi-prow.is-total span { color: #1E1B18; font-weight: 800; }
    .pi-prow.is-total b { font-size: 11.5px; font-weight: 800; color: #D4620F; }

    .pi-pcard--parent { background: #FFF1E4; border-color: #F1BE8F; box-shadow: inset 0 3px 0 #EE7A22; }

    .pi-pcard.is-child { margin-left: 24px; grid-template-columns: 50px minmax(0, 1.45fr) minmax(0, 1fr) minmax(0, 0.95fr) minmax(0, 0.85fr); }
    .pi-pcard.is-child .pi-pimg { width: 46px; height: 40px; }
    .pi-pcard.is-child::before {
        content: ''; position: absolute; left: -15px; top: -7px; bottom: -7px;
        border-left: 1.5px dashed #BDB5AA;
    }
    .pi-pcard.is-child.is-last::before { bottom: 50%; }
    .pi-pcard.is-child::after {
        content: ''; position: absolute; left: -15px; top: 50%; width: 14px;
        border-top: 1.5px dashed #BDB5AA;
    }
    .pi-pcard.is-child .pi-prow.is-total b { color: #0F6E6B; }

    .pi-pcard.is-returned { background: #FBF3F2; }
    .pi-pcard.is-returned .pi-pname,
    .pi-pcard.is-returned .pi-pprice b,
    .pi-pcard.is-returned .pi-ptotal b { color: #8C857D; }
    .pi-pcard.is-returned .pi-prow.is-total b { text-decoration: line-through; }

    .pi-foot { font-size: 10px; font-style: italic; color: #4A4540; margin: 7px 2px 0; }

    .pi-totals { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); padding: 12px 0; page-break-inside: avoid; }
    .pi-col { padding: 0 14px; border-right: 1px solid #DCD7D0; }
    .pi-col:last-child { border-right: 0; }
    .pi-col h3 {
        font-family: 'Barlow Condensed', 'Arial Narrow', Arial, sans-serif;
        text-align: center; font-size: 17px; font-weight: 800; text-transform: uppercase;
        margin: 0 0 8px; padding-bottom: 4px; border-bottom: 2px solid #EE7A22;
    }
    .pi-row { display: flex; align-items: baseline; gap: 4px; font-size: 11px; padding: 2px 0; }
    .pi-row span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 70%; }
    .pi-row i { flex: 1; border-bottom: 1px dotted #A9A29A; transform: translateY(-3px); }
    .pi-row b { font-weight: 700; white-space: nowrap; font-variant-numeric: tabular-nums; }
    .pi-row--line { border-top: 1px solid #DCD7D0; margin-top: 4px; padding-top: 5px; }
    .pi-row--strong span, .pi-row--strong b { font-weight: 800; }
    .pi-row--accent { margin-top: 4px; padding-top: 5px; border-top: 1px solid #DCD7D0; }
    .pi-row--accent span, .pi-row--accent b { color: #D4620F; font-weight: 800; text-transform: uppercase; }
    .pi-grand {
        display: flex; justify-content: space-between; align-items: center;
        background: #EE7A22; color: #fff; border-radius: 999px;
        padding: 4px 12px; margin-top: 6px; font-weight: 800; font-size: 12px; text-transform: uppercase;
    }

    .pi-warranty-refused span {
        color: #C93C30;
        font-style: italic;
        font-weight: 700;
        text-transform: none;
    }
    .pi-warranty-refused i {
        border-bottom: 1px dotted #A9A29A;
    }
    .pi-warranty-refused b {
        font-weight: 700;
        color: #C93C30;
        font-style: italic;
        font-size: 9px;
        text-transform: none;
        text-align: right;
        max-width: 60%;
        line-height: 1.25;
        white-space: normal;
    }

    .pi-layaway { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; }
    .pi-layaway > div { background: #fff; border-radius: 8px; padding: 6px 8px; display: flex; flex-direction: column; font-size: 10px; }
    .pi-layaway b { font-size: 13px; }
    .pi-layaway small { color: #736C63; }

    .pi-finance { background: #FCE3CC; border: 1px solid #F1BE8F; border-radius: 12px; padding: 10px 16px; text-align: center; page-break-inside: avoid; }
    .pi-finance h3 { font-family: 'Barlow Condensed', 'Arial Narrow', Arial, sans-serif; font-size: 18px; font-weight: 800; text-transform: uppercase; margin: 0 0 4px; }
    .pi-finance-line { font-size: 13px; font-weight: 600; }
    .pi-finance-line b { font-weight: 800; }
    .pi-finance p { font-size: 9.5px; color: #4A4540; margin: 4px 0 0; }
}

.vs-history-loading,
.vs-history-error,
.vs-history-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 40px 20px;
    background: #fff;
    border: 1px solid var(--vs-line);
    border-radius: 14px;
    color: var(--vs-muted);
    font-size: 15px;
}
.vs-history-error { color: var(--vs-danger); }
.vs-history-empty { flex-direction: column; }
.vs-history-empty .b-icon { color: var(--vs-orange); font-size: 32px; }
.vs-history-empty p { margin: 0; }

.vs-history-timeline {
    list-style: none;
    margin: 0;
    padding: 6px 4px 4px;
    position: relative;
}
.vs-history-entry {
    position: relative;
    padding-left: 46px;
    padding-bottom: 18px;
}
.vs-history-entry:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 17px;
    top: 34px;
    bottom: -6px;
    width: 2px;
    background: var(--vs-line);
}
.vs-history-marker {
    position: absolute;
    left: 0;
    top: 2px;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--vs-mist);
    color: var(--vs-ebony);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    border: 2px solid #fff;
    box-shadow: 0 0 0 2px var(--vs-line);
}
.vs-history-entry.is-record_sale   .vs-history-marker { background: var(--vs-teal-soft);   color: var(--vs-teal); }
.vs-history-entry.is-refund        .vs-history-marker { background: var(--vs-danger-soft); color: var(--vs-danger); }
.vs-history-entry.is-issue_sc_out  .vs-history-marker { background: var(--vs-orange-soft); color: var(--vs-orange-dark); }
.vs-history-entry.is-update_sale   .vs-history-marker { background: #E7F0F7;              color: #234A66; }

.vs-history-card {
    background: #fff;
    border: 1px solid var(--vs-line);
    border-radius: 12px;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.vs-history-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}
.vs-history-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 15px;
}
.vs-history-num {
    font-family: var(--vs-head);
    font-weight: 800;
    color: var(--vs-orange-dark);
    background: var(--vs-orange-soft);
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 12px;
}
.vs-history-meta {
    display: flex;
    align-items: center;
    gap: 14px;
    font-size: 13px;
    color: var(--vs-muted);
    flex-wrap: wrap;
}
.vs-history-meta .b-icon { margin-right: 4px; }
.vs-history-amount {
    font-family: var(--vs-head);
    font-weight: 800;
    font-size: 14px;
}
.vs-history-amount.is-up { color: var(--vs-teal); }
.vs-history-amount.is-down { color: var(--vs-danger); }

.vs-history-reason {
    font-size: 14px;
    line-height: 1.45;
    color: var(--vs-ebony-2);
}

.vs-history-detail {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding-top: 8px;
    border-top: 1px dashed var(--vs-line);
}
.vs-history-chips,
.vs-history-list {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    font-size: 13px;
}
.vs-history-chip-label {
    font-size: 12px;
    font-weight: 700;
    color: var(--vs-muted);
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-right: 4px;
}
.vs-history-chip {
    display: inline-block;
    font-size: 12px;
    font-weight: 700;
    padding: 2px 9px;
    border-radius: 999px;
    background: var(--vs-mist);
    color: var(--vs-ebony);
}

.vs-history-foot {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    width: 100%;
    flex-wrap: wrap;
}
.vs-history-foot-note {
    font-size: 13px;
    color: var(--vs-muted);
}

.vs-finance-banner-wrap {
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 12px 0;
}

.vs-finance-banner {
    display: grid;
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 1.3fr);
    gap: 0;
    background: #fff;
    border: 1.5px solid var(--vs-line);
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(34, 31, 28, 0.06);
}

.vs-finance-banner-left {
    background: #FDF1E7;
    padding: 10px 12px 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    border-right: 1px solid var(--vs-line);
}

.vs-finance-banner-name {
    font-family: var(--vs-head);
    font-size: 15px;
    font-weight: 400;
    letter-spacing: 0.01em;
    color: #1E1B18;
    line-height: 1;
}

.vs-finance-banner-img-wrap {
    width: 100%;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    overflow: hidden;
    background: transparent;
}
.vs-finance-banner-img-el {
    max-height: 100%;
    max-width: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
}

.vs-finance-banner-img {
    width: 100%;
    height: 42px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: left center;
}

.vs-finance-banner-img.is-empty {
    display: flex;
    align-items: center;
    color: #C9B29E;
    font-size: 22px;
    background: #F8E9DA;
    border-radius: 6px;
    padding-left: 10px;
}

.vs-finance-approved {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    background: #FFEB3B;
    border-radius: 4px;
    padding: 3px 8px;
    align-self: flex-start;
    margin-top: 2px;
}

.vs-finance-approved-amt {
    font-family: var(--vs-head);
    font-size: 18px;
    font-weight: 800;
    color: #C93C30;
    line-height: 1;
}

.vs-finance-approved-label {
    font-family: var(--vs-head);
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.06em;
    color: #C93C30;
    text-transform: uppercase;
}

.vs-finance-banner-right {
    background: #E5F1DE;
    padding: 10px 14px;
    display: flex;
    flex-direction: column;
    gap: 5px;
    justify-content: center;
}

.vs-finance-info-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 10px;
    font-size: 13px;
}

.vs-finance-info-row span {
    font-family: var(--vs-head);
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    color: #4A6B3D;
    text-transform: uppercase;
}

.vs-finance-info-row b {
    font-family: var(--vs-head);
    font-size: 15px;
    font-weight: 800;
    color: #1E1B18;
    font-variant-numeric: tabular-nums;
}

.vs-finance-banner-foot {
    grid-column: 1 / -1;
    background: #D6E7F7;
    color: #234A66;
    font-family: var(--vs-head);
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    text-align: center;
    padding: 5px 8px;
    border-top: 1.5px solid var(--vs-line);
}

@media print {
    .pi-finance-banners {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin: 8px 0;
    }

    .pi-finance-banner {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
        border: 1px solid #C9C2B8;
        border-radius: 6px;
        overflow: hidden;
        page-break-inside: avoid;
        break-inside: avoid;
    }

    .pi-finance-banner-left {
        background: #FDF1E7;
        padding: 5px 7px 4px;
        display: flex;
        flex-direction: column;
        gap: 3px;
        border-right: 1px solid #C9C2B8;
    }

    .pi-finance-banner-name {
        font-family: 'Barlow Condensed', 'Arial Narrow', Arial, sans-serif;
        font-size: 14px;
        font-weight: 800;
        line-height: 1;
        color: #1E1B18;
    }

    .pi-finance-banner-img-wrap {
        width: 100%;
        height: 26px;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        overflow: hidden;
    }
    .pi-finance-banner-img-el {
        max-height: 100%;
        max-width: 100%;
        width: auto;
        height: auto;
        object-fit: contain;
        display: block;
    }

    .pi-finance-banner-img {
        width: 100%;
        height: 24px;
        background-size: contain;
        background-repeat: no-repeat;
        background-position: left center;
    }
    .pi-finance-banner-img.is-empty {
        display: flex;
        align-items: center;
        color: #C9B29E;
        font-size: 18px;
        background: #F8E9DA;
        border-radius: 4px;
        padding-left: 8px;
        height: 26px;
    }

    .pi-finance-approved {
        display: inline-flex;
        align-items: baseline;
        gap: 4px;
        background: #FFEB3B;
        border-radius: 3px;
        padding: 1px 5px;
        align-self: flex-start;
    }

    .pi-finance-approved-amt {
        font-family: 'Barlow Condensed', 'Arial Narrow', Arial, sans-serif;
        font-size: 12px;
        font-weight: 800;
        color: #C93C30;
        line-height: 1;
    }

    .pi-finance-approved-label {
        font-size: 7px;
        font-weight: 800;
        letter-spacing: 0.05em;
        color: #C93C30;
    }

    .pi-finance-banner-right {
        background: #E5F1DE;
        padding: 5px 8px;
        display: flex;
        flex-direction: column;
        gap: 2px;
        justify-content: center;
    }

    .pi-finance-info-row {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 6px;
        font-size: 9px;
    }

    .pi-finance-info-row span {
        font-size: 7.5px;
        font-weight: 800;
        letter-spacing: 0.05em;
        color: #4A6B3D;
        text-transform: uppercase;
    }

    .pi-finance-info-row b {
        font-family: 'Barlow Condensed', 'Arial Narrow', Arial, sans-serif;
        font-size: 10.5px;
        font-weight: 800;
        color: #1E1B18;
    }

    .pi-finance-banner-foot {
        grid-column: 1 / -1;
        background: #D6E7F7;
        color: #234A66;
        font-size: 7.5px;
        font-weight: 800;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        text-align: center;
        padding: 2px 4px;
        border-top: 1px solid #C9C2B8;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/Sales/ViewSale2.vue